import React, { useState, useEffect, useRef, useCallback } from "react";
import { DateRange } from "react-date-range";
import { format } from "date-fns";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import image from "../assets/2000.png";
import image2 from "../assets/2000_101748232_2.jpeg";
import AvailabilityHeader from "./Mobile/AvailabilityHeader";
import TopContactBar from "./TopContactBar";
import Footer from "./Footer";
import { useNavigate, useSearchParams } from "react-router-dom";
import { fetchRoomAvailability, transformApiRoomsToLocalFormat } from "../services/availabilityApi";
import { auditAndReportCompleteness } from "../services/dataCompleteness";
import { getAddonList } from "../services/addonApi";
import Tabs from "./Desktop/Tabs";
import SimpleImageSlider from "./Mobile/SimpleImageSlider";
import { getBreakfastStatus } from "../utils/breakfast";
import RoomCardSkeleton from "./Mobile/RoomCardSkeleton";
import BottomSheet from "./Mobile/BottomSheet";
import { useAccommodationDetail } from "../hooks/useAccommodationDetail";
import { useAccommodationGroupDetail } from "../hooks/useAccommodationGroupDetail";
import { useHotelPriceJsonLd } from "../hooks/useHotelPriceJsonLd";
import { getURLParams, getAccommodationIdFromURL } from "../utils/urlParams";
import { initializeTheme } from "../utils/themeLoader";
import { getTax, extractTaxDataFromRatePlan, isTaxIncluded, hasIncludedTax } from "../services/taxApi";
import { fetchHotelListByGroup } from "../services/accommodationApi";
import { getUserSession, getUserFullName, clearUserSession, isUserLoggedIn, getAuthToken, getLoyaltyDiscount } from "../utils/auth";
import AuthModal from "./AuthModal";
import SignInRegisterModal from "./Desktop/SignInRegisterModal";
import { requestLoyaltyPointsForAccommodation } from "../services/loyaltyApi";
import { processAutoLogin, refreshLoyaltyDiscountForHotel } from "../services/loyaltyAutoLoginApi";
import { captureTrackingParams, initializeAllTracking, isFromHotelAds, getSessionId, resetSessionId, sendBookingTracking } from "../utils/trackingParams";
import { trackPageView, trackSearch, trackRoomSelected, trackAvailabilityResults, trackMemberUnlock } from "../services/analyticsService";
import { useTranslation } from "../context/TranslationContext";
import useCustomScripts from "../hooks/useCustomScripts";


const AvailabilityComponent = () => {
  const { t, changeLanguage } = useTranslation();
  const [activeTab, setActiveTab] = useState("rooms");

  // Calendar selection color from CSS variable
  const [calendarColor, setCalendarColor] = useState('#b02e36');

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Promo code — controlled so the typed value actually reaches the API
  const [promoCode, setPromoCode] = useState(searchParams.get('promo') || '');

  // Capture tracking params (utm_campaign=HotelAds, _ga) on page load & generate new sessionId
  useEffect(() => {
    captureTrackingParams(searchParams);
    // Always generate a fresh session ID on first page
    const sessionId = resetSessionId();
    // Analytics: track page view
    // Build from the LIVE URL (window.location.search), not the searchParams hook —
    // the hook can be stale right after HomeRoute injects a domain-resolved
    // hotel_id, and rebuilding from the stale snapshot would strip it.
    const liveParams = new URLSearchParams(window.location.search);
    const hotelId = liveParams.get('hotel_id') || liveParams.get('accommodationid') || '';
    trackPageView(hotelId, { page: 'availability' });
    liveParams.set('sessionId', sessionId);
    setSearchParams(liveParams, { replace: true });
  }, []);
  
  // Read URL parameters first (before using in state initialization)
  const urlParams = getURLParams(searchParams);
  
  // Helper to get currency symbol from code
  const getCurrencySymbol = (code) => {
    const codeStr = code ? String(code) : '';
    const upperCode = codeStr.toUpperCase();
    const symbols = {
      'INR': '₹', 'USD': '$', 'EUR': '€', 'GBP': '£', 'AED': 'AED',
      'SAR': 'SAR', 'QAR': 'QAR', 'KWD': 'KWD', 'OMR': 'OMR', 'BHD': 'BHD',
      'PKR': 'Rs'
    };
    return symbols[upperCode] || upperCode;
  };

  // Helper to get display currency from ratePlan
  // Priority: convertedCurrency (if convertedRate > 0) > RatePlanCurrencyCode > CurrencyCode > currency > currentCurrency
  const getDisplayCurrency = (ratePlan) => {
    const convertedRate = ratePlan?.convertedRate ? parseFloat(ratePlan.convertedRate) : 0;
    const useConverted = convertedRate > 0 && typeof ratePlan?.convertedCurrency === 'string' && ratePlan.convertedCurrency.trim();
    const code = useConverted ? ratePlan.convertedCurrency :
                 (ratePlan?.RatePlanCurrencyCode || ratePlan?.ratePlanCurrencyCode ||
                  ratePlan?.RatePlanCurrency || ratePlan?.ratePlanCurrency ||
                  ratePlan?.CurrencyCode || ratePlan?.currency || '');
    const symbol = code ? getCurrencySymbol(code) : (currentCurrency?.code ? getCurrencySymbol(currentCurrency.code) : (currentCurrency?.symbol || ''));
    // Add space after code (e.g., "KES ") but not after symbols (e.g., "$")
    return symbol.length > 1 ? symbol + ' ' : symbol;
  };

  // Helper to get currency name from code
  const getCurrencyName = (code) => {
    const codeStr = code ? String(code) : '';
    const upperCode = codeStr.toUpperCase();
    const names = {
      'INR': 'Indian Rupee', 'USD': 'US Dollar', 'EUR': 'Euro', 'GBP': 'British Pound',
      'AED': 'UAE Dirham', 'SAR': 'Saudi Riyal', 'QAR': 'Qatari Riyal',
      'KWD': 'Kuwaiti Dinar', 'OMR': 'Omani Rial', 'BHD': 'Bahraini Dinar'
    };
    return names[upperCode] || upperCode;
  };
  
  // State management
  // Currency state - prioritize URL parameter, then localStorage, no default
  const [currentCurrency, setCurrentCurrency] = useState(() => {
    // First check URL parameter
    const urlCurrency = urlParams.currency?.toUpperCase();
    if (urlCurrency) {
      return {
        code: urlCurrency,
        symbol: getCurrencySymbol(urlCurrency),
        rate: 1,
        name: getCurrencyName(urlCurrency),
      };
    }

    // Then check localStorage
    const saved = localStorage.getItem('selectedCurrency');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        // If parse fails, return null
      }
    }

    // No default - return null
    return null;
  });

  // Load language from localStorage or use default
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    const saved = localStorage.getItem('selectedLanguage');
    if (saved) {
      try {
        const lang = JSON.parse(saved);
        // Apply RTL if needed - minimal changes
        const rtlLanguages = ['AR', 'HE', 'FA', 'UR'];
        const isRTL = rtlLanguages.includes(lang.code);
        if (isRTL) {
          document.documentElement.setAttribute('dir', 'rtl');
          document.documentElement.setAttribute('lang', 'ar');
        } else {
          document.documentElement.setAttribute('dir', 'ltr');
          document.documentElement.setAttribute('lang', 'en');
        }
        return lang;
      } catch (e) {
        // If parse fails, use default
      }
    }
    return {
    code: "EN",
      flag: "https://flagcdn.com/w80/us.png",
      flagEmoji: "🇺🇸",
    name: "English",
      nativeName: "English",
    };
  });
  const [isHotelDropdownOpen, setIsHotelDropdownOpen] = useState(false);

  // Toast notification state
  const [toasts, setToasts] = useState([]);

  const [roomQuantities, setRoomQuantities] = useState({});
  const [selectedRatePlans, setSelectedRatePlans] = useState({});
  const [showSummaryDetails, setShowSummaryDetails] = useState(false);
  const [showRoomDetails, setShowRoomDetails] = useState(false);
  const [expandedDescription, setExpandedDescription] = useState(false);
  const [selectedRoomForDetails, setSelectedRoomForDetails] = useState(null);
  const [showCancellationPolicy, setShowCancellationPolicy] = useState(false);
  const [showAmenities, setShowAmenities] = useState(false);
  const [showTaxInformation, setShowTaxInformation] = useState(false);
  const [showRatePlanPolicy, setShowRatePlanPolicy] = useState(false);
  const [showIncludedModal, setShowIncludedModal] = useState(false);
  const [selectedRatePlanForIncluded, setSelectedRatePlanForIncluded] = useState(null);
  const [selectedRatePlanForPolicy, setSelectedRatePlanForPolicy] = useState(null);
  const [expandedPolicySections, setExpandedPolicySections] = useState({});
  const [showWhatsIncluded, setShowWhatsIncluded] = useState(false);
  
  // Full-screen image lightbox: { images, roomName, index } | null
  const [imageLightbox, setImageLightbox] = useState(null);
  const lightboxTouchX = useRef(null);

  // Tax modal state
  const [showTaxModal, setShowTaxModal] = useState(false);
  const [taxData, setTaxData] = useState(null);
  const [isLoadingTax, setIsLoadingTax] = useState(false);
  const [taxError, setTaxError] = useState(null);
  const [selectedRatePlanForTax, setSelectedRatePlanForTax] = useState(null);
  const [selectedRoomForTax, setSelectedRoomForTax] = useState(null);
  const [selectedQuantityForTax, setSelectedQuantityForTax] = useState(1);
  
  // Store tax data for each room/rate plan combination for summary calculation
  const [storedTaxData, setStoredTaxData] = useState({}); // key: "roomId-ratePlanId", value: taxData
  
  // Login/Sign-in state
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [signInTab, setSignInTab] = useState('signin');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  
  // Loyalty banner state (dismissible)
  const [showLoyaltyBanner, setShowLoyaltyBanner] = useState(() => {
    // Only show if user is not logged in and banner hasn't been dismissed
    const dismissed = localStorage.getItem('loyaltyBannerDismissed') === 'true';
    return !isUserLoggedIn() && !dismissed;
  });

  // Member discount percentage from redeem_override.Value API response
  const [memberSavePercent, setMemberSavePercent] = useState(0);
  // Bumped after the per-hotel loyalty discount cache is refreshed, to force
  // rate rows to re-read the fresh (per-hotel) discount.
  const [loyaltyTick, setLoyaltyTick] = useState(0);
  const [userName, setUserName] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  // Add-ons state management
  const [addOnQuantities, setAddOnQuantities] = useState({});
  const [addOnComments, setAddOnComments] = useState({});
  
  // Add-on policy modal state
  const [showAddOnPolicyModal, setShowAddOnPolicyModal] = useState(false);
  const [selectedAddOnPolicy, setSelectedAddOnPolicy] = useState(null);
  
  // Add-on comment modal state
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [selectedAddOnForComment, setSelectedAddOnForComment] = useState(null);
  const [expandedAddOnDescs, setExpandedAddOnDescs] = useState({});

  // Set default dates to today and tomorrow
  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);

  // Helper to format date as YYYY-MM-DD (for URL - unambiguous format, no timezone issues)
  const formatDate = (date) => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper to format date as DD-MM-YYYY for API
  const formatDateForAPI = (date) => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const [selectedDates, setSelectedDates] = useState({
    checkIn: urlParams.checkin || today,
    checkOut: urlParams.checkout || tomorrow,
  });
  const [showCalendar, setShowCalendar] = useState(false);
  const [showGuestSelector, setShowGuestSelector] = useState(false);
  const [showSearchForm, setShowSearchForm] = useState(true);
  const searchFormRef = useRef(null);
  const [adults, setAdults] = useState(urlParams.adult || 1);
  const [children, setChildren] = useState(urlParams.child || 0);
  // Child ages state - array of ages for each child
  const [childAges, setChildAges] = useState(() => {
    // Initialize from URL or create empty array based on number of children
    const initialAges = urlParams.childAges || [];
    const numChildren = urlParams.child || 0;
    // If URL has ages, use them; otherwise create array with default age 1
    if (initialAges.length === numChildren) {
      return initialAges;
    }
    // Create array with default age 1 for each child
    return Array(numChildren).fill(1);
  });
  const [accommodationId, setAccommodationId] = useState(() => {
    return getAccommodationIdFromURL(searchParams, '');
  });

  // Request loyalty points for accommodation on page load
  useEffect(() => {
    const fetchLoyaltyPoints = async () => {
      try {
        // Get accommodationId and hotelId from URL
        const urlParams = getURLParams(searchParams);
        const accId = urlParams.accommodationid || accommodationId || searchParams.get('accommodationid') || searchParams.get('hotel_id');
        const hotelId = urlParams.hotel_id || urlParams.accommodationid || accommodationId || searchParams.get('hotel_id') || searchParams.get('accommodationid');

        if (accId && hotelId) {
          const response = await requestLoyaltyPointsForAccommodation({
            accommodationId: accId,
            hotelId: hotelId
          });

          // Extract redeem_override.Value for "Members Save X% more" text
          const redeemOverrideValue = parseFloat(response?.data?.data?.redeem_override?.Value || 0);
          // Always set (reset to 0 for hotels with no member discount) so a
          // previous hotel's percentage doesn't stick when switching hotels.
          setMemberSavePercent(redeemOverrideValue > 0 ? redeemOverrideValue : 0);

          // Refresh the logged-in member's redeem-override cache for THIS hotel
          // so one hotel's member discount doesn't leak onto another, then force
          // the rate rows to re-read the fresh value.
          await refreshLoyaltyDiscountForHotel(accId);
          setLoyaltyTick(t => t + 1);
        } else {
        }
      } catch (error) {
        // Don't block page load if this API call fails
      }
    };

    fetchLoyaltyPoints();
  }, [accommodationId, searchParams]);

  // Check if hotel_id is available in URL (check actual URL params, not default value)
  const hasHotelId = searchParams.get('hotel_id') || searchParams.get('accommodationid');

  // Use shared hook for accommodation detail (used in both mobile and desktop)
  const { accommodationDetail, isLoadingAccommodation } = useAccommodationDetail(accommodationId);
  
  // Use shared hook for accommodation group detail
  const { accommodationGroupDetail, isLoadingGroup } = useAccommodationGroupDetail(accommodationId);
  useCustomScripts(accommodationGroupDetail);

  // Auto-login via ltkn URL param (external loyalty token)
  useEffect(() => {
    const ltkn = searchParams.get('ltkn');
    if (!ltkn || !accommodationGroupDetail) return;

    let cancelled = false;

    (async () => {
      const result = await processAutoLogin(searchParams, accommodationGroupDetail);
      if (cancelled) return;

      if (result?.status === true && result?.profile) {
        setIsLoggedIn(true);
        setShowLoyaltyBanner(false);
      }
    })();

    return () => { cancelled = true; };
  }, [searchParams, accommodationGroupDetail]);

  // Update document title dynamically based on accommodation name
  // Only set title when accommodation detail is loaded (not during loading)
  useEffect(() => {
    if (isLoadingAccommodation || isLoadingGroup) {
      document.title = 'Loading...';
      return;
    }
    
    if (accommodationDetail && accommodationDetail.accommodationName) {
      document.title = accommodationDetail.accommodationName;
    } else if (accommodationGroupDetail) {
      // Try to get name from first accommodation in group, or use group name if available
      const firstAccommodation = accommodationGroupDetail.accommodationDetails?.[0] || accommodationGroupDetail.accommodationdetailsdb?.[0];
      if (firstAccommodation && firstAccommodation.AccommodationName) {
        document.title = firstAccommodation.AccommodationName;
      } else if (accommodationGroupDetail.groupName) {
        document.title = accommodationGroupDetail.groupName;
      } else {
        document.title = 'Loading...';
      }
    } else {
      document.title = 'Loading...';
    }
  }, [accommodationDetail, accommodationGroupDetail, isLoadingAccommodation, isLoadingGroup]);

  // Update favicon dynamically based on hotel logo
  useEffect(() => {
    let faviconLink = document.querySelector("link[rel*='icon']") || document.createElement('link');
    
    if (!document.querySelector("link[rel*='icon']")) {
      faviconLink.type = 'image/x-icon';
      faviconLink.rel = 'shortcut icon';
      document.getElementsByTagName('head')[0].appendChild(faviconLink);
    }
    
    if (accommodationDetail && accommodationDetail.logoURL) {
      faviconLink.href = accommodationDetail.logoURL;
    } else if (accommodationGroupDetail) {
      // Try to get logo from first accommodation in group
      const firstAccommodation = accommodationGroupDetail.accommodationDetails?.[0] || accommodationGroupDetail.accommodationdetailsdb?.[0];
      if (firstAccommodation && (firstAccommodation.LogoURL || firstAccommodation.LogoUrl || firstAccommodation.logoURL)) {
        faviconLink.href = firstAccommodation.LogoURL || firstAccommodation.LogoUrl || firstAccommodation.logoURL;
      }
    }
  }, [accommodationDetail, accommodationGroupDetail]);

  // Get group_id from accommodationGroupDetail or URL
  const groupId = accommodationGroupDetail?.groupId || accommodationGroupDetail?.hotelMapping?.GroupId || urlParams.group_id || null;
  const isAccommodationDropDown = accommodationGroupDetail?.hotelMapping?.IsAccommodationDropDown
    ?? accommodationGroupDetail?.accommodationdetailsdb?.[0]?.IsAccommodationDropDown
    ?? accommodationGroupDetail?.IsAccommodationDropDown
    ?? 1;

  const hasLoyalty = accommodationGroupDetail?.hotelMapping?.HasLoyalty
    ?? accommodationGroupDetail?.hotelMapping?.HasLayalty
    ?? accommodationGroupDetail?.accommodationdetailsdb?.[0]?.HasLoyalty
    ?? accommodationGroupDetail?.accommodationdetailsdb?.[0]?.HasLayalty
    ?? 0;

  // Guest selector gating (ALL hotels). Precedence: check IsAdultChild FIRST —
  // if 1, show BOTH adults & children. Otherwise fall back to the individual
  // IsAdult / IsChild flags. If none of the 3 flags exist in the data (legacy
  // hotels), show both. Never hide everything (a booking needs adults).
  const _adFlags = accommodationGroupDetail?.accommodationdetailsdb?.[0] || {};
  const _isAdultFlag = Number(_adFlags.IsAdult ?? accommodationDetail?.IsAdult ?? 0);
  const _isChildFlag = Number(_adFlags.IsChild ?? accommodationDetail?.IsChild ?? 0);
  const _isAdultChildFlag = Number(_adFlags.IsAdultChild ?? accommodationDetail?.IsAdultChild ?? 0);
  const _guestFlagsPresent = _adFlags.IsAdultChild != null || _adFlags.IsAdult != null || _adFlags.IsChild != null
    || accommodationDetail?.IsAdultChild != null || accommodationDetail?.IsAdult != null || accommodationDetail?.IsChild != null;
  const showChildrenOption = !_guestFlagsPresent ? true : (_isAdultChildFlag === 1 || _isChildFlag === 1);
  const _showAdultsRaw = !_guestFlagsPresent || _isAdultChildFlag === 1 || _isAdultFlag === 1;
  const showAdultsOption = _showAdultsRaw || !showChildrenOption; // never hide both

  // Hotel change loyalty logic:
  // - Same group → keep login (continue session across hotels in same group,
  //   even if a hotel has loyalty disabled — member logged in to the GROUP)
  // - Different group → logout and clear all loyalty data
  useEffect(() => {
    if (!accommodationGroupDetail) return;

    const hasLoyaltyEnabled = Number(hasLoyalty) === 1;
    const storedGroupId = localStorage.getItem('loggedInGroupId');
    // Treat group id 0 / "0" as NO group — these are standalone single hotels,
    // not a real chain. Switching between such hotels must log the user out, so
    // never count them as "same group".
    const currentGroupId = groupId && String(groupId) !== '0' ? String(groupId) : null;
    const sameGroup = storedGroupId && currentGroupId && storedGroupId === currentGroupId;

    // Same group → keep login regardless of this hotel's loyalty flag.
    // The member logged in to the GROUP, so a sister hotel (even one with
    // loyalty disabled) shouldn't log them out; they just see no member
    // discount there.
    if (sameGroup) return;

    // First-time stamp ONLY: record the group for a logged-in member who has no
    // stored group yet (e.g. auto-login via ltkn, which bypasses the
    // userLoggedIn handler that normally stamps it). Must NOT overwrite an
    // existing group — otherwise moving to a DIFFERENT group would silently
    // re-stamp here and skip the logout below.
    if (hasLoyaltyEnabled && isLoggedIn && currentGroupId && !storedGroupId) {
      localStorage.setItem('loggedInGroupId', currentGroupId);
      return;
    }

    // Otherwise: different group or no loyalty → clear loyalty data
    const hasLoyaltyData = localStorage.getItem('authToken') ||
                           localStorage.getItem('loyaltyDiscount') ||
                           localStorage.getItem('availableLoyaltyPoints');

    if (hasLoyaltyData) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('userSession');
      localStorage.removeItem('loyaltyDiscount');
      localStorage.removeItem('availableLoyaltyPoints');
      localStorage.removeItem('loggedInGroupId');
      setIsLoggedIn(false);
      setMemberSavePercent(0);
      setUserName('');
      window.dispatchEvent(new Event('userLoggedOut'));
    }
  }, [accommodationId, accommodationGroupDetail, hasLoyalty, groupId]);

  // Hotel list state for group dropdown
  const [accommodations, setAccommodations] = useState([]);
  const [isLoadingAccommodations, setIsLoadingAccommodations] = useState(false);
  const [selectedAccommodation, setSelectedAccommodation] = useState(null);
  const [showAccommodationDropdown, setShowAccommodationDropdown] = useState(false);
  const accommodationDropdownRef = useRef(null);

  // Fetch accommodations when groupId is available
  useEffect(() => {
    const fetchAccommodations = async () => {
      if (!groupId) {
        setAccommodations([]);
        return;
      }

      setIsLoadingAccommodations(true);
      try {
        const langId = currentLanguage?.id || currentLanguage?.lang_id || 1;
        const accList = await fetchHotelListByGroup(groupId, langId);
        setAccommodations(accList);
        
        // Set selected accommodation if accommodationId matches
        if (accommodationId && accList.length > 0) {
          const found = accList.find(acc => 
            acc.accommodationId === parseInt(accommodationId) || 
            acc.AccommodationId === parseInt(accommodationId)
          );
          if (found) {
            setSelectedAccommodation(found);
          }
        }
      } catch (error) {
        setAccommodations([]);
      } finally {
        setIsLoadingAccommodations(false);
      }
    };

    fetchAccommodations();
  }, [groupId, accommodationId, currentLanguage]);

  // Handle accommodation change
  const handleAccommodationChange = (newAccommodationId) => {
    setAccommodationId(newAccommodationId.toString());
    
    // Update URL with new accommodation ID
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('hotel_id', newAccommodationId.toString());
    setSearchParams(newSearchParams, { replace: true });
  };

  // Close accommodation dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showAccommodationDropdown &&
        accommodationDropdownRef.current &&
        !accommodationDropdownRef.current.contains(event.target)
      ) {
        setShowAccommodationDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showAccommodationDropdown]);

  // Sync currency from URL parameter - URL currency takes priority over API currency
  // If URL has currency, use it and don't let API override
  const [hasApiCurrency, setHasApiCurrency] = useState(() => {
    // If URL already has currency, mark as true to prevent API override
    return !!urlParams.currency;
  });

  useEffect(() => {
    // If URL has currency, always use it (priority over API)
    const urlCurrency = urlParams.currency?.toUpperCase();
    if (urlCurrency && (!currentCurrency || currentCurrency.code !== urlCurrency)) {
      const currencyFromUrl = {
        code: urlCurrency,
        symbol: getCurrencySymbol(urlCurrency),
        rate: 1,
        name: getCurrencyName(urlCurrency),
      };
      setCurrentCurrency(currencyFromUrl);
      setHasApiCurrency(true); // Prevent API from overriding URL currency
      localStorage.setItem('selectedCurrency', JSON.stringify(currencyFromUrl));
    }
  }, [urlParams.currency]);

  // Load theme CSS - Priority: Group CSS > Hotel CSS > Default
  // Always pass all available data to initializeTheme and let it handle priority internally
  useEffect(() => {
    // Always call with all available data - themeLoader handles priority internally:
    // 1. First checks accommodationGroupDetail for groupId → loads Group CSS
    // 2. If no groupId, falls back to hotelId → loads Hotel CSS
    // 3. If neither, loads default modern-mobile-ui.css
    const themeResult = initializeTheme(searchParams, accommodationDetail, accommodationGroupDetail);

    if (themeResult.groupId && themeResult.groupId !== '0') {
    } else if (themeResult.hotelId) {
    } else {
    }
  }, [searchParams, accommodationDetail, accommodationGroupDetail]);

  // Re-read calendar color from CSS variable after theme CSS loads
  useEffect(() => {
    const readColor = () => {
      const rootStyles = getComputedStyle(document.documentElement);
      const primaryColor = rootStyles.getPropertyValue('--primary-color').trim();
      if (primaryColor) {
        setCalendarColor(primaryColor);
      }
    };
    readColor();
    // Also read after a delay to catch async theme CSS loading
    const timer = setTimeout(readColor, 500);
    return () => clearTimeout(timer);
  }, [accommodationDetail, accommodationGroupDetail]);

  // Initialize Microsoft Clarity + GTM tracking when accommodation data is loaded
  useEffect(() => {
    if (accommodationDetail) {
      initializeAllTracking({
        accommodationName: accommodationDetail.AccommodationName || accommodationDetail.accommodationName,
        accommodationId: accommodationDetail.AccommodationId || accommodationDetail.accommodationId || urlParams.hotel_id,
        checkIn: urlParams.checkin,
        checkOut: urlParams.checkout,
        adults: parseInt(urlParams.adult) || 1,
        children: parseInt(urlParams.child) || 0,
        isAvailable: false, // Will update when apiRooms loads
        accommodationGroupDetail: accommodationGroupDetail // For GTM container ID (GtmCode)
      });
    }
  }, [accommodationDetail, accommodationGroupDetail, urlParams]);

  // API integration state
  const [apiRooms, setApiRooms] = useState([]);
  const [allApiRooms, setAllApiRooms] = useState([]); // Store all rooms from API

  // Google Hotel-Price JSON-LD (SEO only). READ-ONLY + crash-safe + purely additive:
  // it only reads hotel/rooms data and writes a <head> script — it never touches any
  // pricing calculation or the reservation flow. Every field is guarded; if a value is
  // missing/empty the builder returns null and nothing is injected (no break, no crash).
  useHotelPriceJsonLd({
    name: accommodationDetail?.accommodationName || accommodationDetail?.AccommodationName,
    identifier: accommodationId,
    address: accommodationDetail?.Address || accommodationDetail?.address,
    country: accommodationDetail?.CountryName,
    checkinDate: formatDate(selectedDates?.checkIn),
    checkoutDate: formatDate(selectedDates?.checkOut),
    rooms: apiRooms,
  });
  const [displayedRoomsCount, setDisplayedRoomsCount] = useState(5); // Initial display count
  const [isLoadingRooms, setIsLoadingRooms] = useState(true); // Start true to keep full-page loader until first search completes
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [roomsError, setRoomsError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  // Landing mode: no checkin/checkout in URL → show the search form centered and
  // DON'T auto-search (avoids the today/tomorrow "no rooms / sold out" trap).
  const [isLandingMode, setIsLandingMode] = useState(false);

  // AddOns state
  const [addOns, setAddOns] = useState([]);
  const [isLoadingAddOns, setIsLoadingAddOns] = useState(false);
  const [addOnsError, setAddOnsError] = useState(null);

  // Fetch addons from API when accommodationId or currency changes
  useEffect(() => {
    const fetchAddons = async () => {
      if (!accommodationId) {
        setAddOns([]);
        return;
      }

      setIsLoadingAddOns(true);
      setAddOnsError(null);

      // Priority: URL currency > RatePlanCurrencyCode > CurrencyCode
      const firstRatePlan = apiRooms?.[0]?.ratePlans?.[0];
      const addonCurrency = urlParams.currency ||
                            firstRatePlan?.ratePlanCurrencyCode ||
                            firstRatePlan?.RatePlanCurrencyCode ||
                            firstRatePlan?.CurrencyCode ||
                            firstRatePlan?.currency ||
                            '';

      try {
        const checkInDate = new Date(selectedDates.checkIn);
        const checkOutDate = new Date(selectedDates.checkOut);
        const addonNights = Math.max(1, Math.ceil((checkOutDate - checkInDate) / (1000 * 3600 * 24)));
        const addonsData = await getAddonList({
          hotel_id: accommodationId,
          lang_id: currentLanguage?.id || currentLanguage?.lang_id || 1,
          currency: addonCurrency,
          nights: addonNights,
        });
        setAddOns(addonsData);
      } catch (error) {
        setAddOnsError(error.message);
        setAddOns([]); // Set empty array on error
      } finally {
        setIsLoadingAddOns(false);
      }
    };

    fetchAddons();
  }, [accommodationId, currentLanguage?.id || currentLanguage?.lang_id, currentCurrency?.code, urlParams.currency, apiRooms]);

  // Data completeness audit — adhura hotel data (tax/policy/photos/size missing) detect
  // karke backend ko report bhejo (per hotel per session 1 baar). Non-blocking.
  useEffect(() => {
    if (apiRooms && apiRooms.length > 0) {
      auditAndReportCompleteness(apiRooms, {
        hotelId: accommodationId || urlParams.hotel_id || '',
        hotelName: accommodationDetail?.accommodationName || accommodationDetail?.AccommodationName || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiRooms, accommodationDetail]);


  // Sample hotels data
  const hotels = [
    {
      id: 1,
      name: "MGM Beach Resorts",
      city: "Chennai",
      location: "East Coast Road",
      rating: 4.8,
      price: "₹8,800",
      image: image2,
      type: "Resort",
    },
    {
      id: 2,
      name: "MGM Grand Hotel",
      city: "Mumbai",
      location: "Marine Drive",
      rating: 4.6,
      price: "₹12,500",
      image: image2,
      type: "Hotel",
    },
    {
      id: 3,
      name: "MGM Palace",
      city: "Delhi",
      location: "Connaught Place",
      rating: 4.7,
      price: "₹9,200",
      image: image2,
      type: "Hotel",
    },
    {
      id: 4,
      name: "MGM Beach Resort Goa",
      city: "Goa",
      location: "Calangute Beach",
      rating: 4.9,
      price: "₹15,800",
      image: image2,
      type: "Resort",
    },
    {
      id: 5,
      name: "MGM Business Hotel",
      city: "Bangalore",
      location: "MG Road",
      rating: 4.5,
      price: "₹7,500",
      image: image2,
      type: "Hotel",
    },
  ];

  const [filteredHotels, setFilteredHotels] = useState(hotels);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Refs
  const hotelDropdownRef = useRef(null);

  // Currency functions
  const handleCurrencyChange = async (currency) => {
    setCurrentCurrency(currency);
    localStorage.setItem('selectedCurrency', JSON.stringify(currency));

    // Add currency parameter to URL without page reload
    const newSearchParams = new URLSearchParams(searchParams);
    if (currency?.code) {
      newSearchParams.set('currency', currency.code);
    }
    setSearchParams(newSearchParams, { replace: true });

    // Currency change notification removed
    setIsHotelDropdownOpen(false);

    // If rooms have been searched, reload data silently (no loader)
    if (hasSearched && selectedDates.checkIn && selectedDates.checkOut) {
      try {
        const checkin = formatDateForAPI(selectedDates.checkIn);
        const checkout = formatDateForAPI(selectedDates.checkOut);
        const accId = accommodationId || urlParams.accommodationid || '';

        const params = {
          accommodationid: accId,
          checkin: checkin,
          checkout: checkout,
          adult: adults,
          child: children || 0,
          currency: currency?.code || null,
          ratecode: urlParams.ratecode || ''
        };

        const apiData = await fetchRoomAvailability(params);
        const transformedRooms = transformApiRoomsToLocalFormat(apiData);

        // Store all rooms
        setAllApiRooms(transformedRooms);

        // Display initial batch (first 5 rooms for fast initial load)
        const initialBatch = transformedRooms.slice(0, 5);
        setApiRooms(initialBatch);
        setDisplayedRoomsCount(5);

        if (transformedRooms.length === 0) {
          showToast("No rooms available at current date. Please change date.", "info");
        }
      } catch (error) {
        const errorMessage = error.message || "Failed to fetch room availability";
        setRoomsError(errorMessage);
        showToast(errorMessage, "error");
      }
    } else {
    }
  };

  // Language functions
  const handleLanguageChange = async (language) => {
    localStorage.setItem('selectedLanguage', JSON.stringify(language));
    localStorage.setItem('multilanguageid', (language.id || 1).toString());

    // Update URL without page reload
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('multilanguageid', (language.id || 1).toString());
    setSearchParams(newSearchParams, { replace: true });

    // Update translations and header language display without reload
    changeLanguage(language.code);
    setCurrentLanguage(language);

    // Re-fetch rooms silently with new language (no loader)
    if (hasSearched && selectedDates.checkIn && selectedDates.checkOut) {
      try {
        const checkin = formatDateForAPI(selectedDates.checkIn);
        const checkout = formatDateForAPI(selectedDates.checkOut);
        const accId = accommodationId || urlParams.accommodationid || '';

        const params = {
          hotel_id: accId,
          checkin: checkin,
          checkout: checkout,
          adult: adults,
          child: children || 0,
          multilanguageid: language.id || 1,
          currency: currentCurrency?.code || urlParams.currency || '',
          ratecode: urlParams.ratecode || ''
        };

        const apiData = await fetchRoomAvailability(params);
        const transformedRooms = transformApiRoomsToLocalFormat(apiData);
        setAllApiRooms(transformedRooms);
        const initialBatch = transformedRooms.slice(0, 5);
        setApiRooms(initialBatch);
        setDisplayedRoomsCount(5);
      } catch (error) {
        // Silent fail - keep existing data
      }
    }
  };

  // Hotel functions
  const showHotelDropdown = () => {
    setIsHotelDropdownOpen(true);
    // Show all hotels when input is focused
    if (!searchQuery.trim()) {
      setFilteredHotels(hotels);
    }
  };

  const toggleHotelDropdown = () => {
    setIsHotelDropdownOpen(!isHotelDropdownOpen);
  };

  const searchCity = (city) => {
    // Filter hotels based on city
    const cityHotels = hotels.filter(
      (hotel) =>
        (hotel.city || '').toLowerCase().includes(city.toLowerCase()) ||
        (hotel.name || '').toLowerCase().includes(city.toLowerCase()),
    );
    setFilteredHotels(cityHotels);
    setSearchQuery(city);
    setIsHotelDropdownOpen(true);

    showToast(`Searching for hotels in ${city}...`, "info");
  };

  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (query.trim() === "") {
      setFilteredHotels(hotels);
    } else {
      const filtered = hotels.filter(
        (hotel) =>
          (hotel.name || '').toLowerCase().includes(query.toLowerCase()) ||
          (hotel.city || '').toLowerCase().includes(query.toLowerCase()) ||
          (hotel.location || '').toLowerCase().includes(query.toLowerCase()),
      );
      setFilteredHotels(filtered);
    }

    setIsHotelDropdownOpen(true);
  };

  const selectHotel = (hotel) => {
    setSelectedHotel(hotel);
    setSearchQuery(hotel.name);
    setIsHotelDropdownOpen(false);
    showToast(`Selected: ${hotel.name}`, "success");
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSelectedHotel(null);
    setFilteredHotels(hotels);
    setIsHotelDropdownOpen(false);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Escape") {
      setIsHotelDropdownOpen(false);
    } else if (e.key === "Enter" && filteredHotels.length > 0) {
      selectHotel(filteredHotels[0]);
    }
  };

  // Calendar functions
  const calendarRef = useRef(null);
  const guestSelectorRef = useRef(null);

  // Close calendar on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showCalendar &&
        calendarRef.current &&
        !calendarRef.current.contains(event.target)
      ) {
        setShowCalendar(false);
      }
      if (
        showGuestSelector &&
        guestSelectorRef.current &&
        !guestSelectorRef.current.contains(event.target)
      ) {
        setShowGuestSelector(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showCalendar, showGuestSelector]);

  // Function to update pricing when room quantity changes
  const updatePricingForRoomQuantity = (roomNumber, newQuantity) => {
    // This function is used by QuantityDropdown component
    // No additional logic needed for now
  };

  // Rate plan selection function
  const selectRatePlan = (roomId, ratePlanId) => {
    const selectionKey = `${roomId}-${ratePlanId}`;

    setSelectedRatePlans((prev) => {
      const newState = { ...prev };
      // Always select the rate plan (this is called when quantity > 0)
      newState[selectionKey] = { roomId, ratePlanId };
      return newState;
    });
  };

  // Rate plan deselection function
  const deselectRatePlan = (roomId, ratePlanId) => {
    const selectionKey = `${roomId}-${ratePlanId}`;

    setSelectedRatePlans((prev) => {
      const newState = { ...prev };
      delete newState[selectionKey];
      return newState;
    });
  };

  // Check if a rate plan is selected
  const isRatePlanSelected = (roomId, ratePlanId) => {
    const selectionKey = `${roomId}-${ratePlanId}`;
    return !!selectedRatePlans[selectionKey];
  };

  // Room Details, Cancellation Policy, and Amenities functions
  const showRoomDetailsModal = (room) => {
    setSelectedRoomForDetails(room);
    setShowRoomDetails(true);
    setExpandedDescription(false); // Reset expanded state when modal opens
  };

  const showCancellationPolicyModal = (room) => {
    setSelectedRoomForDetails(room);
    setShowCancellationPolicy(true);
  };

  const showAmenitiesModal = (room) => {
    setSelectedRoomForDetails(room);
    setShowAmenities(true);
  };

  const showTaxInformationModal = () => {
    setShowTaxInformation(true);
  };

  const hasAnyPolicyData = (rp) => {
    if (!rp) return false;
    const isValid = (val) => val && !Array.isArray(val) && String(val).trim() && String(val).trim().toLowerCase() !== 'free cancellation';
    const hasBooking = isValid(rp.bookingPolicy);
    const hasCancellation = isValid(rp.cancellationPolicy);
    const hasNoShow = isValid(rp.noShowPolicy) || (isValid(rp.noShowPolicyType) && String(rp.noShowPolicyType).trim().toLowerCase() !== 'free');
    return !!(hasBooking || hasCancellation || hasNoShow);
  };

  const showRatePlanPolicyModal = (ratePlan) => {
    if (!hasAnyPolicyData(ratePlan)) return;
    setSelectedRatePlanForPolicy(ratePlan);
    setExpandedPolicySections({});
    setShowRatePlanPolicy(true);
  };

  const showWhatsIncludedModal = (ratePlan) => {
    setSelectedRatePlanForIncluded(ratePlan);
    setShowWhatsIncluded(true);
  };

  // Tax calculation function
  const calculateTax = async (room, ratePlan, quantity = 1) => {
    setIsLoadingTax(true);
    setTaxError(null);
    
    try {
      const nights = Math.ceil(
        (selectedDates.checkOut - selectedDates.checkIn) / (1000 * 60 * 60 * 24)
      ) || 1;
      
      // Extract tax data from rate plan
      const taxArray = extractTaxDataFromRatePlan(ratePlan);
      
      if (!taxArray || taxArray.length === 0) {
        setTaxError('No tax information available for this rate plan');
        setIsLoadingTax(false);
        return;
      }

      // Check ConvertedRate first - if it's 0 or null, use original rate price
      const convertedRate = ratePlan.convertedRate != null ? parseFloat(ratePlan.convertedRate) : null;
      const useConvertedRate = convertedRate != null && !isNaN(convertedRate) && convertedRate > 0;
      
      // Get original rate price
      const ratePlanPrice = ratePlan.price != null ? parseFloat(ratePlan.price) : null;
      const roomBasePrice = room.basePrice != null ? parseFloat(room.basePrice) : null;
      
      // Use convertedRate if available and > 0, otherwise use original rate price
      let roomPrice = 0;
      if (useConvertedRate) {
        roomPrice = convertedRate;
      } else if (ratePlanPrice != null && !isNaN(ratePlanPrice) && ratePlanPrice >= 0) {
        roomPrice = ratePlanPrice;
      } else if (roomBasePrice != null && !isNaN(roomBasePrice) && roomBasePrice >= 0) {
        roomPrice = roomBasePrice;
      }
      
      if (ratePlan.promotionDiscount?.discount > 0 && ratePlan.promotionDiscount?.afterDiscountAmount) {
        const discountPrice = parseFloat(ratePlan.promotionDiscount.afterDiscountAmount);
        if (!isNaN(discountPrice) && discountPrice > 0) {
          // INC-048b: afterDiscountAmount is in the BASE currency. When converting, scale the
          // same discount ratio onto the converted rate so the tax base matches the shown price.
          if (useConvertedRate && convertedRate > 0 && ratePlanPrice != null && ratePlanPrice > 0) {
            roomPrice = convertedRate * (discountPrice / ratePlanPrice);
          } else {
            roomPrice = discountPrice;
          }
        }
      }
      
      const totalPrice = roomPrice * quantity ;
      const noofroomArray = { "0": quantity };
      const totalBedrooms = (ratePlan.noOfBedRooms || 1);
      
      // Call tax API
      const result = await getTax({
        nights,
        conversionrate: ratePlan.conversionRate || 0,
        noofroomArray,
        totalnumberofbedrooms: totalBedrooms,
        price: totalPrice,
        data: taxArray
      });
      
      setTaxData(result);
      
      // Store tax data for summary calculation
      const taxKey = `${room.id}-${ratePlan.id}`;
      setStoredTaxData(prev => ({
        ...prev,
        [taxKey]: result
      }));
    } catch (error) {
      setTaxError(error.message || 'Failed to calculate tax');
    } finally {
      setIsLoadingTax(false);
    }
  };

  // Show tax modal for a specific rate plan
  const showTaxModalForRatePlan = (room, ratePlan, quantity = 1) => {
    setSelectedRoomForTax(room);
    setSelectedRatePlanForTax(ratePlan);
    setSelectedQuantityForTax(quantity);
    setShowTaxModal(true);
    calculateTax(room, ratePlan, quantity);
  };

  // Calculate and store tax when room quantity changes (for summary)
  const calculateAndStoreTax = async (room, ratePlan, quantity) => {
    if (!room || !ratePlan || quantity <= 0) return;
    
    try {
      const nights = Math.ceil(
        (selectedDates.checkOut - selectedDates.checkIn) / (1000 * 60 * 60 * 24)
      ) || 1;
      
      const taxArray = extractTaxDataFromRatePlan(ratePlan);
      
      if (!taxArray || taxArray.length === 0) return;

      // Check ConvertedRate first - if it's 0 or null, use original rate price
      const convertedRate = ratePlan.convertedRate != null ? parseFloat(ratePlan.convertedRate) : null;
      const useConvertedRate = convertedRate != null && !isNaN(convertedRate) && convertedRate > 0;
      
      // Get original rate price
      const ratePlanPrice = ratePlan.price != null ? parseFloat(ratePlan.price) : null;
      const roomBasePrice = room.basePrice != null ? parseFloat(room.basePrice) : null;
      
      // Use convertedRate if available and > 0, otherwise use original rate price
      let roomPrice = 0;
      if (useConvertedRate) {
        roomPrice = convertedRate;
      } else if (ratePlanPrice != null && !isNaN(ratePlanPrice) && ratePlanPrice >= 0) {
        roomPrice = ratePlanPrice;
      } else if (roomBasePrice != null && !isNaN(roomBasePrice) && roomBasePrice >= 0) {
        roomPrice = roomBasePrice;
      }
      
      if (ratePlan.promotionDiscount?.discount > 0 && ratePlan.promotionDiscount?.afterDiscountAmount) {
        const discountPrice = parseFloat(ratePlan.promotionDiscount.afterDiscountAmount);
        if (!isNaN(discountPrice) && discountPrice > 0) {
          // INC-048b: afterDiscountAmount is in the BASE currency. When converting, scale the
          // same discount ratio onto the converted rate so the tax base matches the shown price.
          if (useConvertedRate && convertedRate > 0 && ratePlanPrice != null && ratePlanPrice > 0) {
            roomPrice = convertedRate * (discountPrice / ratePlanPrice);
          } else {
            roomPrice = discountPrice;
          }
        }
      }
      
      const totalPrice = roomPrice * quantity * nights;
      const noofroomArray = { "0": quantity };
      const totalBedrooms = (ratePlan.noOfBedRooms || 1);
      
      const result = await getTax({
        nights,
        conversionrate: ratePlan.conversionRate || 0,
        noofroomArray,
        totalnumberofbedrooms: totalBedrooms,
        price: totalPrice,
        data: taxArray
      });
      
      // Store tax data for summary calculation
      const taxKey = `${room.id}-${ratePlan.id}`;
      setStoredTaxData(prev => ({
        ...prev,
        [taxKey]: result
      }));
    } catch (error) {
    }
  };

  const closeModal = () => {
    setShowRoomDetails(false);
    setShowRatePlanPolicy(false);
    setShowWhatsIncluded(false);
    setSelectedRatePlanForPolicy(null);
    setSelectedRatePlanForIncluded(null);
    setSelectedRoomForDetails(null);
    setShowCancellationPolicy(false);
    setShowAmenities(false);
    setShowTaxInformation(false);
    setShowTaxModal(false);
    setSelectedRatePlanForTax(null);
    setSelectedRoomForTax(null);
    setTaxData(null);
  };

  // Utility functions
  const showToast = (message, type = "info") => {
    const id = Date.now() + Math.random();
    const newToast = {
      id,
      message,
      type,
      timestamp: Date.now(),
    };

    setToasts((prev) => [...prev, newToast]);

    // Auto remove toast after 4 seconds
    setTimeout(() => {
      removeToast(id);
    }, 4000);
  };

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  const convertPrice = (priceInINR) => {
    // Handle null, undefined, or empty string
    if (priceInINR === null || priceInINR === undefined || priceInINR === '') {
      return 0;
    }
    
    // Convert to number if it's a string
    const numPrice = typeof priceInINR === 'string' ? parseFloat(priceInINR) : Number(priceInINR);
    
    // Check if it's a valid number
    if (isNaN(numPrice)) {
      return 0;
    }
    
    // Prices from API are already in the converted currency, so just return as-is
    // No conversion needed - return the price directly
    return numPrice;
  };

  const formatPrice = (price, ratePlan = null) => {
    // Convert to number if string
    let numPrice = typeof price === 'string' ? parseFloat(price) : Number(price);

    // Handle null, undefined, or NaN
    if (numPrice === null || numPrice === undefined || isNaN(numPrice)) {
      numPrice = 0;
    }

    // Match desktop behavior: show decimals only if price has decimals, otherwise round
    const hasDecimals = numPrice % 1 !== 0;
    const fractionDigits = hasDecimals ? 2 : 0;

    // Use getDisplayCurrency for ratePlan-based currency (RatePlanCurrencyCode > CurrencyCode)
    const symbol = getDisplayCurrency(ratePlan || {});
    return `${symbol}${numPrice.toLocaleString(undefined, { minimumFractionDigits: fractionDigits, maximumFractionDigits: 2 })}`;
  };

  // Fetch room availability from API
  const handleSearchAvailability = async () => {
    if (!selectedDates.checkIn || !selectedDates.checkOut) {
      showToast("Please select check-in and check-out dates", "warning");
      return;
    }

    if (adults < 1) {
      showToast("Please select at least 1 adult", "warning");
      return;
    }

    // User is searching → leave landing mode
    setIsLandingMode(false);

    setIsLoadingRooms(true);
    setRoomsError(null);
    setHasSearched(true);
    setShowSearchForm(false);
    setDisplayedRoomsCount(5); // Reset to initial count for new search

    // Analytics: track search
    const accId = accommodationId || searchParams.get('hotel_id') || '';
    trackSearch(accId, {
      checkin: formatDateForAPI(selectedDates.checkIn),
      checkout: formatDateForAPI(selectedDates.checkOut),
      adults,
      children,
      promoCode: (promoCode || '').trim() || null
    });

    try {
      const checkinForAPI = formatDateForAPI(selectedDates.checkIn);  // DD-MM-YYYY for API
      const checkoutForAPI = formatDateForAPI(selectedDates.checkOut);
      const checkinForURL = formatDate(selectedDates.checkIn);  // YYYY-MM-DD for URL (unambiguous)
      const checkoutForURL = formatDate(selectedDates.checkOut);
      const accId = accommodationId || urlParams.hotel_id || urlParams.accommodationid || '';

      // Update URL parameters when search is performed
      // Start with existing search params to preserve all parameters
      const newSearchParams = new URLSearchParams(searchParams);

      // Update required parameters - use YYYY-MM-DD in URL (unambiguous, works with Google & all sources)
      newSearchParams.set('hotel_id', accId);
      newSearchParams.set('checkin', checkinForURL);
      newSearchParams.set('checkout', checkoutForURL);
      newSearchParams.set('adult', adults.toString());
      newSearchParams.set('child', children.toString());

      // Add child ages as comma-separated string to URL
      if (childAges.length > 0) {
        newSearchParams.set('childAges', childAges.join(','));
      } else {
        // Remove childAges if empty
        newSearchParams.delete('childAges');
      }

      // Promo code — use the typed value (falls back to URL param)
      const promoForSearch = (promoCode || '').trim();
      if (promoForSearch) {
        newSearchParams.set('promo', promoForSearch);
      } else {
        newSearchParams.delete('promo');
      }
      if (urlParams.profileid) {
        newSearchParams.set('profileid', urlParams.profileid);
      }
      if (urlParams.multilanguageid) {
        newSearchParams.set('multilanguageid', urlParams.multilanguageid.toString());
      }

      // Preserve currency parameter ONLY if it exists in URL (user has changed currency)
      // Don't add currency parameter if it's not in URL
      if (urlParams.currency) {
        newSearchParams.set('currency', urlParams.currency);
      }

      // Update URL without page reload
      setSearchParams(newSearchParams, { replace: true });

      const params = {
        hotel_id: accId,
        checkin: checkinForAPI,
        checkout: checkoutForAPI,
        adult: adults,
        child: children,
        childAges: childAges.length > 0 ? childAges.join(',') : '', // Comma-separated child ages
        promo: promoForSearch,
        profileid: urlParams.profileid || '',
        multilanguageid: searchParams.get('multilanguageid') || localStorage.getItem('multilanguageid') || urlParams.multilanguageid || 1,
        currency: urlParams.currency || '',
        device: '',
        ratecode: urlParams.ratecode || ''
      };

      const apiData = await fetchRoomAvailability(params);
      
      const transformedRooms = transformApiRoomsToLocalFormat(apiData);
      
      // Extract currency from API response (priority: Availability API > Accommodation API)
      let currencyExtracted = false;
      
      if (transformedRooms && transformedRooms.length > 0) {
        const firstRoom = transformedRooms[0];
        if (firstRoom.ratePlans && firstRoom.ratePlans.length > 0) {
          const firstRatePlan = firstRoom.ratePlans[0];
          
          // Priority: CurrencyCode (from availabilityApi.js) > currency > convertedCurrency > ratePlanCurrencyCode
          // Check each field and use the first non-empty value
          let apiCurrency = null;
          // First priority: CurrencyCode directly from ratePlan (as set in availabilityApi.js)
          if (firstRatePlan.CurrencyCode && String(firstRatePlan.CurrencyCode).trim()) {
            apiCurrency = String(firstRatePlan.CurrencyCode).trim();
          } else if (firstRatePlan.currency && String(firstRatePlan.currency).trim()) {
            // Second priority: currency field directly in ratePlan object (which comes from CurrencyCode in availabilityApi.js)
            apiCurrency = String(firstRatePlan.currency).trim();
          } else if (firstRatePlan.convertedCurrency && String(firstRatePlan.convertedCurrency).trim()) {
            apiCurrency = String(firstRatePlan.convertedCurrency).trim();
          } else if (firstRatePlan.ratePlanCurrencyCode && String(firstRatePlan.ratePlanCurrencyCode).trim()) {
            apiCurrency = String(firstRatePlan.ratePlanCurrencyCode).trim();
          }
          
          if (apiCurrency) {
            // Always update currency from API response (API has highest priority)
            const currencySymbols = {
              'INR': '₹', 'USD': '$', 'EUR': '€', 'GBP': '£', 'AED': 'AED',
              'SAR': 'SAR', 'QAR': 'QAR', 'KWD': 'KWD', 'OMR': 'OMR', 'BHD': 'BHD',
              'PHP': '₱', 'THB': '฿', 'MYR': 'RM', 'SGD': 'S$', 'IDR': 'Rp',
              'PKR': 'Rs'
            };
            const currencyNames = {
              'INR': 'Indian Rupee', 'USD': 'US Dollar', 'EUR': 'Euro', 'GBP': 'British Pound',
              'AED': 'UAE Dirham', 'SAR': 'Saudi Riyal', 'QAR': 'Qatari Riyal',
              'KWD': 'Kuwaiti Dinar', 'OMR': 'Omani Rial', 'BHD': 'Bahraini Dinar',
              'PHP': 'Philippine Peso', 'THB': 'Thai Baht', 'MYR': 'Malaysian Ringgit',
              'SGD': 'Singapore Dollar', 'IDR': 'Indonesian Rupiah', 'PKR': 'Pakistani Rupee'
            };

            const currencyToSet = {
              code: apiCurrency.toUpperCase(),
              symbol: currencySymbols[apiCurrency.toUpperCase()] || apiCurrency,
              rate: firstRatePlan.conversionRate || 1,
              name: currencyNames[apiCurrency.toUpperCase()] || apiCurrency
            };

            setCurrentCurrency(currencyToSet);
            setHasApiCurrency(true); // Mark that we have API currency
            localStorage.setItem('selectedCurrency', JSON.stringify(currencyToSet));
            currencyExtracted = true;

            // Don't add currency to URL automatically from API
            // Currency parameter will only be added when user manually changes currency
          }
        }
      }

      // Fallback: If currency not extracted from availability API, use accommodationDetail API
      if (!currencyExtracted && accommodationDetail && accommodationDetail.currencyCode) {
        const accommodationCurrency = accommodationDetail.currencyCode.toUpperCase();

        const currencySymbols = {
          'INR': '₹', 'USD': '$', 'EUR': '€', 'GBP': '£', 'AED': 'AED',
          'SAR': 'SAR', 'QAR': 'QAR', 'KWD': 'KWD', 'OMR': 'OMR', 'BHD': 'BHD',
          'PHP': '₱', 'THB': '฿', 'MYR': 'RM', 'SGD': 'S$', 'IDR': 'Rp',
          'PKR': 'Rs'
        };
        const currencyNames = {
          'INR': 'Indian Rupee', 'USD': 'US Dollar', 'EUR': 'Euro', 'GBP': 'British Pound',
          'AED': 'UAE Dirham', 'SAR': 'Saudi Riyal', 'QAR': 'Qatari Riyal',
          'KWD': 'Kuwaiti Dinar', 'OMR': 'Omani Rial', 'BHD': 'Bahraini Dinar',
          'PHP': 'Philippine Peso', 'THB': 'Thai Baht', 'MYR': 'Malaysian Ringgit',
          'SGD': 'Singapore Dollar', 'IDR': 'Indonesian Rupiah', 'PKR': 'Pakistani Rupee'
        };
        
        const currencyToSet = {
          code: accommodationCurrency,
          symbol: currencySymbols[accommodationCurrency] || accommodationCurrency,
          rate: 1,
          name: currencyNames[accommodationCurrency] || accommodationCurrency
        };
        
        setCurrentCurrency(currencyToSet);
        localStorage.setItem('selectedCurrency', JSON.stringify(currencyToSet));
      }
      
      // Store all rooms
      setAllApiRooms(transformedRooms);
      
      // Display initial batch (first 5 rooms for fast initial load)
      const initialBatch = transformedRooms.slice(0, 5);
      setApiRooms(initialBatch);
      setDisplayedRoomsCount(5);
      
      // Analytics: track availability results
      const availableRooms = transformedRooms.filter(r => r.totalroom > 0 || r.ratePlans?.length > 0).length;
      trackAvailabilityResults(accId, {
        checkin: formatDateForAPI(selectedDates.checkIn),
        checkout: formatDateForAPI(selectedDates.checkOut),
        adults,
        children,
        totalRooms: transformedRooms.length,
        availableRooms,
        soldOut: transformedRooms.length === 0
      });

      if (transformedRooms.length === 0) {
        showToast("No rooms available at current date. Please change date.", "info");
      }
    } catch (error) {
      const errorMessage = error.message || "Failed to fetch room availability";
      setRoomsError(errorMessage);
      showToast(errorMessage, "error");
    } finally {
      setIsLoadingRooms(false);
    }
  };

  // Load more rooms function - using useCallback to avoid dependency issues
  const loadMoreRooms = useCallback(() => {
    if (isLoadingMore || displayedRoomsCount >= allApiRooms.length) return;
    
    setIsLoadingMore(true);
    
    // Load next batch (5 more rooms)
    setTimeout(() => {
      const nextCount = Math.min(displayedRoomsCount + 5, allApiRooms.length);
      const nextBatch = allApiRooms.slice(0, nextCount);
      setApiRooms(nextBatch);
      setDisplayedRoomsCount(nextCount);
      setIsLoadingMore(false);
    }, 200);
  }, [isLoadingMore, displayedRoomsCount, allApiRooms]);

  // Scroll detection for infinite scroll
  useEffect(() => {
    if (!hasSearched || allApiRooms.length <= displayedRoomsCount || isLoadingMore) return;

    const handleScroll = () => {
      const scrollPosition = window.innerHeight + window.scrollY;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Load more when user is 300px from bottom
      if (scrollPosition >= documentHeight - 300) {
        loadMoreRooms();
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasSearched, allApiRooms.length, displayedRoomsCount, isLoadingMore, loadMoreRooms]);

  // Use API rooms if available, otherwise fall back to static data
  const displayRooms = hasSearched && apiRooms.length > 0 ? apiRooms : apiRooms;

  // Auto-select first room when tracking params (utm_campaign=HotelAds) are present
  useEffect(() => {
    // Only auto-select if:
    // 1. User came from HotelAds (utm_campaign=HotelAds)
    // 2. Rooms are loaded
    // 3. No room is already selected
    const hasSelectedRooms = Object.values(roomQuantities).some(qty => qty > 0);

    if (isFromHotelAds() && apiRooms.length > 0 && !hasSelectedRooms) {
      const firstRoom = apiRooms[0];
      const firstRatePlan = firstRoom?.ratePlans?.[0];

      if (firstRoom && firstRatePlan) {
        const selectionKey = `${firstRoom.id}-${firstRatePlan.id}`;
        // Auto-select first room with quantity 1 — same flow as manual + button click
        setRoomQuantities(prev => ({
          ...prev,
          [selectionKey]: 1
        }));
        // Update pricing (matches manual increment flow)
        updatePricingForRoomQuantity(firstRoom.id, 1);
        // Mark rate plan as selected
        selectRatePlan(firstRoom.id, firstRatePlan.id);
        // Calculate and store tax for summary (applies promotion discount internally)
        calculateAndStoreTax(firstRoom, firstRatePlan, 1);
      }
    }
  }, [apiRooms]); // Run when rooms are loaded

  // Update child ages from URL parameters when they change
  useEffect(() => {
    const urlChildAges = searchParams.get('childAges');
    const urlChildren = searchParams.get('child');
    
    if (urlChildAges) {
      // Parse child ages from comma-separated string
      const parsedAges = urlChildAges.split(',').map(age => parseInt(age.trim())).filter(age => !isNaN(age));
      const numChildren = urlChildren ? parseInt(urlChildren) : 0;
      
      // Update child ages if URL has valid ages
      if (parsedAges.length === numChildren && numChildren > 0) {
        setChildAges(parsedAges);
      } else if (numChildren > 0 && parsedAges.length !== numChildren) {
        // If children count changed but ages don't match, adjust ages array
        if (parsedAges.length < numChildren) {
          // Add default age 1 for missing children
          setChildAges([...parsedAges, ...Array(numChildren - parsedAges.length).fill(1)]);
        } else {
          // Remove extra ages
          setChildAges(parsedAges.slice(0, numChildren));
        }
      }
    } else if (urlChildren) {
      // If URL has children but no ages, initialize with default ages
      const numChildren = parseInt(urlChildren) || 0;
      if (numChildren > 0 && childAges.length !== numChildren) {
        setChildAges(Array(numChildren).fill(1));
      }
    }
  }, [searchParams]); // Run when URL params change

  // Auto-search on mount with default dates (today and tomorrow) if hotel_id is available
  useEffect(() => {
    // Check if hotel_id is available
    if (!hasHotelId) {
      return; // Don't search if no hotel_id
    }

    const checkin = searchParams.get('checkin');
    const checkout = searchParams.get('checkout');
    const adult = searchParams.get('adult');
    
    // If checkin & checkout are in the URL → auto-search with them.
    // Otherwise → LANDING MODE: show the search form centered, do NOT auto-search
    // (auto-searching today/tomorrow often returns "no rooms" since hotels rarely
    // have same-day inventory — bad first impression). Let the user pick dates.
    if (checkin && checkout) {
      handleSearchAvailability();
    } else {
      setIsLandingMode(true);
      setIsLoadingRooms(false);
      setShowSearchForm(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasHotelId]); // Run when hotel_id becomes available

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        hotelDropdownRef.current &&
        !hotelDropdownRef.current.contains(event.target)
      ) {
        setIsHotelDropdownOpen(false);
      }
      if (showUserDropdown && !event.target.closest('.user-dropdown-container')) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [showUserDropdown]);

  // Check login status on mount and when user logs in
  useEffect(() => {
    const checkLoginStatus = () => {
      const loggedIn = isUserLoggedIn();
      setIsLoggedIn(loggedIn);
      if (loggedIn) {
        const name = getUserFullName();
        setUserName(name);

        // Log full user details
        const session = getUserSession();
        console.log('====== 👤 LOGGED-IN USER DETAILS (Mobile) ======');
        console.log('Full Name:', name);
        console.log('Auth Token:', getAuthToken());
        console.log('Session:', session);
        console.log('Profile:', session?.user?.profile);
        console.log('Profile Data:', session?.user?.data);
        console.log('Login Time:', session?.loginTime);
        console.log('Loyalty Discount:', getLoyaltyDiscount());
        console.log('================================================');
      } else {
        setUserName('');
      }
    };

    // Check on mount
    checkLoginStatus();

    // Check login status when modal closes
    if (!showSignInModal) {
      checkLoginStatus();
    }
  }, [showSignInModal]);

  // Listen for login/logout events separately to ensure it always works
  useEffect(() => {
    const handleLoginStatusChange = () => {
      const loggedIn = isUserLoggedIn();
      setIsLoggedIn(loggedIn);
      if (loggedIn) {
        const name = getUserFullName();
        setUserName(name);
        // Hide banner when user logs in
        setShowLoyaltyBanner(false);
        // Store current group_id so we can keep login across hotels in same group
        const currentGroupId = accommodationGroupDetail?.groupId || accommodationGroupDetail?.hotelMapping?.GroupId || urlParams.group_id;
        if (currentGroupId) {
          localStorage.setItem('loggedInGroupId', String(currentGroupId));
        }
      } else {
        setUserName('');
        // Show banner if not dismissed
        const dismissed = localStorage.getItem('loyaltyBannerDismissed') === 'true';
        setShowLoyaltyBanner(!dismissed);
      }
    };

    // Listen for both login and logout events
    window.addEventListener('userLoggedIn', handleLoginStatusChange);
    window.addEventListener('userLoggedOut', handleLoginStatusChange);

    return () => {
      window.removeEventListener('userLoggedIn', handleLoginStatusChange);
      window.removeEventListener('userLoggedOut', handleLoginStatusChange);
    };
  }, []);
  
  // Update banner visibility when login status changes
  useEffect(() => {
    if (isLoggedIn) {
      setShowLoyaltyBanner(false);
    } else {
      // Only show if not dismissed
      const dismissed = localStorage.getItem('loyaltyBannerDismissed') === 'true';
      setShowLoyaltyBanner(!dismissed);
    }
  }, [isLoggedIn]);

  // Handle logout
  const handleLogout = () => {
    clearUserSession();
    setIsLoggedIn(false);
    setUserName('');
    setShowUserDropdown(false);
    // Trigger logout event
    window.dispatchEvent(new CustomEvent('userLoggedOut'));
  };

  // Get LoyaltyUrl from accommodationGroupDetail and append token
  const getLoyaltyUrl = () => {
    let baseUrl = null;
    
    // Get LoyaltyUrl from accommodationGroupDetail.accommodationDetails[0].LoyaltyUrl
    if (accommodationGroupDetail?.accommodationDetails && Array.isArray(accommodationGroupDetail.accommodationDetails) && accommodationGroupDetail.accommodationDetails.length > 0) {
      baseUrl = accommodationGroupDetail.accommodationDetails[0]?.LoyaltyUrl || null;
    } else if (accommodationGroupDetail?.accommodationDetails?.LoyaltyUrl) {
      baseUrl = accommodationGroupDetail.accommodationDetails.LoyaltyUrl;
    }
    
    if (!baseUrl) {
      return null;
    }
    
    // Get token from user session (user->data->accessToken)
    const session = getUserSession();
    let token = null;
    
    // Priority: user->data->accessToken
    if (session?.user?.data?.directToken) {
      token = session.user.data.directToken;
    } else if (session?.user?.data?.token) {
      token = session.user.data.token;
    } else if (session?.token) {
      token = session.token;
    } else {
      // Fallback: try to get from localStorage
      token = getAuthToken();
    }
    
    if (!token) {
      return baseUrl; // Return base URL without token if token not found
    }
    
    // Construct URL: LoyaltyUrl + /dashboard?directToken=token
    // Remove trailing slash from baseUrl if exists
    const cleanBaseUrl = baseUrl.replace(/\/$/, '');
    return `${cleanBaseUrl}/dashboard?token=${token}`;
  };

  // Handle My Booking click
  const handleMyBooking = () => {
    const loyaltyUrl = getLoyaltyUrl();
    if (loyaltyUrl) {
      // Open in new tab
      window.open(loyaltyUrl, '_blank');
      setShowUserDropdown(false);
    } else {
      alert('My Booking URL is not available');
    }
  };

  // If no hotel_id, show message instead of full page
  // Track if initial page load is complete (accommodation detail loaded)
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  
  // Check if initial load is complete (accommodation detail AND first room search must be done)
  useEffect(() => {
    if (hasHotelId && (accommodationDetail || accommodationGroupDetail) && !isLoadingAccommodation && !isLoadingGroup && !isLoadingRooms) {
      setIsInitialLoadComplete(true);
    }
  }, [hasHotelId, accommodationDetail, accommodationGroupDetail, isLoadingAccommodation, isLoadingGroup, isLoadingRooms]);

  if (!hasHotelId) {
    return (
      <div className="h-screen bg-gray-50 font-sans antialiased overflow-hidden flex items-center justify-center">
        <div className="max-w-md mx-auto bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="mb-6">
            <svg className="mx-auto h-20 w-20 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Please Select Hotel</h2>
          <p className="text-gray-600 mb-6">Please select a hotel to view room availability and make a booking.</p>
          <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
            <i className="fas fa-info-circle"></i>
            <span>Hotel ID is required to proceed</span>
          </div>
        </div>
      </div>
    );
  }

  // Show single full page loader until accommodation detail and rooms are loaded
  if (!isInitialLoadComplete || isLoadingAccommodation || isLoadingGroup) {
    return (
      <div className="fixed inset-0 bg-white z-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 font-sans antialiased overflow-hidden" data-loyalty-tick={loyaltyTick}>
      <div className="max-w-md mx-auto h-screen bg-white shadow-2xl relative flex flex-col mobile-container">
        <main className="flex-1 overflow-y-auto p-0 flex flex-col">
          <TopContactBar accommodationDetail={accommodationDetail} />
          <AvailabilityHeader
            image={accommodationDetail?.logoURL || image}
            currentLanguage={currentLanguage}
            onLanguageChange={handleLanguageChange}
            currentCurrency={currentCurrency || (urlParams.currency ? {
              code: urlParams.currency,
              symbol: getCurrencySymbol(urlParams.currency),
              rate: 1,
              name: getCurrencyName(urlParams.currency),
            } : null)}
            onCurrencyChange={handleCurrencyChange}
            navigate={navigate}
            isLoggedIn={isLoggedIn}
            userName={userName}
            showUserDropdown={showUserDropdown}
            setShowUserDropdown={setShowUserDropdown}
            setShowSignInModal={setShowSignInModal}
            handleLogout={handleLogout}
            handleMyBooking={handleMyBooking}
            accommodationGroupDetail={accommodationGroupDetail}
          />

          {/* Compact Summary Bar - sticky under header so search context stays visible while scrolling (app pattern) */}
          {hasSearched && !showSearchForm && selectedDates.checkIn && selectedDates.checkOut && (
            <section className="sticky top-0 z-20 px-4 pt-3 pb-3 bg-gray-50/95 backdrop-blur-sm border-b border-gray-100">
              <div
                role="button"
                tabIndex={0}
                onClick={() => {
                  setShowSearchForm(true);
                  setTimeout(() => {
                    searchFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 100);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setShowSearchForm(true);
                    setTimeout(() => {
                      searchFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 100);
                  }
                }}
                className="bg-white rounded-2xl shadow-sm px-4 py-3 border border-gray-100 flex items-center justify-between cursor-pointer hover:border-gray-200 hover:shadow transition-all"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0 overflow-hidden">
                  {/* Dates */}
                  <div className="flex items-center gap-1.5 text-gray-700 flex-shrink-0">
                    <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="text-xs font-medium whitespace-nowrap">
                      {format(selectedDates.checkIn, 'MMM dd')} → {format(selectedDates.checkOut, 'MMM dd')}
                    </span>
                  </div>
                  {/* Nights badge */}
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[11px] font-medium rounded-full whitespace-nowrap flex-shrink-0">
                    {(() => {
                      const nights = Math.ceil((selectedDates.checkOut - selectedDates.checkIn) / (1000 * 60 * 60 * 24)) || 1;
                      return `${nights} ${nights === 1 ? t('common.night') : t('common.nights')}`;
                    })()}
                  </span>
                  {/* Guests */}
                  <div className="flex items-center gap-1.5 text-gray-700 min-w-0">
                    <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span className="text-xs font-medium truncate">
                      {adults} {adults === 1 ? t('search.adult') : t('search.adults')}
                      {children > 0 && `, ${children} ${children === 1 ? t('search.child') : t('search.children')}`}
                    </span>
                  </div>
                </div>
                {/* Edit icon — whole bar is clickable, this is just the visual cue */}
                <span className="ml-2 p-1.5 text-gray-400 flex-shrink-0" aria-hidden="true">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                </span>
              </div>
            </section>
          )}

          {/* Quick Search Card */}
          <section ref={searchFormRef} className={`px-4 mb-6 ${hasSearched && !showSearchForm ? 'hidden' : ''} ${isLandingMode ? 'mt-8' : 'mt-6'}`}>
            {isLandingMode && (
              <div className="text-center mb-4 animate-fade-in">
                <h2 className="text-lg font-bold text-gray-900">
                  {accommodationDetail?.accommodationName
                    ? `Welcome to ${accommodationDetail.accommodationName}`
                    : 'Your stay starts here'}
                </h2>
              </div>
            )}
            <div className="bg-white rounded-2xl shadow-lg p-4 border border-gray-100 animate-scale-in">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('room.findYourPerfectRoom')}
                </h3>
                {/* Close — collapse the search form back to results (only when results already exist) */}
                {hasSearched && !isLandingMode && (
                  <button
                    type="button"
                    onClick={() => setShowSearchForm(false)}
                    aria-label="Close search"
                    className="flex-shrink-0 w-9 h-9 -mr-1 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 active:bg-gray-200 active:scale-90 transition-all"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>

              <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
                {/* Location with Hotel Search / Accommodation Dropdown - Same condition as Desktop SearchForm */}
                {Number(isAccommodationDropDown) !== 0 && (
                <div className="relative" ref={groupId ? accommodationDropdownRef : hotelDropdownRef}>
                  <div
                    className="absolute left-3 top-1/2 text-gray-400 form-icon-container"
                    style={{ transform: "translateY(-50%) !important" }}
                  >
                    <i className="fas fa-map-marker-alt form-icon"></i>
                  </div>
                  {groupId && accommodations.length > 0 && Number(isAccommodationDropDown) !== 0 ? (
                    <>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowAccommodationDropdown(!showAccommodationDropdown);
                        }}
                        className="w-full pl-10 pr-10 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all text-left flex items-center justify-between"
                      >
                        <span className="truncate text-gray-900">
                          {selectedAccommodation
                            ? (selectedAccommodation.accommodationName || selectedAccommodation.AccommodationName)
                            : t('search.selectHotel')}
                        </span>
                        <svg
                          className={`h-4 w-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ${
                            showAccommodationDropdown ? "rotate-180" : ""
                          }`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                      {showAccommodationDropdown && (
                        <div
                          className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-[9999] max-h-60 overflow-y-auto"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {isLoadingAccommodations ? (
                            <div className="p-4 text-center text-xs text-gray-500">
                              {t('search.loadingHotels')}
                            </div>
                          ) : accommodations.length > 0 ? (
                            <ul className="py-1 relative">
                              {accommodations.map((acc) => {
                                const accId = acc.accommodationId || acc.AccommodationId;
                                const accName = acc.accommodationName || acc.AccommodationName;
                                const isSelected = selectedAccommodation &&
                                  (selectedAccommodation.accommodationId === accId ||
                                   selectedAccommodation.AccommodationId === accId);

                                // Check if this accommodation has limited rooms (for "Last Room" badge)
                                const showLastRoom = acc.totalroom && acc.totalroom <= 3;
                                const lastRoomCount = acc.totalroom || 0;

                                return (
                                  <li key={accId} className="relative isolate">
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setSelectedAccommodation(acc);
                                        setShowAccommodationDropdown(false);
                                        handleAccommodationChange(accId);
                                      }}
                                      className={`w-full text-left px-4 py-2 text-xs hover:bg-gray-100 transition-colors relative ${
                                        isSelected ? 'bg-blue-50 text-blue-900 font-medium' : 'text-gray-900'
                                      }`}
                                      style={{ zIndex: 10 }}
                                    >
                                      <div className="flex flex-col relative">
                                        <span className="truncate relative" style={{ zIndex: 11 }}>{accName}</span>
                                        {showLastRoom && lastRoomCount > 0 && (
                                          <span
                                            className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold bg-teal-500 text-white w-fit mt-1 relative"
                                            style={{ zIndex: 1 }}
                                          >
                                            {lastRoomCount === 1 ? 'Last Room' : `Last ${lastRoomCount} Rooms`}
                                          </span>
                                        )}
                                      </div>
                                    </button>
                                  </li>
                                );
                              })}
                            </ul>
                          ) : (
                            <div className="p-4 text-center text-xs text-gray-500">
                              No hotels available
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    /* Single hotel or no group - show readonly hotel name like desktop */
                    <>
                      <input
                        type="text"
                        value={accommodationDetail?.AccommodationName || accommodationDetail?.accommodationName || ''}
                        className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 form-input-with-icon"
                        readOnly
                      />
                    </>
                  )}
                </div>
                )}

                {/* Date Range */}
                <div className="relative">
                  <div
                    className="absolute left-3 top-1/2 text-gray-400 form-icon-container"
                    style={{ transform: "translateY(-50%) !important" }}
                  >
                    <i className="fas fa-calendar-alt form-icon"></i>
                  </div>
                  <input
                    type="text"
                    readOnly
                    placeholder={t('search.selectDates')}
                    value={
                      selectedDates.checkIn && selectedDates.checkOut
                        ? `${format(selectedDates.checkIn, "dd/MM/yyyy")} - ${format(
                            selectedDates.checkOut,
                            "dd/MM/yyyy",
                          )}`
                        : t('search.selectDates')
                    }
                    onClick={() => {
                      setShowCalendar(!showCalendar);
                      setShowGuestSelector(false); // close guest box so they never overlap
                    }}
                    className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent active:bg-gray-100 transition-all cursor-pointer form-input-with-icon"
                  />

                  {/* Date Range Calendar — native bottom-sheet on mobile (clean, focused) */}
                  <BottomSheet
                    open={showCalendar}
                    onClose={() => setShowCalendar(false)}
                    title={t('search.selectDates') || 'Select your dates'}
                    maxHeight="85vh"
                  >
                    <div className="flex justify-center">
                      <DateRange
                        ranges={[
                          {
                            startDate: selectedDates.checkIn || new Date(),
                            endDate: selectedDates.checkOut || new Date(),
                            key: "selection",
                          },
                        ]}
                        onChange={(ranges) => {
                          const newCheckIn = ranges.selection.startDate;
                          let newCheckOut = ranges.selection.endDate;
                          // Checkout must always be at least 1 day after checkin
                          if (newCheckOut <= newCheckIn) {
                            newCheckOut = new Date(newCheckIn);
                            newCheckOut.setDate(newCheckOut.getDate() + 1);
                          }
                          setSelectedDates({ checkIn: newCheckIn, checkOut: newCheckOut });
                        }}
                        moveRangeOnFirstSelection={false}
                        rangeColors={[calendarColor]}
                        minDate={new Date()} // disables past dates
                        showDateDisplay={false} // cleaner UI
                        months={2} // 2 months so cross-month ranges (31 → 4) need no arrow
                        direction="vertical"
                      />
                    </div>
                    <div className="sticky bottom-0 bg-white pt-3 pb-1 mt-1 border-t border-gray-200">
                      <button
                        onClick={() => setShowCalendar(false)}
                        className="calendar-done-btn w-full min-h-[44px] py-3 text-white text-sm font-semibold rounded-lg hover:opacity-90 active:scale-[0.99] transition-all"
                        style={{ backgroundColor: calendarColor || '#b02e36' }}
                      >
                        {t('search.done')}
                      </button>
                    </div>
                  </BottomSheet>
                </div>

                {/* Guests & Promo Code - Same row */}
                <div className="flex items-end gap-2">
                <div ref={guestSelectorRef} className="relative guest-selector-container" style={{ width: '45%' }}>
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    {t('search.guests')}
                  </label>
                  <button
                    onClick={() => { setShowGuestSelector(!showGuestSelector); setShowCalendar(false); }}
                    className="w-full px-3 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent active:bg-gray-100 transition-all text-left flex items-center justify-between"
                  >
                    <span className="text-xs text-gray-900 truncate">
                      {adults} {adults === 1 ? t('search.adult') : t('search.adults')}
                      {children > 0 && `, ${children} ${children === 1 ? t('search.child') : t('search.children')}`}
                    </span>
                    <svg
                      className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${
                        showGuestSelector ? "rotate-180" : ""
                      }`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>

                  {showGuestSelector && (
                    <div className="absolute top-full left-0 mt-2 bg-white border border-gray-200 rounded-xl p-4 z-[9999] shadow-xl" style={{ minWidth: 'min(260px, calc(100vw - 2rem))', maxWidth: 'calc(100vw - 2rem)', right: 'auto' }}>
                      <div className="space-y-4">
                        {/* Adults */}
                        {showAdultsOption && (
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-gray-900 font-medium text-sm block">
                              {t('search.adults')}
                            </span>
                    </div>
                          <div className="flex items-center rounded-lg border-2 overflow-hidden bg-white" style={{ borderColor: 'var(--bgButton)' }}>
                            <button
                              onClick={() => setAdults(Math.max(1, adults - 1))}
                              className="w-10 h-10 flex items-center justify-center text-xl font-bold bg-white hover:bg-gray-50 active:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors active:scale-95"
                              style={{ color: 'var(--bgButton)' }}
                              disabled={adults <= 1}
                              aria-label="Decrease adults"
                            >
                              &minus;
                            </button>
                            <span className="h-10 px-1 min-w-[36px] text-center text-sm font-bold text-gray-900 flex items-center justify-center bg-white">
                              {adults}
                            </span>
                            <button
                              onClick={() => { const maxAdults = Number(accommodationDetail?.MaxAdultQty || accommodationDetail?.maxAdultQty) || 5; setAdults(Math.min(maxAdults, adults + 1)); }}
                              className="w-10 h-10 flex items-center justify-center text-xl font-bold bg-white hover:bg-gray-50 active:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors active:scale-95"
                              style={{ color: 'var(--bgButton)' }}
                              disabled={adults >= (Number(accommodationDetail?.MaxAdultQty || accommodationDetail?.maxAdultQty) || 5)}
                              aria-label="Increase adults"
                            >
                              +
                            </button>
                          </div>
                        </div>
                        )}

                        {/* Children */}
                        {showChildrenOption && (
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-gray-900 font-medium text-sm block">
                              {t('search.children')}
                            </span>
                            {(accommodationDetail?.FreeChild || accommodationDetail?.freeChild) ? (
                              <p className="text-xs text-gray-500">
                                Age 0-{accommodationDetail?.FreeChild || accommodationDetail?.freeChild}
                              </p>
                            ) : null}
                          </div>
                          <div className="flex items-center rounded-lg border-2 overflow-hidden bg-white" style={{ borderColor: 'var(--bgButton)' }}>
                            <button
                              onClick={() => {
                                const newChildren = Math.max(0, children - 1);
                                setChildren(newChildren);
                                // Update child ages array when children count changes
                                if (newChildren < childAges.length) {
                                  setChildAges(childAges.slice(0, newChildren));
                                }
                              }}
                              className="w-10 h-10 flex items-center justify-center text-xl font-bold bg-white hover:bg-gray-50 active:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors active:scale-95"
                              style={{ color: 'var(--bgButton)' }}
                              disabled={children <= 0}
                              aria-label="Decrease children"
                            >
                              &minus;
                            </button>
                            <span className="h-10 px-1 min-w-[36px] text-center text-sm font-bold text-gray-900 flex items-center justify-center bg-white">
                              {children}
                            </span>
                            <button
                              onClick={() => {
                                const maxChildren = Number(accommodationDetail?.MaxChildQty || accommodationDetail?.maxChildQty) || 4;
                                const newChildren = Math.min(maxChildren, children + 1);
                                setChildren(newChildren);
                                // Update child ages array when children count changes
                                if (newChildren > childAges.length) {
                                  setChildAges([...childAges, ...Array(newChildren - childAges.length).fill(1)]);
                                }
                              }}
                              className="w-10 h-10 flex items-center justify-center text-xl font-bold bg-white hover:bg-gray-50 active:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors active:scale-95"
                              style={{ color: 'var(--bgButton)' }}
                              disabled={children >= (Number(accommodationDetail?.MaxChildQty || accommodationDetail?.maxChildQty) || 4)}
                              aria-label="Increase children"
                            >
                              +
                            </button>
                          </div>
                        </div>
                        )}

                        {/* Child Age Selectors - Show only if children > 0 */}
                        {showChildrenOption && children > 0 && (
                          <div className="pt-3 border-t border-gray-200 space-y-3">
                            {Array.from({ length: children }).map((_, index) => (
                              <div key={index} className="flex items-center justify-between">
                                <span className="text-gray-900 font-medium text-sm">
                                  Child {index + 1} Age
                                </span>
                    <select
                                  value={childAges[index] || 1}
                                  onChange={(e) => {
                                    const newAges = [...childAges];
                                    newAges[index] = parseInt(e.target.value);
                                    setChildAges(newAges);
                                  }}
                                  className="w-24 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                                >
                                  {Array.from({ length: Number(accommodationDetail?.FreeChild || accommodationDetail?.freeChild) || 10 }, (_, i) => i + 1).map((age) => (
                                    <option key={age} value={age}>
                                      {age} {age === 1 ? 'year' : 'years'}
                        </option>
                      ))}
                    </select>
                    </div>
                            ))}
                  </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Promo Code */}
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-700 mb-2">
                      {t('search.promoCode')}
                    </label>
                    <div className="relative">
                      <div
                        className="absolute left-2.5 top-1/2 text-gray-400 form-icon-container"
                        style={{ transform: "translateY(-50%) !important" }}
                      >
                        <i className="fas fa-tag form-icon text-xs"></i>
                      </div>
                      <input
                        type="text"
                        placeholder={t('search.promoCode')}
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value)}
                        className="w-full pl-8 pr-2 py-3 text-xs bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all form-input-with-icon"
                      />
                    </div>
                </div>
                </div>

                {/* Search Button */}
                <button
                  type="submit"
                  onClick={handleSearchAvailability}
                  disabled={isLoadingRooms}
                  className="w-full card-head text-white py-3 rounded-xl font-semibold shadow-lg hover:bg-primary-600 active:scale-95 transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoadingRooms ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      <span>{t('search.searching')}</span>
                    </>
                  ) : (
                    <>
                  <i className="fas fa-search"></i>
                  <span>{t('search.checkAvailability')}</span>
                    </>
                  )}
                </button>
              </form>
            </div>
            {isLandingMode && (
              <p className="text-center text-sm text-gray-500 mt-4 animate-fade-in">
                Book direct &amp; save more — your perfect room is just a couple of dates away.
              </p>
            )}
          </section>

          <section className="px-4 pt-4 pb-28">
            {Array.isArray(addOns) && addOns.length > 0 && (
              <Tabs
                tabs={[
                  { label: t('tabs.rooms'), value: "rooms" },
                  { label: t('tabs.addOns'), value: "addons" },
                ]}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
              />
            )}
            {activeTab === "rooms" ? (
              <>
                {/* Loyalty Banner - Sticky at top while scrolling (Mobile, logged-out users) */}
                {showLoyaltyBanner && !isLoggedIn && Number(hasLoyalty) === 1 && (
                  <div className="relative bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3 mb-4 fade-in shadow-md">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center flex-1 min-w-0">
                        <div className="flex-shrink-0 mr-1.5">
                          <span className="text-base">🔒</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-gray-900 mb-0.5 leading-tight line-clamp-2">
                            {memberSavePercent > 0
                              ? t('loyalty.membersOnlyShort', { percent: memberSavePercent })
                              : t('loyalty.membersSaveMore')}
                          </p>
                          <p className="text-[10px] text-gray-600 leading-tight line-clamp-2">
                            {memberSavePercent > 0
                              ? t('loyalty.joinFreeSaveShort', { percent: memberSavePercent })
                              : t('loyalty.signInToUnlock')}
                          </p>
                          <span className="mt-1 inline-flex items-center gap-1 bg-emerald-100 border border-emerald-300 text-emerald-800 text-[10px] font-semibold px-2 py-0.5 rounded-full shadow-sm w-fit">
                            <svg className="h-3 w-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {t('search.bestPriceTitle')}
                          </span>
                        </div>
                        <button
                          onClick={() => setShowSignInModal(true)}
                          className="ml-2 px-3 min-h-[32px] bg-blue-600 text-white text-[10px] font-medium rounded-lg hover:bg-blue-700 active:bg-blue-800 active:scale-95 transition-all whitespace-nowrap flex-shrink-0 self-center"
                        >
                          {memberSavePercent > 0 ? t('loyalty.savePercent', { percent: memberSavePercent }) : t('header.signIn')}
                        </button>
                      </div>
                      <button
                        onClick={() => {
                          setShowLoyaltyBanner(false);
                          localStorage.setItem('loyaltyBannerDismissed', 'true');
                        }}
                        className="ml-1 flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-200/60 active:bg-gray-300/70 active:scale-90 transition-all duration-150"
                        aria-label="Close banner"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}

                {isLoadingRooms && (
                  <RoomCardSkeleton count={3} />
                )}
                {roomsError && !isLoadingRooms && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                    <p className="text-red-800 text-sm">{roomsError}</p>
                    <button
                      onClick={handleSearchAvailability}
                      className="mt-2 text-red-600 hover:text-red-800 text-sm underline"
                    >
                      Try again
                    </button>
                  </div>
                )}
                {!isLoadingRooms && displayRooms.length === 0 && hasSearched && (
                  <div className="bg-white rounded-2xl mb-4 shadow-lg border border-gray-100 p-8 text-center">
                    <div className="mb-4">
                      <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">{t('room.noRoomsAvailable')}</h3>
                    <p className="text-gray-600 mb-2">{t('room.noRoomsAtDate')}</p>
                    <p className="text-gray-600 mb-4">{t('room.changeDateMessage')}</p>
                    <button
                      onClick={() => {
                        // Open the search form + calendar so the guest can pick new dates
                        setShowSearchForm(true);
                        setShowCalendar(true);
                        setShowGuestSelector(false);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="mt-4 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium inline-flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {t('room.searchAgain')}
                    </button>
                  </div>
                )}

                {/* Best-price / book-direct trust bar — only when the loyalty banner isn't already showing (avoid double bars) */}
                {displayRooms.length > 0 && !(showLoyaltyBanner && !isLoggedIn && Number(hasLoyalty) === 1) && (
                  <div className="flex items-center gap-2.5 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2.5 mb-4">
                    <svg className="h-5 w-5 flex-shrink-0 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="leading-tight">
                      <p className="text-sm font-semibold text-emerald-800">{t('search.bestPriceTitle')}</p>
                      <p className="text-xs text-emerald-700">{t('search.bestPriceSubtitle')}</p>
                    </div>
                  </div>
                )}

                {displayRooms.map((room) => (
                  <div
                    key={room.id}
                    className="card-enter bg-white rounded-2xl mb-4 shadow-lg border border-gray-100 overflow-hidden"
                  >
                    <SimpleImageSlider
                      images={room.images}
                      roomName={room.name}
                      discount={room.discount}
                      rating={room.rating}
                      roomsAvailable={room.totalroom || null}
                      autoplay={false}
                      showControls={true}
                      onImageClick={(idx) => {
                        if (room.images && room.images.length > 0) {
                          setImageLightbox({ images: room.images, roomName: room.name, index: idx || 0 });
                        }
                      }}
                    />

                    <div className="p-4">
                      <h4 className="text-[15px] font-semibold mb-3" style={{color: 'var(--primary-color, #1a1a1a)'}}>
                        {room.name}
                      </h4>

                      <div className="flex items-center flex-nowrap gap-1.5 mb-3">
                        {room.RoomMaxPerson && (
                          <div className="inline-flex items-center gap-1 bg-gray-100 rounded-full px-2 py-1 flex-shrink-0">
                            <svg className="h-3.5 w-3.5 text-gray-600 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                            </svg>
                            <span className="text-[10px] font-medium text-gray-700 whitespace-nowrap">
                              {room.RoomMaxPerson} {Number(room.RoomMaxPerson) === 1 ? t('room.guest') : t('room.guests')}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center space-x-1 flex-shrink-0">
                          <i className="fas fa-bed text-gray-400 text-[10px] flex-shrink-0"></i>
                          <span className="text-[10px] text-gray-600 whitespace-nowrap">
                            {room.BedSize}
                          </span>
                        </div>
                        <div
                          onClick={() => showRoomDetailsModal(room)}
                          className="flex items-center cursor-pointer px-1.5 py-1 rounded-md text-blue-600 hover:bg-blue-50 active:bg-blue-100 active:scale-95 transition border border-transparent hover:border-blue-400 text-[10px] flex-shrink-0"
                        >
                          <i className="fas fa-info-circle text-blue-500 mr-1"></i>
                          <span className="font-medium whitespace-nowrap">{t('room.roomDetails')}</span>
                        </div>
                      </div>

                      {/* Amenities Preview */}
                      {room.amenities?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {room.amenities?.slice(0, 3).map((amenity, index) => (
                            <span key={index} className="inline-flex items-center text-[10px] text-gray-600 bg-gray-100 rounded-full px-2 py-0.5">
                              <i className="fas fa-check text-green-500 mr-1 text-[7px]"></i>
                              {amenity}
                            </span>
                          ))}
                          {room.amenities?.length > 3 && (
                            <span
                              onClick={() => showRoomDetailsModal(room)}
                              className="inline-flex items-center text-[10px] text-blue-600 bg-blue-50 rounded-full px-2 py-0.5 cursor-pointer active:bg-blue-100 active:scale-95 transition"
                            >
                              +{room.amenities.length - 3} more
                            </span>
                          )}
                        </div>
                      )}

                      {/* Rate Plans Section - Only show when dates and adults are selected */}
                      {selectedDates.checkIn &&
                      selectedDates.checkOut &&
                      adults > 0 ? (
                        <div className="mb-4">
                          <h5 className="text-sm font-medium text-gray-700 mb-3">
                            {t('room.selectRatePlan')}
                          </h5>
                          <div className="space-y-3">
                            {(room.ratePlans || []).map((ratePlan, ratePlanIndex) => {
                              // Promotion only applies if both name and discount value are present
                              // Check for promotion discount from availabilityApi
                              const hasPromotionFromAvailability = ratePlan.promotionDiscount && 
                                (ratePlan.promotionDiscount.discount > 0 || ratePlan.promotionDiscount.DiscountValue > 0) &&
                                (ratePlan.promotionDiscount.promotionName || ratePlan.promotionName);
                              
                              // Calculate nights
                              const nights = Math.ceil(
                                (selectedDates.checkOut - selectedDates.checkIn) / (1000 * 60 * 60 * 24)
                              ) || 1;
                              
                              // Check if plan has taxes
                              const hasTax = () => {
                                if (ratePlan.taxes && Array.isArray(ratePlan.taxes) && ratePlan.taxes.length > 0) {
                                  const hasNonZeroTax = ratePlan.taxes.some(tax => {
                                    const taxAmount = tax.TaxAmmount || tax.TaxAmount || tax.taxAmount || tax.amount || 0;
                                    const taxValue = tax.TaxValue || tax.taxValue || tax.value || 0;
                                    return parseFloat(taxAmount) > 0 || parseFloat(taxValue) > 0;
                                  });
                                  return hasNonZeroTax;
                                }
                                return false;
                              };
                              
                              // Calculate tax per night for 1 room
                              const calculateTaxPerNight = () => {
                                if (!ratePlan.taxes || !Array.isArray(ratePlan.taxes) || ratePlan.taxes.length === 0) {
                                  return 0;
                                }

                                // Calculate total tax per night for 1 room by summing ALL taxes
                                let totalTax = 0;
                                
                                ratePlan.taxes.forEach((tax) => {
                                  if (isTaxIncluded(tax)) return; // Included tax = already in rate, don't add
                                  const taxId = tax.TaxId || tax.taxId || tax.TaxID || 0;

                                  const taxAmount = parseFloat(
                                    tax.TaxAmmount || tax.TaxAmount || tax.taxAmount || tax.amount || 0
                                  );
                                  
                                  const taxValue = parseFloat(
                                    tax.TaxValue || tax.taxValue || tax.value || 0
                                  );
                                  
                                  let calculatedTax = 0;
                                  
                                  // For TaxId = 19, TaxValue is typically a fixed amount per room per night
                                  if (taxId === 19) {
                                    calculatedTax = taxValue;
                                  } else if (taxAmount > 0) {
                                    // If TaxAmmount exists (from tax API), use it
                                    // TaxAmmount is typically for 1 room for all nights, so divide by nights to get per night
                                    calculatedTax = nights > 0 ? taxAmount / nights : taxAmount;
                                  } else if (taxValue > 0) {
                                    // If only TaxValue exists, calculate based on room price
                                    // If taxValue is less than 100, likely a percentage
                                    if (taxValue < 100 && taxValue > 0) {
                                      // Calculate tax as percentage of room price per night
                                      calculatedTax = (displayPrice * taxValue) / 100;
                                    } else {
                                      // Treat as fixed amount per night
                                      calculatedTax = taxValue;
                                    }
                                  }
                                  
                                  // Add this tax to the total sum (sums all taxes: service, GST, VAT, etc.)
                                  if (!isNaN(calculatedTax) && calculatedTax > 0) {
                                    totalTax += calculatedTax;
                                  }
                                });

                                // Round to 2 decimal places
                                const roundedTax = Math.round(totalTax * 100) / 100;
                                
                                return roundedTax;
                              };
                              
                              // Get loyalty discount if user is logged in
                              const loyaltyDiscount = isLoggedIn ? getLoyaltyDiscount() : null;
                              const loyaltyData = loyaltyDiscount && loyaltyDiscount.data ? loyaltyDiscount.data : null;
                              const loyaltyDiscountValue = loyaltyData && loyaltyData.Value !== undefined && loyaltyData.Value !== null
                                ? parseFloat(loyaltyData.Value) 
                                : 0;
                              const loyaltyMaxValue = loyaltyData && loyaltyData.MaxValue !== undefined && loyaltyData.MaxValue !== null 
                                ? parseFloat(loyaltyData.MaxValue) 
                                : 0;
                              const loyaltyType = loyaltyData && loyaltyData.Type ? loyaltyData.Type : 'percent';
                              const membershipTypeName = loyaltyData && loyaltyData.membership_type && loyaltyData.membership_type.MemberShipTypeName 
                                ? loyaltyData.membership_type.MemberShipTypeName 
                                : null;
                              
                              // Get original rate price first - check multiple possible price fields
                              // Priority: ratePlan.price > ratePlan.Rate > ratePlan.BaseRate > rateDetailsByDate > room.basePrice
                              let ratePlanPrice = null;
                              if (ratePlan.price != null && ratePlan.price !== 0) {
                                ratePlanPrice = parseFloat(ratePlan.price);
                              } else if (ratePlan.Rate != null && ratePlan.Rate !== 0) {
                                ratePlanPrice = parseFloat(ratePlan.Rate);
                              } else if (ratePlan.BaseRate != null && ratePlan.BaseRate !== 0) {
                                ratePlanPrice = parseFloat(ratePlan.BaseRate);
                              } else if (ratePlan.rateDetailsByDate && Array.isArray(ratePlan.rateDetailsByDate) && ratePlan.rateDetailsByDate.length > 0) {
                                // Try to get price from rateDetailsByDate (first date's rate)
                                const firstRateDetail = ratePlan.rateDetailsByDate[0];
                                if (firstRateDetail) {
                                  // First check convertedRate in rateDetailsByDate
                                  const dateConvertedRate = firstRateDetail.convertedRate || firstRateDetail.ConvertedRate;
                                  if (dateConvertedRate && parseFloat(dateConvertedRate) > 0) {
                                    ratePlanPrice = parseFloat(dateConvertedRate);
                                  } else {
                                    // Then check regular rate
                                    const dateRate = firstRateDetail.rate || firstRateDetail.Rate || firstRateDetail.baseRate || firstRateDetail.BaseRate;
                                    if (dateRate != null && dateRate !== 0) {
                                      ratePlanPrice = parseFloat(dateRate);
                                    }
                                  }
                                }
                              }
                              
                              const roomBasePrice = room.basePrice != null && room.basePrice !== 0 ? parseFloat(room.basePrice) : null;
                              
                              // Get originalPrice from ratePlan.price (this is the base price)
                              const originalPrice = (ratePlanPrice != null && !isNaN(ratePlanPrice) && ratePlanPrice > 0) 
                                ? ratePlanPrice 
                                : ((roomBasePrice != null && !isNaN(roomBasePrice) && roomBasePrice > 0) ? roomBasePrice : 0);
                              
                              // Check ConvertedRate - if it's 0 or null, use originalPrice
                              const convertedRate = ratePlan.convertedRate != null ? parseFloat(ratePlan.convertedRate) : null;
                              const useConvertedRate = convertedRate != null && !isNaN(convertedRate) && convertedRate > 0;
                              
                              // Final price: use convertedRate if available and > 0, otherwise use originalPrice
                              let finalPrice = useConvertedRate ? convertedRate : originalPrice;
                              
                              // Calculate promotion discount percentage from availabilityApi
                              let promotionDiscountPercent = 0;
                              if (hasPromotionFromAvailability) {
                                if ((ratePlan.promotionDiscount.discountType || '').toLowerCase().includes('percentage')) {
                                  const discountVal = parseFloat(ratePlan.promotionDiscount.discountValue || ratePlan.promotionDiscount.DiscountValue || 0);
                                  promotionDiscountPercent = Math.max(0, Math.min(100, discountVal));
                                } else {
                                  promotionDiscountPercent = parseFloat(ratePlan.promotionDiscount.discountValue || ratePlan.promotionDiscount.DiscountValue || 0);
                                }
                              }
                              
                              // Get discount from fetchRedeemOverrideRules API (if available and user is logged in)
                              let redeemOverrideDiscountPercent = 0;
                              if (isLoggedIn && loyaltyDiscountValue > 0 && loyaltyType === 'percent') {
                                const validLoyaltyDiscount = Math.max(0, Math.min(100, loyaltyDiscountValue));
                                redeemOverrideDiscountPercent = validLoyaltyDiscount;
                              }
                              
                              // Calculate total discount based on conditions:
                              // 1. If only availabilityApi discount exists -> use it
                              // 2. If only fetchRedeemOverrideRules discount exists -> use it
                              // 3. If both exist -> sum them
                              let totalDiscountPercent = 0;
                              
                              if (hasPromotionFromAvailability && redeemOverrideDiscountPercent > 0) {
                                // Both discounts exist - sum them
                                totalDiscountPercent = promotionDiscountPercent + redeemOverrideDiscountPercent;
                                
                                // Apply MaxValue limit dynamically from API - if total exceeds MaxValue, cap at MaxValue
                                if (loyaltyMaxValue > 0 && totalDiscountPercent > loyaltyMaxValue) {
                                  totalDiscountPercent = loyaltyMaxValue;
                                }
                              } else if (hasPromotionFromAvailability) {
                                // Only availabilityApi discount exists
                                totalDiscountPercent = promotionDiscountPercent;
                              } else if (redeemOverrideDiscountPercent > 0) {
                                // Only fetchRedeemOverrideRules discount exists
                                totalDiscountPercent = redeemOverrideDiscountPercent;
                                
                                // Apply MaxValue limit dynamically from API - if total exceeds MaxValue, cap at MaxValue
                                if (loyaltyMaxValue > 0 && totalDiscountPercent > loyaltyMaxValue) {
                                  totalDiscountPercent = loyaltyMaxValue;
                                }
                              }
                              
                              // Final safety check: ensure total discount doesn't exceed 100%
                              if (totalDiscountPercent > 100) {
                                totalDiscountPercent = 100;
                              }
                              
                              // Final validation: ensure total discount is between 0 and 100
                              totalDiscountPercent = Math.max(0, Math.min(100, totalDiscountPercent));
                              
                              // Check if we have any discount (from availabilityApi or fetchRedeemOverrideRules)
                              const hasAnyDiscount = hasPromotionFromAvailability || redeemOverrideDiscountPercent > 0;
                              
                              // Apply discount if available
                              let displayPrice = finalPrice;
                              if (hasAnyDiscount) {
                                if (isLoggedIn && loyaltyDiscountValue > 0 && loyaltyType === 'percent') {
                                  // User is logged in with loyalty discount - apply total discount
                                  if (useConvertedRate && convertedRate > 0) {
                                    displayPrice = convertedRate * (1 - (totalDiscountPercent / 100));
                                  } else {
                                    displayPrice = finalPrice * (1 - (totalDiscountPercent / 100));
                                  }
                                } else {
                                  // User is NOT logged in - use API's afterDiscountAmount if available
                                  // But skip if convertedRate is being used (afterDiscountAmount is in original currency)
                                  if (hasPromotionFromAvailability && ratePlan.promotionDiscount?.afterDiscountAmount && !useConvertedRate) {
                                    displayPrice = parseFloat(ratePlan.promotionDiscount.afterDiscountAmount) || finalPrice;
                                  } else if (useConvertedRate && convertedRate > 0) {
                                    displayPrice = convertedRate * (1 - (totalDiscountPercent / 100));
                                  } else {
                                    displayPrice = finalPrice * (1 - (totalDiscountPercent / 100));
                                  }
                                }
                              }
                              
                              // Apply secret deal discount if available (after loyalty discount)
                              if (ratePlan.secretDeal) {
                                displayPrice = displayPrice * (1 - (ratePlan.memberDiscount || 0));
                              }
                              
                              
                              return (
                              <div
                                key={ratePlan.id}
                                className={`rate-plan-option rounded-xl p-4 transition-all duration-200 ${
                                  isRatePlanSelected(room.id, ratePlan.id)
                                    ? "border border-slate-600 bg-green-50 shadow-lg ring-2 ring-green-200"
                                    : hasAnyDiscount
                                      ? "border bg-gradient-to-r from-blue-50 to-white shadow-lg"
                                      : "border border-slate-300 bg-white shadow-sm hover:shadow-md hover:border-slate-400"
                                }`}
                              >
                                {/* Top Row: Name + Price */}
                                <div className="flex items-start justify-between gap-2 mb-1">
                                  <div className="flex-1 min-w-0">
                                    {/* Rate Plan Name + Promotion Badge - Inline */}
                                    <div className="flex items-center gap-1.5 flex-wrap mb-1">
                                    {isRatePlanSelected(
                                      room.id,
                                      ratePlan.id,
                                    ) && (
                                        <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                                          <i className="fas fa-check text-white text-[11px]"></i>
                                      </div>
                                    )}
                                      <h4 className={`text-sm font-bold leading-tight break-words ${
                                        !hasAnyDiscount ? "text-slate-900" : "text-gray-900"
                                      }`}>
                                      {ratePlan.name}
                                    </h4>

                                    {/* Secret Deal Badge - Inline with Rate Plan Name */}
                                    {ratePlan.secretDeal && !hasPromotionFromAvailability && redeemOverrideDiscountPercent === 0 && (
                                        <span className="inline-block px-1 py-0.5 rounded text-[7px] font-bold leading-snug"
                                          style={{
                                            backgroundColor: 'var(--primary-color, #facc15)',
                                            color: '#fff',
                                          }}
                                        >
                                          <svg className="h-2 w-2 inline-block align-middle mr-0.5" style={{ color: '#fff' }} fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M10 15l-3.09 1.636.588-3.43L5 10.364l3.454-.502L10 6.5l1.546 3.362L15 10.364l-2.498 2.842.588 3.43z" />
                                          </svg>
                                          {t('ratePlan.secretDeal')}
                                        </span>
                                    )}

                                    {/* Promotion Name Badge - Inline with Rate Plan Name */}
                                    {hasAnyDiscount && (ratePlan.promotionDiscount?.promotionName || ratePlan.promotionName || membershipTypeName) && (
                                        <span className="inline-block px-1 py-0.5 rounded text-[7px] font-bold leading-snug"
                                          style={{
                                            backgroundColor: 'var(--primary-color, #facc15)',
                                            color: '#fff',
                                          }}
                                        >
                                          <svg className="h-2 w-2 inline-block align-middle mr-0.5" style={{ color: '#fff' }} fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M10 15l-3.09 1.636.588-3.43L5 10.364l3.454-.502L10 6.5l1.546 3.362L15 10.364l-2.498 2.842.588 3.43z" />
                                          </svg>
                                          {(() => {
                                            // Determine display name based on discount source
                                            let displayName = '';

                                            if (hasPromotionFromAvailability && redeemOverrideDiscountPercent > 0) {
                                              // Both discounts exist - show both names
                                              const promotionName = ratePlan.promotionDiscount?.promotionName || ratePlan.promotionName || '';
                                              const promoName = (typeof promotionName === 'string' ? promotionName.trim() : String(promotionName || '')).toUpperCase();
                                              const memberName = membershipTypeName ? membershipTypeName.toUpperCase() : '';
                                              if (promoName && memberName) {
                                                displayName = `${promoName} + ${memberName}`;
                                              } else {
                                                displayName = promoName || memberName || 'PROMOTION';
                                              }
                                            } else if (hasPromotionFromAvailability) {
                                              // Only availabilityApi discount exists - show promotion name
                                              const promotionName = ratePlan.promotionDiscount?.promotionName || ratePlan.promotionName || '';
                                              displayName = (typeof promotionName === 'string' ? promotionName.trim() : String(promotionName || '')).toUpperCase();
                                            } else if (redeemOverrideDiscountPercent > 0) {
                                              // Only fetchRedeemOverrideRules discount exists - show membership type name
                                              displayName = membershipTypeName ? membershipTypeName.toUpperCase() : 'MEMBER DISCOUNT';
                                            }

                                            // Append SECRET DEAL if applicable
                                            if (ratePlan.secretDeal && displayName) {
                                              displayName = `${displayName} + ${t('ratePlan.secretDeal').toUpperCase()}`;
                                            }

                                            return displayName || 'PROMOTION';
                                          })()}
                                        </span>
                                    )}
                                    </div>

                                </div>

                                  {/* Price Display - Right Side */}
                                  <div className="text-right ml-2 flex-shrink-0 max-w-[48%]">
                                    {hasAnyDiscount ? (
                                      <div className="flex flex-col items-end">
                                        {!(ratePlan.secretDeal && !isLoggedIn) && (
                                        <>
                                        <div className="flex items-baseline justify-end gap-1.5 mb-1">
                                          <span className="text-xs line-through text-gray-400">
                                            {(() => {
                                              const price = finalPrice || 0;
                                              const converted = convertPrice(price);
                                              const formatted = formatPrice(converted, ratePlan);
                                              return formatted;
                                            })()}
                                    </span>
                                          {hasTax() && calculateTaxPerNight() > 0 ? (
                                          <span
                                            onClick={() => {
                                              const qty = roomQuantities[`${room.id}-${ratePlan.id}`] || 1;
                                              showTaxModalForRatePlan(room, ratePlan, qty);
                                            }}
                                              className="text-sm font-bold text-amber-700 hover:underline cursor-pointer"
                                          >
                                            {(() => {
                                              const price = displayPrice || 0;
                                              const converted = convertPrice(price);
                                              const formatted = formatPrice(converted, ratePlan);
                                              return formatted;
                                            })()}
                                    </span>
                                          ) : (
                                            <span className="text-sm font-bold text-amber-700">
                                              {(() => {
                                                const price = displayPrice || 0;
                                                const converted = convertPrice(price);
                                                const formatted = formatPrice(converted, ratePlan);
                                                return formatted;
                                              })()}
                                            </span>
                                          )}
                                        </div>
                                        <div className="inline-flex items-center text-[8px] bg-blue-500 text-white px-1 py-0.5 rounded font-bold mb-1">
                                          {(ratePlan.promotionDiscount?.discountType || '').toLowerCase().includes('percentage') || !ratePlan.promotionDiscount
                                            ? `${Math.round(totalDiscountPercent)}% OFF`
                                            : `${getDisplayCurrency(ratePlan)} ${(ratePlan.promotionDiscount?.discountValue || ratePlan.promotionDiscount?.DiscountValue || 0).toLocaleString()} OFF`}
                                        </div>
                                        {hasTax() && (() => {
                                          const taxPerNight = calculateTaxPerNight();
                                          return taxPerNight > 0 ? (
                                            <div className="mt-0.5">
                                              <p className="text-[10px] text-gray-600">
                                                + {getDisplayCurrency(ratePlan)}{taxPerNight.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                                              </p>
                                              <p className="text-[9px] text-gray-500 mt-0.5">{t('ratePlan.taxesAndCharges')}</p>
                                            </div>
                                          ) : (hasIncludedTax(ratePlan.taxes) ? (
                                            <p className="text-[9px] text-green-700 font-medium mt-0.5">Price includes all taxes and fees</p>
                                          ) : null);
                                        })()}
                                        </>
                                        )}
                                      </div>
                                    ) : ratePlan.secretDeal && !isLoggedIn ? (
                                      null
                                    ) : ratePlan.secretDeal && isLoggedIn ? (
                                      <div className="flex flex-col items-end">
                                        {hasTax() && calculateTaxPerNight() > 0 ? (
                                          <span 
                                            onClick={() => {
                                              const qty = roomQuantities[`${room.id}-${ratePlan.id}`] || 1;
                                              showTaxModalForRatePlan(room, ratePlan, qty);
                                            }}
                                            className="text-sm font-extrabold hover:underline cursor-pointer"
                                            style={{ color: 'var(--primary-color, #1e40af)' }}
                                          >
                                            {(() => {
                                              const converted = convertPrice(displayPrice);
                                              const formatted = formatPrice(converted, ratePlan);
                                              return formatted || 'N/A';
                                            })()}
                                          </span>
                                        ) : (
                                          <span className="text-sm font-extrabold" style={{ color: 'var(--primary-color, #1e40af)' }}>
                                            {(() => {
                                              const converted = convertPrice(displayPrice);
                                              const formatted = formatPrice(converted, ratePlan);
                                              return formatted || 'N/A';
                                            })()}
                                          </span>
                                        )}
                                        {hasTax() && (() => {
                                          const taxPerNight = calculateTaxPerNight();
                                          return taxPerNight > 0 ? (
                                            <div className="mt-0.5">
                                              <p className="text-[10px] text-gray-600">
                                                + {getDisplayCurrency(ratePlan)}{taxPerNight.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                                              </p>
                                              <p className="text-[9px] text-gray-500 mt-0.5">{t('ratePlan.taxesAndCharges')}</p>
                                            </div>
                                          ) : (hasIncludedTax(ratePlan.taxes) ? (
                                            <p className="text-[9px] text-green-700 font-medium mt-0.5">Price includes all taxes and fees</p>
                                          ) : null);
                                        })()}
                                        
                                      </div>
                                    ) : (
                                      <div className="flex flex-col items-end">
                                        {hasTax() && calculateTaxPerNight() > 0 ? (
                                        <span 
                                          onClick={() => {
                                            const qty = roomQuantities[`${room.id}-${ratePlan.id}`] || 1;
                                            showTaxModalForRatePlan(room, ratePlan, qty);
                                          }}
                                          className="text-sm font-extrabold text-slate-800 hover:underline cursor-pointer"
                                        >
                                          {(() => {
                                            const converted = convertPrice(displayPrice);
                                            const formatted = formatPrice(converted, ratePlan);
                                            return formatted || 'N/A';
                                          })()}
                                        </span>
                                        ) : (
                                          <span className="text-sm font-extrabold text-slate-800">
                                            {(() => {
                                              const converted = convertPrice(displayPrice);
                                              const formatted = formatPrice(converted, ratePlan);
                                              return formatted || 'N/A';
                                            })()}
                                          </span>
                                        )}
                                        {hasTax() && (() => {
                                          const taxPerNight = calculateTaxPerNight();
                                          return taxPerNight > 0 ? (
                                            <div className="mt-0.5">
                                              <p className="text-[10px] text-gray-600">
                                                + {getDisplayCurrency(ratePlan)}{taxPerNight.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                                              </p>
                                              <p className="text-[9px] text-gray-500 mt-0.5">{t('ratePlan.taxesAndCharges')}</p>
                                            </div>
                                          ) : (hasIncludedTax(ratePlan.taxes) ? (
                                            <p className="text-[9px] text-green-700 font-medium mt-0.5">Price includes all taxes and fees</p>
                                          ) : null);
                                        })()}
                                        
                                      </div>
                                    )}
                                  </div>
                                </div>

                                <div className="flex items-center justify-between mt-0">
                                {/* Left Side: Policy, Refund Status, and Included */}
                                <div className="flex-1 min-w-0 pr-2">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    {/* Refundable / Non-Refundable with Policy - Desktop style — only render when it has content, so it doesn't leave a leading gap */}
                                    {(hasAnyPolicyData(ratePlan) || ratePlan.refundStatus == 1 || ratePlan.refundStatus == 2) && (
                                    <span
                                      className={`flex items-center gap-1.5 ${hasAnyPolicyData(ratePlan) ? 'cursor-pointer group' : ''}`}
                                      onClick={() => hasAnyPolicyData(ratePlan) && showRatePlanPolicyModal(ratePlan)}
                                    >
                                      {hasAnyPolicyData(ratePlan) && (
                                        <span className="w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                                          <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                          </svg>
                                        </span>
                                      )}
                                      {(ratePlan.refundStatus == 1 || ratePlan.refundStatus == 2) && (
                                        ratePlan.refundStatus == 1 ? (
                                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-green-700 bg-green-50 border border-green-200 rounded-md px-2 py-0.5">
                                            <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                                            {t('ratePlan.freeCancellation')}
                                          </span>
                                        ) : (
                                          <span className="inline-flex items-center gap-1.5 text-[10px] rounded-md px-2 py-0.5 bg-gray-100 border border-gray-200">
                                            <span className="font-medium text-gray-600">{t('ratePlan.nonRefundable')}</span>
                                            <span className="text-gray-300">·</span>
                                            <span className="inline-flex items-center gap-0.5 font-semibold text-green-700">
                                              <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                                              {t('ratePlan.bestPrice')}
                                            </span>
                                          </span>
                                        )
                                      )}
                                      {hasAnyPolicyData(ratePlan) && (
                                        <svg className="w-2.5 h-2.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                      )}
                                    </span>
                                    )}

                                    {/* Breakfast Indicator — flag first, else infer from name; unknown → show nothing (don't contradict name) */}
                                    {(() => {
                                      const bf = getBreakfastStatus(ratePlan);
                                      if (bf === true) return (
                                        <span className="flex items-center gap-1">
                                          <i className="fas fa-check-circle text-green-600 text-[10px]"></i>
                                          <span className="text-[10px] font-medium text-green-600">{t('ratePlan.breakfastIncluded')}</span>
                                        </span>
                                      );
                                      if (bf === false) return (
                                        <span className="flex items-center gap-1">
                                          <i className="fas fa-times-circle text-gray-400 text-[10px]"></i>
                                          <span className="text-[10px] text-gray-400">{t('ratePlan.noBreakfastIncluded')}</span>
                                        </span>
                                      );
                                      return null;
                                    })()}

                                    {/* What's Included — Clickable, opens modal */}
                                    {ratePlan.included && (() => {
                                      const includedText = Array.isArray(ratePlan.included) ? ratePlan.included.join('\n') : String(ratePlan.included);
                                      const items = includedText.split(/[\n|]+/).map(s => s.trim().replace(/[|]+$/, '').trim()).filter(Boolean);
                                      if (items.length === 0) return null;
                                      return (
                                        <span
                                          className="flex items-center gap-1 cursor-pointer group"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedRatePlanForIncluded(ratePlan);
                                            setShowIncludedModal(true);
                                          }}
                                        >
                                          <svg className="w-3 h-3 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                          </svg>
                                          <span className="text-[10px] font-semibold text-green-700 group-hover:text-green-900">
                                            {t('ratePlan.whatsIncluded')}
                                          </span>
                                          <svg className="w-2.5 h-2.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                          </svg>
                                        </span>
                                      );
                                    })()}

                                  </div>
                              </div>

                                {/* Right Side: Quantity +/- Buttons or Unlock Button */}
                                <div className="flex flex-col items-end flex-shrink-0">
                                  {ratePlan.secretDeal && !isLoggedIn ? (
                                    /* Unlock Button for Secret Deal - Only show when user is not logged in */
                                    <>
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        trackMemberUnlock(accommodationId || '', { ratePlanName: ratePlan?.RatePlanName || ratePlan?.name || '', roomName: room?.RoomName || room?.name || '' });
                                        setShowSignInModal(true);
                                      }}
                                      className="px-4 py-2 flex items-center justify-center gap-2 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-sm hover:opacity-90"
                                      style={{ backgroundColor: 'var(--secondary-color, #3b82f6)' }}
                                    >
                                      {/* Lock Icon */}
                                      <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2.5}
                                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                        />
                                      </svg>
                                      <span className="tracking-wide">{t('ratePlan.unlock')}</span>
                                    </button>
                                    </>
                                  ) : (roomQuantities[`${room.id}-${ratePlan.id}`] || 0) === 0 ? (
                                    /* Select button — tap to add first room; stepper appears once selected */
                                    <button
                                      onClick={() => {
                                        navigator.vibrate?.(8); // subtle haptic tick (Android)
                                        // Same guard as increment: don't exceed available inventory
                                        const totalSelectedForRoom = room.ratePlans.reduce((sum, rp) => {
                                          return sum + (roomQuantities[`${room.id}-${rp.id}`] || 0);
                                        }, 0);
                                        const roomsAvailable = room.totalroom || 0;
                                        if (totalSelectedForRoom >= roomsAvailable) {
                                          showToast(`Only ${roomsAvailable} room${roomsAvailable > 1 ? 's' : ''} available for ${room.name}`, "warning");
                                          return;
                                        }
                                        setRoomQuantities((prev) => ({
                                          ...prev,
                                          [`${room.id}-${ratePlan.id}`]: 1,
                                        }));
                                        updatePricingForRoomQuantity(room.id, 1);
                                        selectRatePlan(room.id, ratePlan.id);
                                        calculateAndStoreTax(room, ratePlan, 1);
                                      }}
                                      disabled={(room.ratePlans.reduce((sum, rp) => sum + (roomQuantities[`${room.id}-${rp.id}`] || 0), 0) >= (room.totalroom || 0))}
                                      className="px-6 h-11 min-w-[88px] flex items-center justify-center rounded-lg avablity-btn active:scale-95 text-white text-sm font-semibold shadow-md transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                      {t('ratePlan.select')}
                                    </button>
                                  ) : (
                                  <div className="flex items-center rounded-lg border-2 overflow-hidden bg-white" style={{ borderColor: 'var(--bgButton)' }}>
                                    {/* Decrement Button */}
                                    <button
                                      onClick={() => {
                                        navigator.vibrate?.(8); // subtle haptic tick (Android)
                                        const currentQty = roomQuantities[`${room.id}-${ratePlan.id}`] || 0;
                                        const newQty = Math.max(0, currentQty - 1);
                                        setRoomQuantities((prev) => ({
                                          ...prev,
                                          [`${room.id}-${ratePlan.id}`]: newQty,
                                        }));
                                        updatePricingForRoomQuantity(room.id, newQty);
                                        if (newQty > 0) {
                                          selectRatePlan(room.id, ratePlan.id);
                                          // Calculate and store tax for summary
                                          calculateAndStoreTax(room, ratePlan, newQty);
                                        } else {
                                          deselectRatePlan(room.id, ratePlan.id);
                                          // Remove stored tax data when quantity is 0
                                          const taxKey = `${room.id}-${ratePlan.id}`;
                                          setStoredTaxData(prev => {
                                            const updated = { ...prev };
                                            delete updated[taxKey];
                                            return updated;
                                          });
                                        }
                                      }}
                                      disabled={(roomQuantities[`${room.id}-${ratePlan.id}`] || 0) === 0}
                                      className="w-10 h-10 flex items-center justify-center text-xl font-bold bg-white hover:bg-gray-50 active:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors active:scale-95"
                                      style={{ color: 'var(--bgButton)' }}
                                    >
                                      &minus;
                                    </button>

                                    {/* Quantity Display */}
                                    <span className="h-10 px-1 text-sm font-bold text-gray-900 min-w-[32px] text-center bg-white flex items-center justify-center">
                                      {roomQuantities[`${room.id}-${ratePlan.id}`] || 0}
                                    </span>

                                    {/* Increment Button */}
                                    <button
                                      onClick={() => {
                                        navigator.vibrate?.(8); // subtle haptic tick (Android)
                                        const currentQty = roomQuantities[`${room.id}-${ratePlan.id}`] || 0;

                                        // Calculate total quantity selected for this room across all rate plans
                                        const totalSelectedForRoom = room.ratePlans.reduce((sum, rp) => {
                                          return sum + (roomQuantities[`${room.id}-${rp.id}`] || 0);
                                        }, 0);

                                        // Get available rooms for this room type
                                        const roomsAvailable = room.totalroom || 0;

                                        // Check if we can add more rooms
                                        if (totalSelectedForRoom >= roomsAvailable) {
                                          showToast(`Only ${roomsAvailable} room${roomsAvailable > 1 ? 's' : ''} available for ${room.name}`, "warning");
                                          return;
                                        }

                                        const newQty = currentQty + 1;
                                        setRoomQuantities((prev) => ({
                                          ...prev,
                                          [`${room.id}-${ratePlan.id}`]: newQty,
                                        }));
                                        updatePricingForRoomQuantity(room.id, newQty);
                                        selectRatePlan(room.id, ratePlan.id);
                                        // Calculate and store tax for summary
                                        calculateAndStoreTax(room, ratePlan, newQty);
                                      }}
                                      disabled={(room.ratePlans.reduce((sum, rp) => sum + (roomQuantities[`${room.id}-${rp.id}`] || 0), 0) >= (room.totalroom || 0))}
                                      className="w-10 h-10 flex items-center justify-center text-xl font-bold bg-white hover:bg-gray-50 active:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors active:scale-95"
                                      style={{ color: 'var(--bgButton)' }}
                                    >
                                      +
                                    </button>
                              </div>
                                  )}
                                  </div>
                                </div>
                              </div>
                            );
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className="mb-4 text-center py-8">
                          <div className="bg-gradient-to-b from-blue-50/60 to-gray-50 rounded-xl p-6 border border-gray-100">
                            <div className="w-14 h-14 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm ring-4 ring-blue-50">
                              <i className="fas fa-calendar text-blue-500 text-xl"></i>
                            </div>
                            <h5 className="text-sm font-semibold text-gray-900 mb-1.5">
                              Select Dates & Guests
                            </h5>
                            <p className="text-xs text-gray-600 leading-relaxed max-w-[240px] mx-auto">
                              Choose your check-in, check-out dates and number
                              of adults to view available rate plans
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <>
                <div className="space-y-6">
                  {addOns.map((addOn) => {
                    const currentQty = addOnQuantities[addOn.id] || 0;
                    // If selected, apply blue border and bg
                    const selectedClass =
                      currentQty > 0
                        ? "border-2  bg-green-50"
                        : "border border-gray-100 bg-white";

                    return (
                      <div
                        key={addOn.id}
                        className={`rounded-2xl mb-4 shadow-lg overflow-hidden ${selectedClass} transition-all duration-200`}
                      >
                        {/* Add-on Image */}
                        <div className="relative w-full h-40 md:h-56">
                          <img
                            src={addOn.image}
                            alt={addOn.name}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute top-3 right-3 bg-black bg-opacity-70 text-white px-2 py-1 rounded flex items-center space-x-1">
                            <svg
                              className="h-4 w-4 fill-yellow-400 text-yellow-400"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                            <span className="text-xs">{addOn.category}</span>
                          </div>
                        </div>
                        {/* Add-on Content (below image, like room card) */}
                        <div className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1 min-w-0 pr-3">
                              <h3 className="text-lg font-semibold leading-tight" style={{ color: 'var(--primary-color, #1e40af)' }}>
                              {addOn.name}
                            </h3>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <div className="text-lg font-bold text-gray-900">
                                {(() => {
                                  const code = addOn?.currencyCode || '';
                                  const symbol = code ? getCurrencySymbol(code) : getDisplayCurrency({});
                                  const sym = symbol.length > 1 ? symbol + ' ' : symbol;
                                  const price = typeof addOn.price === 'number' ? addOn.price : parseFloat(addOn.price) || 0;
                                  return `${sym}${price.toLocaleString()}`;
                                })()}
                            </div>
                          </div>
                          </div>
                          {addOn.description && (
                            <div className="mb-3">
                              <p className={`text-sm text-gray-600 ${!expandedAddOnDescs[addOn.id] ? 'line-clamp-2 lg:line-clamp-none' : ''}`}>
                                {addOn.description}
                              </p>
                              {addOn.description.length > 80 && (
                                <button
                                  className="text-xs font-medium mt-1 lg:hidden"
                                  style={{ color: 'var(--primary-color, #1e40af)' }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedAddOnDescs(prev => ({ ...prev, [addOn.id]: !prev[addOn.id] }));
                                  }}
                                >
                                  {expandedAddOnDescs[addOn.id] ? t('room.readLess') : t('room.readMore')}
                                </button>
                              )}
                            </div>
                          )}
                          <div className="flex items-center space-x-4 text-sm text-gray-600 mb-2">
                            <div className="flex items-center space-x-1">
                              <svg
                                className="h-4 w-4"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              <span>{addOn.duration}</span>
                            </div>
                            {(addOn.guaranteePolicy || addOn.cancellationPolicy) && (
                              <button
                                onClick={() => {
                                  setSelectedAddOnPolicy(addOn);
                                  setShowAddOnPolicyModal(true);
                                }}
                                className="flex items-center space-x-1 text-blue-600 hover:text-blue-700 transition-colors"
                              >
                                <i className="fas fa-shield-alt text-blue-500"></i>
                                <span className="font-medium">Policy</span>
                              </button>
                            )}
                          </div>
                          {/* Quantity Controls with +/- buttons and Comment */}
                          <div className="mt-4">
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-sm font-medium text-gray-700">
                                Select Quantity
                              </span>
                              <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
                                <button
                                  onClick={() => {
                                    const newQty = Math.max(0, currentQty - 1);
                                    setAddOnQuantities((prev) => ({
                                      ...prev,
                                      [addOn.id]: newQty,
                                    }));
                                  }}
                                  disabled={currentQty === 0}
                                  className={`w-8 h-8 rounded-md flex items-center justify-center transition-all duration-200 ${
                                    currentQty === 0
                                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                      : 'bg-white text-gray-700 hover:bg-gray-200 hover:text-gray-900 shadow-sm'
                                  }`}
                                >
                                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                                  </svg>
                                </button>
                                <span className="w-12 text-center font-bold text-gray-900 text-lg">{currentQty}</span>
                                <button
                                  onClick={() => {
                                    const maxQty = addOn.maxQty || 5;
                                    if (currentQty < maxQty) {
                                      setAddOnQuantities((prev) => ({
                                        ...prev,
                                        [addOn.id]: currentQty + 1,
                                      }));
                                    } else {
                                      showToast(`Maximum quantity is ${maxQty} for this add-on.`, "info");
                                    }
                                  }}
                                  disabled={currentQty >= (addOn.maxQty || 5)}
                                  className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors duration-200 shadow-sm ${
                                    currentQty >= (addOn.maxQty || 5)
                                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                      : 'avablity-btn text-white hover:bg-blue-700 hover:shadow-md'
                                  }`}
                                >
                                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                            
                            {/* Comment Section */}
                      <div className="mt-3 pt-3 border-t border-gray-200">
                              <div className="flex items-center space-x-2 mb-2">
                                <input
                                  type="checkbox"
                                  id={`comment-${addOn.id}`}
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  checked={!!addOnComments[addOn.id]}
                                  onChange={() => {
                                    if (addOnComments[addOn.id]) {
                                      // Remove comment if checkbox unchecked
                                      setAddOnComments((prev) => {
                                        const updated = { ...prev };
                                        delete updated[addOn.id];
                                        return updated;
                                      });
                                    } else {
                                      // Open comment input
                                      setSelectedAddOnForComment(addOn);
                                      setShowCommentModal(true);
                                    }
                                  }}
                                />
                                <label htmlFor={`comment-${addOn.id}`} className="text-sm text-gray-600 cursor-pointer">
                                  Add Comment
                                </label>
                        </div>
                              {addOnComments[addOn.id] && (
                                <div className="mt-2 text-xs text-blue-600 bg-blue-50 p-2 rounded border border-blue-200">
                                  <strong>Comment:</strong> {addOnComments[addOn.id]}
                      </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Load More Indicator */}
                  {hasSearched && allApiRooms.length > displayedRoomsCount && (
                    <div className="text-center py-6">
                      {isLoadingMore ? (
                        <div className="flex flex-col items-center gap-2">
                          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                          <p className="text-sm text-gray-600">Loading more rooms...</p>
                        </div>
                      ) : (
                        <button
                          onClick={loadMoreRooms}
                          className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm"
                        >
                          Load More ({allApiRooms.length - displayedRoomsCount} remaining)
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </section>

          {/* Sticky Bottom Booking Bar - Show when any room or add-on has selection */}
          {(Object.values(roomQuantities).some((qty) => qty > 0) ||
            Object.values(addOnQuantities).some((qty) => qty > 0)) && (
            <>
              {/* Expandable Summary Details */}
              {showSummaryDetails && (() => {
                // Group rooms by room ID to show multiple rate plans for same room
                const groupedRooms = {};
                Object.keys(roomQuantities).forEach((key) => {
                  const qty = roomQuantities[key] || 0;
                  if (qty > 0) {
                    const [roomId, ratePlanId] = key.split('-');
                    const room = displayRooms.find(r => r.id === parseInt(roomId));
                    const ratePlan = room?.ratePlans?.find(rp => rp.id === ratePlanId);
                    if (room && ratePlan) {
                      if (!groupedRooms[roomId]) {
                        groupedRooms[roomId] = {
                          room: room,
                          ratePlans: []
                        };
                      }
                      groupedRooms[roomId].ratePlans.push({
                        ratePlan: ratePlan,
                        quantity: qty
                      });
                    }
                  }
                });

                const nights = Math.ceil(
                  (selectedDates.checkOut - selectedDates.checkIn) / (1000 * 60 * 60 * 24)
                ) || 1;

                return (
                  <div className="fixed bottom-16 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-40 max-h-[50vh] overflow-y-auto safe-bottom">
                    <div className="max-w-md mx-auto px-4 py-3">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-semibold text-gray-900">Booking Summary</h4>
                        <button
                          onClick={() => setShowSummaryDetails(false)}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                      <div className="space-y-3">
                        {/* Grouped Rooms with Multiple Rate Plans */}
                        {Object.values(groupedRooms).map((groupedItem, groupIndex) => {
                          // Get loyalty discount if user is logged in
                          const loyaltyDiscount = isLoggedIn ? getLoyaltyDiscount() : null;
                          const loyaltyData = loyaltyDiscount && loyaltyDiscount.data ? loyaltyDiscount.data : null;
                          const loyaltyDiscountValue = loyaltyData && loyaltyData.Value !== undefined && loyaltyData.Value !== null
                            ? parseFloat(loyaltyData.Value) 
                            : 0;
                          const loyaltyMaxValue = loyaltyData && loyaltyData.MaxValue !== undefined && loyaltyData.MaxValue !== null 
                            ? parseFloat(loyaltyData.MaxValue) 
                            : 0;
                          const loyaltyType = loyaltyData && loyaltyData.Type ? loyaltyData.Type : 'percent';
                          
                          const totalRoomPrice = groupedItem.ratePlans.reduce((sum, rp) => {
                            const ratePlan = rp.ratePlan;
                            if (!ratePlan) return sum;
                            
                            // Calculate discounted price (same logic as rate plan card)
                            // Check ConvertedRate first
                            const convertedRate = ratePlan.convertedRate != null ? parseFloat(ratePlan.convertedRate) : null;
                            const useConvertedRate = convertedRate != null && !isNaN(convertedRate) && convertedRate > 0;
                            
                            // Get original rate price
                            const ratePlanPrice = ratePlan.price != null ? parseFloat(ratePlan.price) : null;
                            const roomBasePrice = groupedItem.room?.basePrice != null ? parseFloat(groupedItem.room.basePrice) : null;
                            
                            // Use convertedRate if available and > 0, otherwise use original rate price
                            let originalPrice = 0;
                            if (useConvertedRate) {
                              originalPrice = convertedRate;
                            } else if (ratePlanPrice != null && !isNaN(ratePlanPrice) && ratePlanPrice > 0) {
                              originalPrice = ratePlanPrice;
                            } else if (roomBasePrice != null && !isNaN(roomBasePrice) && roomBasePrice > 0) {
                              originalPrice = roomBasePrice;
                            }
                            
                            // Calculate promotion discount percentage
                            // Promotion only applies if both name and discount value are present
                            const hasPromotion = ratePlan.promotionDiscount && 
                              ratePlan.promotionDiscount.discount > 0 && 
                              (ratePlan.promotionDiscount.promotionName || ratePlan.promotionName);
                            let promotionDiscountPercent = 0;
                            if (hasPromotion) {
                              if (ratePlan.promotionDiscount.discountType === 'Percentage') {
                                const discountVal = parseFloat(ratePlan.promotionDiscount.discountValue || ratePlan.promotionDiscount.DiscountValue || 0);
                                promotionDiscountPercent = Math.max(0, Math.min(100, discountVal));
                              } else {
                                promotionDiscountPercent = parseFloat(ratePlan.promotionDiscount.DiscountValue || ratePlan.promotionDiscount.discount || 0);
                              }
                            }
                            
                            // Calculate total discount: promotion discount + loyalty discount
                            let totalDiscountPercent = promotionDiscountPercent;
                            if (isLoggedIn && loyaltyDiscountValue > 0 && loyaltyType === 'percent') {
                              const validLoyaltyDiscount = Math.max(0, Math.min(100, loyaltyDiscountValue));
                              totalDiscountPercent = promotionDiscountPercent + validLoyaltyDiscount;
                              
                              // Apply MaxValue limit
                              if (loyaltyMaxValue > 0 && totalDiscountPercent > loyaltyMaxValue) {
                                totalDiscountPercent = loyaltyMaxValue;
                              }
                              
                              // Final safety check
                              if (totalDiscountPercent > 100) {
                                totalDiscountPercent = 100;
                              }
                            }
                            totalDiscountPercent = Math.max(0, Math.min(100, totalDiscountPercent));
                            
                            // Calculate final price with all discounts
                            let displayPrice = originalPrice;
                            if (hasPromotion) {
                              if (isLoggedIn && loyaltyDiscountValue > 0 && loyaltyType === 'percent') {
                                // User is logged in with loyalty discount - apply total discount
                                if (useConvertedRate && convertedRate > 0) {
                                  displayPrice = convertedRate * (1 - (totalDiscountPercent / 100));
                                } else {
                                  displayPrice = originalPrice * (1 - (totalDiscountPercent / 100));
                                }
                              } else {
                                // User is NOT logged in - skip afterDiscountAmount if convertedRate used
                                if (ratePlan.promotionDiscount?.afterDiscountAmount && !useConvertedRate) {
                                  const discountPrice = parseFloat(ratePlan.promotionDiscount.afterDiscountAmount);
                                  if (!isNaN(discountPrice) && discountPrice > 0) {
                                    displayPrice = discountPrice;
                                  }
                                } else if (useConvertedRate && convertedRate > 0) {
                                  displayPrice = convertedRate * (1 - (promotionDiscountPercent / 100));
                                } else {
                                  displayPrice = originalPrice * (1 - (promotionDiscountPercent / 100));
                                }
                              }
                            } else if (isLoggedIn && loyaltyDiscountValue > 0 && loyaltyType === 'percent') {
                              // No promotion but user is logged in with loyalty discount
                              if (useConvertedRate && convertedRate > 0) {
                                displayPrice = convertedRate * (1 - (loyaltyDiscountValue / 100));
                              } else {
                                displayPrice = originalPrice * (1 - (loyaltyDiscountValue / 100));
                              }
                            }
                            
                            // Apply secret deal discount if available (after loyalty discount)
                            if (ratePlan.secretDeal) {
                              displayPrice = displayPrice * (1 - (ratePlan.memberDiscount || 0));
                            }
                            
                            return sum + (displayPrice * rp.quantity);
                          }, 0);
                          const totalQuantity = groupedItem.ratePlans.reduce((sum, rp) => sum + rp.quantity, 0);

                          return (
                            <div key={groupIndex} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                              <div className="flex justify-between items-start mb-2">
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-semibold text-gray-900">
                                    {totalQuantity} {groupedItem.room?.name ? `× ${groupedItem.room.name}` : (totalQuantity > 1 ? 'rooms' : 'room')}
                                  </div>
                                </div>
                                <span className="text-sm font-bold text-gray-900 ml-2">
                                  {getDisplayCurrency(groupedItem.ratePlans?.[0]?.ratePlan || {})}{(totalRoomPrice || 0).toLocaleString()}
                                </span>
                              </div>
                              {/* Display all rate plans for this room */}
                              {groupedItem.ratePlans.length > 0 && (
                                <div className="mt-2 space-y-1 pl-2 border-l-2 border-gray-300">
                                  {groupedItem.ratePlans.map((rpItem, rpIndex) => {
                                    const ratePlan = rpItem.ratePlan;
                                    if (!ratePlan) return null;
                                    
                                    // Calculate discounted price (same logic as above)
                                    const convertedRate = ratePlan.convertedRate != null ? parseFloat(ratePlan.convertedRate) : null;
                                    const useConvertedRate = convertedRate != null && !isNaN(convertedRate) && convertedRate > 0;
                                    
                                    const ratePlanPrice = ratePlan.price != null ? parseFloat(ratePlan.price) : null;
                                    const roomBasePrice = groupedItem.room?.basePrice != null ? parseFloat(groupedItem.room.basePrice) : null;
                                    
                                    let originalPrice = 0;
                                    if (useConvertedRate) {
                                      originalPrice = convertedRate;
                                    } else if (ratePlanPrice != null && !isNaN(ratePlanPrice) && ratePlanPrice > 0) {
                                      originalPrice = ratePlanPrice;
                                    } else if (roomBasePrice != null && !isNaN(roomBasePrice) && roomBasePrice > 0) {
                                      originalPrice = roomBasePrice;
                                    }
                                    
                                    // Promotion only applies if both name and discount value are present
                                    const hasPromotion = ratePlan.promotionDiscount && 
                                      ratePlan.promotionDiscount.discount > 0 && 
                                      (ratePlan.promotionDiscount.promotionName || ratePlan.promotionName);
                                    let promotionDiscountPercent = 0;
                                    if (hasPromotion) {
                                      if ((ratePlan.promotionDiscount.discountType || '').toLowerCase().includes('percentage')) {
                                        const discountVal = parseFloat(ratePlan.promotionDiscount.discountValue || ratePlan.promotionDiscount.DiscountValue || 0);
                                        promotionDiscountPercent = Math.max(0, Math.min(100, discountVal));
                                      } else {
                                        promotionDiscountPercent = parseFloat(ratePlan.promotionDiscount.discountValue || ratePlan.promotionDiscount.DiscountValue || 0);
                                      }
                                    }
                                    
                                    let totalDiscountPercent = promotionDiscountPercent;
                                    if (isLoggedIn && loyaltyDiscountValue > 0 && loyaltyType === 'percent') {
                                      const validLoyaltyDiscount = Math.max(0, Math.min(100, loyaltyDiscountValue));
                                      totalDiscountPercent = promotionDiscountPercent + validLoyaltyDiscount;
                                      
                                      if (loyaltyMaxValue > 0 && totalDiscountPercent > loyaltyMaxValue) {
                                        totalDiscountPercent = loyaltyMaxValue;
                                      }
                                      
                                      if (totalDiscountPercent > 100) {
                                        totalDiscountPercent = 100;
                                      }
                                    }
                                    totalDiscountPercent = Math.max(0, Math.min(100, totalDiscountPercent));
                                    
                                    let displayPrice = originalPrice;
                                    if (hasPromotion) {
                                      if (isLoggedIn && loyaltyDiscountValue > 0 && loyaltyType === 'percent') {
                                        if (useConvertedRate && convertedRate > 0) {
                                          displayPrice = convertedRate * (1 - (totalDiscountPercent / 100));
                                        } else {
                                          displayPrice = originalPrice * (1 - (totalDiscountPercent / 100));
                                        }
                                      } else {
                                        if (ratePlan.promotionDiscount?.afterDiscountAmount && !useConvertedRate) {
                                          const discountPrice = parseFloat(ratePlan.promotionDiscount.afterDiscountAmount);
                                          if (!isNaN(discountPrice) && discountPrice > 0) {
                                            displayPrice = discountPrice;
                                          }
                                        } else if (useConvertedRate && convertedRate > 0) {
                                          displayPrice = convertedRate * (1 - (promotionDiscountPercent / 100));
                                        } else {
                                          displayPrice = originalPrice * (1 - (promotionDiscountPercent / 100));
                                        }
                                      }
                                    } else if (isLoggedIn && loyaltyDiscountValue > 0 && loyaltyType === 'percent') {
                                      if (useConvertedRate && convertedRate > 0) {
                                        displayPrice = convertedRate * (1 - (loyaltyDiscountValue / 100));
                                      } else {
                                        displayPrice = originalPrice * (1 - (loyaltyDiscountValue / 100));
                                      }
                                    }
                                    
                                    if (ratePlan.secretDeal) {
                                      displayPrice = displayPrice * (1 - (ratePlan.memberDiscount || 0));
                                    }
                                    
                                    const finalRatePlanPrice = displayPrice * rpItem.quantity;
                                    return (
                                      <div key={rpIndex} className="flex justify-between items-center text-xs">
                                        <div className="text-gray-600">
                                          {rpItem.quantity} × {ratePlan?.name || 'Rate Plan'}
                                        </div>
                                        <span className="text-gray-700 font-medium">
                                          {getDisplayCurrency(ratePlan)}{(finalRatePlanPrice || 0).toLocaleString()}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}

                        {/* Add-ons */}
                        {Object.entries(addOnQuantities).some(([_, qty]) => qty > 0) && (
                          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                            <div className="text-sm font-semibold text-gray-900 mb-2">Add-ons</div>
                            <div className="space-y-1">
                              {Object.entries(addOnQuantities).map(([addOnId, quantity]) => {
                                if (quantity === 0) return null;
                                const addOn = addOns.find(a => a.id === parseInt(addOnId));
                                if (!addOn) return null;
                                return (
                                  <div key={addOnId} className="flex justify-between items-center text-xs">
                                    <div className="text-gray-600">
                                      {quantity} × {addOn.name}
                                    </div>
                                    <span className="text-gray-700 font-medium">
                                      {getDisplayCurrency({})} {(() => {
                                        const addonNights = Math.max(1, Math.ceil((selectedDates.checkOut - selectedDates.checkIn) / (1000 * 60 * 60 * 24)));
                                        const nightsMul = String(addOn?.activityId) === '4' ? addonNights : 1;
                                        return (quantity * (addOn.price || 0) * nightsMul).toLocaleString();
                                      })()}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Price Breakdown */}
                        <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 space-y-2">
                          {(() => {
                            const nights = Math.ceil(
                              (selectedDates.checkOut - selectedDates.checkIn) / (1000 * 60 * 60 * 24)
                            ) || 1;
                            
                            // Get loyalty discount if user is logged in
                            const loyaltyDiscount = isLoggedIn ? getLoyaltyDiscount() : null;
                            const loyaltyData = loyaltyDiscount && loyaltyDiscount.data ? loyaltyDiscount.data : null;
                            const loyaltyDiscountValue = loyaltyData && loyaltyData.Value !== undefined && loyaltyData.Value !== null
                              ? parseFloat(loyaltyData.Value) 
                              : 0;
                            const loyaltyMaxValue = loyaltyData && loyaltyData.MaxValue !== undefined && loyaltyData.MaxValue !== null 
                              ? parseFloat(loyaltyData.MaxValue) 
                              : 0;
                            const loyaltyType = loyaltyData && loyaltyData.Type ? loyaltyData.Type : 'percent';
                            
                            // Calculate room subtotal with loyalty discount
                            const roomSubtotal = Object.keys(roomQuantities).reduce((total, key) => {
                              const qty = roomQuantities[key] || 0;
                              if (qty > 0) {
                                const [roomId, ratePlanId] = key.split("-");
                                const room = displayRooms.find(r => r.id === parseInt(roomId));
                                const ratePlan = room?.ratePlans?.find(rp => rp.id === ratePlanId);
                                if (room && ratePlan) {
                                  // Check ConvertedRate first - if it's 0 or null, use original rate price
                                  const convertedRate = ratePlan.convertedRate != null ? parseFloat(ratePlan.convertedRate) : null;
                                  const useConvertedRate = convertedRate != null && !isNaN(convertedRate) && convertedRate > 0;
                                  
                                  // Get original rate price
                                  const ratePlanPrice = ratePlan.price != null ? parseFloat(ratePlan.price) : null;
                                  const roomBasePrice = room.basePrice != null ? parseFloat(room.basePrice) : null;
                                  
                                  // Use convertedRate if available and > 0, otherwise use original rate price
                                  let originalPrice = 0;
                                  if (useConvertedRate) {
                                    originalPrice = convertedRate;
                                  } else if (ratePlanPrice != null && !isNaN(ratePlanPrice) && ratePlanPrice >= 0) {
                                    originalPrice = ratePlanPrice;
                                  } else if (roomBasePrice != null && !isNaN(roomBasePrice) && roomBasePrice >= 0) {
                                    originalPrice = roomBasePrice;
                                  }
                                  
                                  // Calculate promotion discount percentage
                                  // Promotion only applies if both name and discount value are present
                                  const hasPromotion = ratePlan.promotionDiscount && 
                                    ratePlan.promotionDiscount.discount > 0 && 
                                    (ratePlan.promotionDiscount.promotionName || ratePlan.promotionName);
                                  let promotionDiscountPercent = 0;
                                  if (hasPromotion) {
                                    if ((ratePlan.promotionDiscount.discountType || '').toLowerCase().includes('percentage')) {
                                      const discountVal = parseFloat(ratePlan.promotionDiscount.discountValue || ratePlan.promotionDiscount.DiscountValue || 0);
                                      promotionDiscountPercent = Math.max(0, Math.min(100, discountVal));
                                    } else {
                                      promotionDiscountPercent = parseFloat(ratePlan.promotionDiscount.discountValue || ratePlan.promotionDiscount.DiscountValue || 0);
                                    }
                                  }
                                  
                                  // Calculate total discount: promotion discount + loyalty discount
                                  let totalDiscountPercent = promotionDiscountPercent;
                                  if (isLoggedIn && loyaltyDiscountValue > 0 && loyaltyType === 'percent') {
                                    const validLoyaltyDiscount = Math.max(0, Math.min(100, loyaltyDiscountValue));
                                    totalDiscountPercent = promotionDiscountPercent + validLoyaltyDiscount;
                                    
                                    // Apply MaxValue limit
                                    if (loyaltyMaxValue > 0 && totalDiscountPercent > loyaltyMaxValue) {
                                      totalDiscountPercent = loyaltyMaxValue;
                                    }
                                    
                                    // Final safety check
                                    if (totalDiscountPercent > 100) {
                                      totalDiscountPercent = 100;
                                    }
                                  }
                                  totalDiscountPercent = Math.max(0, Math.min(100, totalDiscountPercent));
                                  
                                  // Calculate final price with all discounts
                                  let displayPrice = originalPrice;
                                  if (hasPromotion) {
                                    if (isLoggedIn && loyaltyDiscountValue > 0 && loyaltyType === 'percent') {
                                      // User is logged in with loyalty discount - apply total discount
                                      if (useConvertedRate && convertedRate > 0) {
                                        displayPrice = convertedRate * (1 - (totalDiscountPercent / 100));
                                      } else {
                                        displayPrice = originalPrice * (1 - (totalDiscountPercent / 100));
                                      }
                                    } else {
                                      // User is NOT logged in - afterDiscountAmount only when NOT converting (INC-048b)
                                      if (ratePlan.promotionDiscount?.afterDiscountAmount && !useConvertedRate) {
                                        const discountPrice = parseFloat(ratePlan.promotionDiscount.afterDiscountAmount);
                                        if (!isNaN(discountPrice) && discountPrice > 0) {
                                          displayPrice = discountPrice;
                                        }
                                      } else if (useConvertedRate && convertedRate > 0) {
                                        displayPrice = convertedRate * (1 - (promotionDiscountPercent / 100));
                                      } else {
                                        displayPrice = originalPrice * (1 - (promotionDiscountPercent / 100));
                                      }
                                    }
                                  } else if (isLoggedIn && loyaltyDiscountValue > 0 && loyaltyType === 'percent') {
                                    // No promotion but user is logged in with loyalty discount
                                    if (useConvertedRate && convertedRate > 0) {
                                      displayPrice = convertedRate * (1 - (loyaltyDiscountValue / 100));
                                    } else {
                                      displayPrice = originalPrice * (1 - (loyaltyDiscountValue / 100));
                                    }
                                  }
                                  
                                  // Apply secret deal discount if available (after loyalty discount)
                                  if (ratePlan.secretDeal) {
                                    displayPrice = displayPrice * (1 - (ratePlan.memberDiscount || 0));
                                  }

                                  return total + (displayPrice * qty);
                                }
                              }
                              return total;
                            }, 0);
                            
                            // Calculate taxes using stored tax data (from tax API) - same as tax modal
                            const taxes = Object.keys(roomQuantities).reduce((total, key) => {
                              const qty = roomQuantities[key] || 0;
                              if (qty > 0) {
                                const [roomId, ratePlanId] = key.split("-");
                                const room = displayRooms.find(r => r.id === parseInt(roomId));
                                const ratePlan = room?.ratePlans?.find(rp => rp.id === ratePlanId);
                                if (room && ratePlan) {
                                  // First try to use stored tax data (from tax API call)
                                  const taxKey = `${roomId}-${ratePlanId}`;
                                  const storedTax = storedTaxData[taxKey];
                                  
                                  if (storedTax) {
                                    // Use stored tax data (same as tax modal)
                                    const taxArray = storedTax.taxesBreakDown || storedTax.taxes || [];
                                    let roomTax = 0;
                                    
                                    if (Array.isArray(taxArray) && taxArray.length > 0) {
                                      roomTax = taxArray.reduce((sum, tax) => {
                                        if (isTaxIncluded(tax)) return sum; // Included tax = already in rate, don't add
                                        const taxId = tax.TaxId || tax.taxId || tax.TaxID || 0;
                                        let taxAmount = 0;

                                        if (taxId === 19) {
                                          const taxValue = parseFloat(tax.TaxValue || tax.taxValue || 0);
                                          taxAmount = taxValue;
                                        } else {
                                          taxAmount = parseFloat(tax.TaxAmmount || tax.TaxAmount || tax.taxAmount || tax.amount || 0);
                                        }

                                        return sum + (isNaN(taxAmount) ? 0 : taxAmount);
                                      }, 0);
                                    } else {
                                      roomTax = parseFloat(storedTax.totalTax || storedTax.TotalTax || storedTax.totalTaxAmount || 0);
                                    }
                                    
                                    return total + roomTax;
                                  } else if (ratePlan.taxes && Array.isArray(ratePlan.taxes)) {
                                    // Fallback: use ratePlan.taxes if stored tax data not available
                                    const roomTax = ratePlan.taxes.reduce((taxSum, tax) => {
                                      if (isTaxIncluded(tax)) return taxSum; // Included tax = already in rate, don't add
                                      const taxId = tax.TaxId || tax.taxId || tax.TaxID || 0;
                                      let taxAmt = 0;
                                      
                                      if (taxId === 19) {
                                        const taxValue = parseFloat(tax.TaxValue || tax.taxValue || 0);
                                        taxAmt = taxValue;
                                      } else {
                                        taxAmt = parseFloat(tax.TaxAmmount || tax.TaxAmount || tax.taxAmount || tax.amount || 0);
                                      }
                                      
                                      return taxSum + (isNaN(taxAmt) ? 0 : taxAmt);
                                    }, 0);
                                    return total + roomTax;
                                  }
                                }
                              }
                              return total;
                            }, 0);
                            
                            // Calculate add-ons subtotal
                            const mobileAddonNights = Math.max(1, Math.ceil((selectedDates.checkOut - selectedDates.checkIn) / (1000 * 60 * 60 * 24)));
                            const addOnsSubtotal = Object.entries(addOnQuantities).reduce(
                              (total, [addOnId, quantity]) => {
                                if (quantity === 0) return total;
                                const addOn = addOns.find(a => a.id === parseInt(addOnId));
                                const nightsMul = String(addOn?.activityId) === '4' ? mobileAddonNights : 1;
                                return total + (quantity * (addOn?.price || 0) * nightsMul);
                              },
                              0,
                            );
                            
                            // Get currency from first selected rate plan
                            const firstSelectedRoom = Object.entries(roomQuantities).find(([_, qty]) => qty > 0);
                            const firstRoomData = firstSelectedRoom ? apiRooms.find(r => r.id === parseInt(firstSelectedRoom[0])) : null;
                            const firstRatePlan = firstRoomData?.ratePlans?.[0] || {};
                            const currencySymbol = getDisplayCurrency(firstRatePlan);
                            
                            return (
                              <>
                                {roomSubtotal > 0 && (
                                  <div className="flex justify-between text-xs">
                                    <span className="text-gray-600">Rooms:</span>
                                    <span className="font-medium text-gray-900">
                                      {currencySymbol}{(roomSubtotal || 0).toLocaleString()}
                                    </span>
                      </div>
                                )}
                                {addOnsSubtotal > 0 && (
                                  <div className="flex justify-between text-xs">
                                    <span className="text-gray-600">Add-ons:</span>
                                    <span className="font-medium text-gray-900">
                                      {currencySymbol}{(addOnsSubtotal || 0).toLocaleString()}
                                    </span>
                    </div>
                                )}
                                {(taxes || 0) > 0 && (
                                <div className="flex justify-between text-xs">
                                  <span className="text-gray-600">Taxes & Charges:</span>
                                  <span className="font-medium text-gray-900">
                                    {currencySymbol}{(taxes || 0).toLocaleString()}
                                  </span>
                                </div>
                                )}
                                <hr className="border-gray-300" />
                                <div className="flex justify-between text-sm font-bold">
                                  <span className="text-gray-900">Total:</span>
                                  <span className="text-gray-900">
                                    {currencySymbol}{((roomSubtotal || 0) + (taxes || 0) + (addOnsSubtotal || 0)).toLocaleString()}
                                  </span>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Bottom Bar */}
            <div className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-md border-t border-gray-200/80 shadow-[0_-4px_24px_rgba(0,0,0,0.10)] z-50 safe-bottom">
              <div className="max-w-md mx-auto px-3 py-3">
                <div className="flex items-center justify-between gap-2">
                  {/* Left Section */}
                    <div className="flex items-center space-x-2 flex-1 min-w-0" onClick={() => setShowSummaryDetails(!showSummaryDetails)}>
                    <div className="card-head rounded-full p-2 shadow flex-shrink-0">
                      <i className="fas fa-bed text-white text-sm"></i>
                    </div>
                      <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-gray-900">
                        {Object.values(roomQuantities).reduce(
                          (sum, qty) => sum + (qty || 0),
                          0,
                        )}{" "}
                        Room
                        {Object.values(roomQuantities).reduce(
                          (sum, qty) => sum + (qty || 0),
                          0,
                        ) > 1
                          ? "s"
                          : ""}
                        {Object.values(addOnQuantities).some(
                          (qty) => qty > 0,
                        ) && (
                          <>
                            {" "}
                            •{" "}
                            {Object.values(addOnQuantities).reduce(
                              (sum, qty) => sum + (qty || 0),
                              0,
                            )}{" "}
                            Add-on
                            {Object.values(addOnQuantities).reduce(
                              (sum, qty) => sum + (qty || 0),
                              0,
                            ) > 1
                              ? "s"
                              : ""}
                          </>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        {Math.ceil(
                          (selectedDates.checkOut - selectedDates.checkIn) /
                            (1000 * 60 * 60 * 24),
                        )}{" "}
                        night
                        {Math.ceil(
                          (selectedDates.checkOut - selectedDates.checkIn) /
                            (1000 * 60 * 60 * 24),
                        ) > 1
                          ? "s"
                          : ""}{" "}
                        •{Object.keys(selectedRatePlans).length} plan
                        {Object.keys(selectedRatePlans).length > 1 ? "s" : ""}
                          <span className="ml-1 text-primary-600">
                            {showSummaryDetails ? '▼' : '▲'} Details
                          </span>
                      </div>
                    </div>
                  </div>

                  {/* Right Section */}
                  <div className="flex items-center space-x-2 flex-shrink-0">
                    <div className="text-right">
                      <div className="text-sm font-bold text-gray-900 whitespace-nowrap">
                        {(() => {
                          // Get first selected rate plan for currency
                          const firstSelected = Object.entries(roomQuantities).find(([_, q]) => q > 0);
                          const firstRoom = firstSelected ? apiRooms.find(r => r.id === parseInt(firstSelected[0])) : null;
                          const firstRatePlan = firstRoom?.ratePlans?.[0] || null;
                          return formatPrice(
                          convertPrice(
                              (() => {
                                const nights = Math.ceil(
                                  (selectedDates.checkOut - selectedDates.checkIn) / (1000 * 60 * 60 * 24)
                                ) || 1;
                                
                                // Get loyalty discount if user is logged in
                                const loyaltyDiscount = isLoggedIn ? getLoyaltyDiscount() : null;
                                const loyaltyData = loyaltyDiscount && loyaltyDiscount.data ? loyaltyDiscount.data : null;
                                const loyaltyDiscountValue = loyaltyData && loyaltyData.Value !== undefined && loyaltyData.Value !== null
                                  ? parseFloat(loyaltyData.Value) 
                                  : 0;
                                const loyaltyMaxValue = loyaltyData && loyaltyData.MaxValue !== undefined && loyaltyData.MaxValue !== null 
                                  ? parseFloat(loyaltyData.MaxValue) 
                                  : 0;
                                const loyaltyType = loyaltyData && loyaltyData.Type ? loyaltyData.Type : 'percent';
                                
                                // Calculate room subtotal with loyalty discount
                                const roomSubtotal = Object.keys(roomQuantities).reduce((total, key) => {
                              const qty = roomQuantities[key] || 0;
                              if (qty > 0) {
                                const [roomId, ratePlanId] = key.split("-");
                                    const room = displayRooms.find(r => r.id === parseInt(roomId));
                                    const ratePlan = room?.ratePlans?.find(rp => rp.id === ratePlanId);
                                    if (room && ratePlan) {
                                      // Check ConvertedRate first - if it's 0 or null, use original rate price
                                      const convertedRate = ratePlan.convertedRate != null ? parseFloat(ratePlan.convertedRate) : null;
                                      const useConvertedRate = convertedRate != null && !isNaN(convertedRate) && convertedRate > 0;
                                      
                                      // Get original rate price
                                      const ratePlanPrice = ratePlan.price != null ? parseFloat(ratePlan.price) : null;
                                      const roomBasePrice = room.basePrice != null ? parseFloat(room.basePrice) : null;
                                      
                                      // Use convertedRate if available and > 0, otherwise use original rate price
                                      let originalPrice = 0;
                                      if (useConvertedRate) {
                                        originalPrice = convertedRate;
                                      } else if (ratePlanPrice != null && !isNaN(ratePlanPrice) && ratePlanPrice >= 0) {
                                        originalPrice = ratePlanPrice;
                                      } else if (roomBasePrice != null && !isNaN(roomBasePrice) && roomBasePrice >= 0) {
                                        originalPrice = roomBasePrice;
                                      }
                                      
                                      // Calculate promotion discount percentage
                                      // Promotion only applies if both name and discount value are present
                                      const hasPromotion = ratePlan.promotionDiscount && 
                                        ratePlan.promotionDiscount.discount > 0 && 
                                        (ratePlan.promotionDiscount.promotionName || ratePlan.promotionName);
                                      let promotionDiscountPercent = 0;
                                      if (hasPromotion) {
                                        if ((ratePlan.promotionDiscount.discountType || '').toLowerCase().includes('percentage')) {
                                          const discountVal = parseFloat(ratePlan.promotionDiscount.discountValue || ratePlan.promotionDiscount.DiscountValue || 0);
                                          promotionDiscountPercent = Math.max(0, Math.min(100, discountVal));
                                        } else {
                                          promotionDiscountPercent = parseFloat(ratePlan.promotionDiscount.discountValue || ratePlan.promotionDiscount.DiscountValue || 0);
                                        }
                                      }
                                      
                                      // Calculate total discount: promotion discount + loyalty discount
                                      let totalDiscountPercent = promotionDiscountPercent;
                                      if (isLoggedIn && loyaltyDiscountValue > 0 && loyaltyType === 'percent') {
                                        const validLoyaltyDiscount = Math.max(0, Math.min(100, loyaltyDiscountValue));
                                        totalDiscountPercent = promotionDiscountPercent + validLoyaltyDiscount;
                                        
                                        // Apply MaxValue limit
                                        if (loyaltyMaxValue > 0 && totalDiscountPercent > loyaltyMaxValue) {
                                          totalDiscountPercent = loyaltyMaxValue;
                                        }
                                        
                                        // Final safety check
                                        if (totalDiscountPercent > 100) {
                                          totalDiscountPercent = 100;
                                        }
                                      }
                                      totalDiscountPercent = Math.max(0, Math.min(100, totalDiscountPercent));
                                      
                                      // Calculate final price with all discounts
                                      let displayPrice = originalPrice;
                                      if (hasPromotion) {
                                        if (isLoggedIn && loyaltyDiscountValue > 0 && loyaltyType === 'percent') {
                                          // User is logged in with loyalty discount - apply total discount
                                          if (useConvertedRate && convertedRate > 0) {
                                            displayPrice = convertedRate * (1 - (totalDiscountPercent / 100));
                                          } else {
                                            displayPrice = originalPrice * (1 - (totalDiscountPercent / 100));
                                          }
                                        } else {
                                          // User is NOT logged in - afterDiscountAmount only when NOT converting (INC-048b)
                                          if (ratePlan.promotionDiscount?.afterDiscountAmount && !useConvertedRate) {
                                        const discountPrice = parseFloat(ratePlan.promotionDiscount.afterDiscountAmount);
                                        if (!isNaN(discountPrice) && discountPrice > 0) {
                                              displayPrice = discountPrice;
                                            }
                                          } else if (useConvertedRate && convertedRate > 0) {
                                            displayPrice = convertedRate * (1 - (promotionDiscountPercent / 100));
                                          } else {
                                            displayPrice = originalPrice * (1 - (promotionDiscountPercent / 100));
                                          }
                                        }
                                      } else if (isLoggedIn && loyaltyDiscountValue > 0 && loyaltyType === 'percent') {
                                        // No promotion but user is logged in with loyalty discount
                                        if (useConvertedRate && convertedRate > 0) {
                                          displayPrice = convertedRate * (1 - (loyaltyDiscountValue / 100));
                                        } else {
                                          displayPrice = originalPrice * (1 - (loyaltyDiscountValue / 100));
                                        }
                                      }
                                      
                                      // Apply secret deal discount if available (after loyalty discount)
                                      if (ratePlan.secretDeal) {
                                        displayPrice = displayPrice * (1 - (ratePlan.memberDiscount || 0));
                                      }

                                      return total + (displayPrice * qty);
                                    }
                              }
                              return total;
                                }, 0);
                                
                                // Calculate taxes using stored tax data (from tax API) - same as tax modal
                                const taxes = Object.keys(roomQuantities).reduce((total, key) => {
                                  const qty = roomQuantities[key] || 0;
                                  if (qty > 0) {
                                    const [roomId, ratePlanId] = key.split("-");
                                    const room = displayRooms.find(r => r.id === parseInt(roomId));
                                    const ratePlan = room?.ratePlans?.find(rp => rp.id === ratePlanId);
                                    if (room && ratePlan) {
                                      // First try to use stored tax data (from tax API call)
                                      const taxKey = `${roomId}-${ratePlanId}`;
                                      const storedTax = storedTaxData[taxKey];
                                      
                                      if (storedTax) {
                                        // Use stored tax data (same as tax modal)
                                        const taxArray = storedTax.taxesBreakDown || storedTax.taxes || [];
                                        let roomTax = 0;
                                        
                                        if (Array.isArray(taxArray) && taxArray.length > 0) {
                                          roomTax = taxArray.reduce((sum, tax) => {
                                            if (isTaxIncluded(tax)) return sum; // Included tax = already in rate, don't add
                                            const taxId = tax.TaxId || tax.taxId || tax.TaxID || 0;
                                            let taxAmount = 0;

                                            if (taxId === 19) {
                                              const taxValue = parseFloat(tax.TaxValue || tax.taxValue || 0);
                                              taxAmount = taxValue;
                                            } else {
                                              taxAmount = parseFloat(tax.TaxAmmount || tax.TaxAmount || tax.taxAmount || tax.amount || 0);
                                            }

                                            return sum + (isNaN(taxAmount) ? 0 : taxAmount);
                                          }, 0);
                                        } else {
                                          roomTax = parseFloat(storedTax.totalTax || storedTax.TotalTax || storedTax.totalTaxAmount || 0);
                                        }
                                        
                                        return total + roomTax;
                                      } else if (ratePlan.taxes && Array.isArray(ratePlan.taxes)) {
                                        // Fallback: use ratePlan.taxes if stored tax data not available
                                        const roomTax = ratePlan.taxes.reduce((taxSum, tax) => {
                                          if (isTaxIncluded(tax)) return taxSum; // Included tax = already in rate, don't add
                                          const taxId = tax.TaxId || tax.taxId || tax.TaxID || 0;
                                          let taxAmt = 0;
                                          
                                          if (taxId === 19) {
                                            const taxValue = parseFloat(tax.TaxValue || tax.taxValue || 0);
                                            taxAmt = taxValue;
                                          } else {
                                            taxAmt = parseFloat(tax.TaxAmmount || tax.TaxAmount || tax.taxAmount || tax.amount || 0);
                                          }
                                          
                                          return taxSum + (isNaN(taxAmt) ? 0 : taxAmt);
                                        }, 0);
                                        return total + roomTax;
                                      }
                                    }
                                  }
                                  return total;
                                }, 0);
                                
                                // Calculate add-ons subtotal
                                const addOnsSubtotal = Object.entries(addOnQuantities).reduce(
                                (total, [addOnId, quantity]) => {
                                  if (quantity === 0) return total;
                                    const addOn = addOns.find(a => a.id === parseInt(addOnId));
                                    return total + (quantity * (addOn?.price || 0));
                                  },
                                  0,
                                );
                                
                                return roomSubtotal + taxes + addOnsSubtotal;
                              })(),
                          ),
                          firstRatePlan
                        );
                        })()}
                      </div>
                      <div className="text-xs text-gray-500">Total</div>
                    </div>

                    <button
                        onClick={async () => {
                          // If on rooms tab and add-ons are available, switch to add-ons tab first
                          if (activeTab === "rooms" && Array.isArray(addOns) && addOns.length > 0) {
                            setActiveTab("addons");
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                            return;
                          }

                          // Calculate nights
                          const nights = Math.ceil(
                            (selectedDates.checkOut - selectedDates.checkIn) /
                              (1000 * 60 * 60 * 24)
                          ) || 1;

                          // Build selectedRooms array with real data and calculate taxes
                          const selectedRooms = [];
                          for (const key of Object.keys(roomQuantities)) {
                              const qty = roomQuantities[key] || 0;
                              if (qty > 0) {
                                const [roomId, ratePlanId] = key.split('-');
                              const room = displayRooms.find(r => r.id === parseInt(roomId));
                                const ratePlan = room?.ratePlans?.find(rp => rp.id === ratePlanId);
                              if (room && ratePlan) {
                                // Calculate room price first (needed for both tax calculation and storing in selectedRooms)
                                // Get loyalty discount if user is logged in
                                const loyaltyDiscount = isLoggedIn ? getLoyaltyDiscount() : null;
                                const loyaltyData = loyaltyDiscount && loyaltyDiscount.data ? loyaltyDiscount.data : null;
                                const loyaltyDiscountValue = loyaltyData && loyaltyData.Value !== undefined && loyaltyData.Value !== null
                                  ? parseFloat(loyaltyData.Value) 
                                  : 0;
                                const loyaltyMaxValue = loyaltyData && loyaltyData.MaxValue !== undefined && loyaltyData.MaxValue !== null 
                                  ? parseFloat(loyaltyData.MaxValue) 
                                  : 0;
                                const loyaltyType = loyaltyData && loyaltyData.Type ? loyaltyData.Type : 'percent';
                                
      // Check ConvertedRate first - if it's 0 or null, use original rate price
      const convertedRate = ratePlan.convertedRate != null ? parseFloat(ratePlan.convertedRate) : null;
      const useConvertedRate = convertedRate != null && !isNaN(convertedRate) && convertedRate > 0;
      
      // Get original rate price
      const ratePlanPrice = ratePlan.price != null ? parseFloat(ratePlan.price) : null;
      const roomBasePrice = room.basePrice != null ? parseFloat(room.basePrice) : null;
      
      // Use convertedRate if available and > 0, otherwise use original rate price
                                let originalPrice = 0;
      if (useConvertedRate) {
                                  originalPrice = convertedRate;
      } else if (ratePlanPrice != null && !isNaN(ratePlanPrice) && ratePlanPrice >= 0) {
                                  originalPrice = ratePlanPrice;
      } else if (roomBasePrice != null && !isNaN(roomBasePrice) && roomBasePrice >= 0) {
                                  originalPrice = roomBasePrice;
                                }
                                
                                // Calculate promotion discount percentage
                                // Promotion only applies if both name and discount value are present
                                const hasPromotion = ratePlan.promotionDiscount && 
                                  ratePlan.promotionDiscount.discount > 0 && 
                                  (ratePlan.promotionDiscount.promotionName || ratePlan.promotionName);
                                let promotionDiscountPercent = 0;
                                if (hasPromotion) {
                                  if ((ratePlan.promotionDiscount.discountType || '').toLowerCase().includes('percentage')) {
                                    const discountVal = parseFloat(ratePlan.promotionDiscount.discountValue || ratePlan.promotionDiscount.DiscountValue || 0);
                                    promotionDiscountPercent = Math.max(0, Math.min(100, discountVal));
                                  } else {
                                    promotionDiscountPercent = parseFloat(ratePlan.promotionDiscount.discountValue || ratePlan.promotionDiscount.DiscountValue || 0);
                                  }
                                }
                                
                                // Calculate total discount: promotion discount + loyalty discount
                                let totalDiscountPercent = promotionDiscountPercent;
                                if (isLoggedIn && loyaltyDiscountValue > 0 && loyaltyType === 'percent') {
                                  const validLoyaltyDiscount = Math.max(0, Math.min(100, loyaltyDiscountValue));
                                  totalDiscountPercent = promotionDiscountPercent + validLoyaltyDiscount;
                                  
                                  // Apply MaxValue limit
                                  if (loyaltyMaxValue > 0 && totalDiscountPercent > loyaltyMaxValue) {
                                    totalDiscountPercent = loyaltyMaxValue;
                                  }
                                  
                                  // Final safety check
                                  if (totalDiscountPercent > 100) {
                                    totalDiscountPercent = 100;
                                  }
                                }
                                totalDiscountPercent = Math.max(0, Math.min(100, totalDiscountPercent));
                                
                                // Calculate final price with all discounts
                                let roomPrice = originalPrice;
                                if (hasPromotion) {
                                  if (isLoggedIn && loyaltyDiscountValue > 0 && loyaltyType === 'percent') {
                                    // User is logged in with loyalty discount - apply total discount
                                    if (useConvertedRate && convertedRate > 0) {
                                      roomPrice = convertedRate * (1 - (totalDiscountPercent / 100));
                                    } else {
                                      roomPrice = originalPrice * (1 - (totalDiscountPercent / 100));
                                    }
                                  } else {
                                    // User is NOT logged in
                                    if (useConvertedRate && convertedRate > 0) {
                                      // INC-048: afterDiscountAmount is in the BASE currency. When a
                                      // conversion is applied, apply the promo % to the CONVERTED rate
                                      // so checkout matches the card (card uses convertedRate*(1-disc%)).
                                      roomPrice = convertedRate * (1 - (promotionDiscountPercent / 100));
                                    } else if (ratePlan.promotionDiscount?.afterDiscountAmount) {
                                      const discountPrice = parseFloat(ratePlan.promotionDiscount.afterDiscountAmount);
                                      if (!isNaN(discountPrice) && discountPrice > 0) {
                                        roomPrice = discountPrice;
                                      }
                                    } else {
                                      roomPrice = originalPrice * (1 - (promotionDiscountPercent / 100));
                                    }
                                  }
                                } else if (isLoggedIn && loyaltyDiscountValue > 0 && loyaltyType === 'percent') {
                                  // No promotion but user is logged in with loyalty discount
                                  if (useConvertedRate && convertedRate > 0) {
                                    roomPrice = convertedRate * (1 - (loyaltyDiscountValue / 100));
                                  } else {
                                    roomPrice = originalPrice * (1 - (loyaltyDiscountValue / 100));
                                  }
                                }
                                
                                // Apply secret deal discount if available (after loyalty discount)
      if (ratePlan.secretDeal) {
        roomPrice = roomPrice * (1 - (ratePlan.memberDiscount || 0));
      }
                                    
                                // Calculate tax for this room selection
                                let calculatedTax = 0;
                                try {
                                  // Extract tax data from rate plan
                                  const taxArray = extractTaxDataFromRatePlan(ratePlan);
                                  
                                  if (taxArray && taxArray.length > 0) {
                                    const totalPrice = roomPrice * qty * nights;
                                    const noofroomArray = { "0": qty };
                                    const totalBedrooms = (ratePlan.noOfBedRooms || 1);
                                    
                                    // Call tax API
                                    const taxResult = await getTax({
                                      nights,
                                      conversionrate: ratePlan.conversionRate || 0,
                                      noofroomArray,
                                      totalnumberofbedrooms: totalBedrooms,
                                      price: totalPrice,
                                      data: taxArray
                                    });
                                    
                                    // Extract total tax from response with TaxId = 19 logic
                                    const taxes = taxResult.taxesBreakDown || taxResult.taxes || [];
                                    if (Array.isArray(taxes) && taxes.length > 0) {
                                      calculatedTax = taxes.reduce((sum, tax) => {
                                        if (isTaxIncluded(tax)) return sum; // Included tax = already in rate, don't add
                                        const taxId = tax.TaxId || tax.taxId || tax.TaxID || 0;
                                        // For TaxId = 19, use TaxValue as-is (no quantity multiplication)
                                        // For other taxes, use TaxAmmount (already calculated by API with quantity and nights)
                                        let taxAmt = 0;
                                        if (taxId === 19) {
                                          const taxValue = parseFloat(tax.TaxValue || tax.taxValue || 0);
                                          taxAmt = taxValue;
                                        } else {
                                          taxAmt = parseFloat(tax.TaxAmmount || tax.TaxAmount || tax.taxAmount || tax.amount || 0);
                                        }
                                        return sum + (isNaN(taxAmt) ? 0 : taxAmt);
                                      }, 0);
                                    } else {
                                      calculatedTax = parseFloat(taxResult.totalTax || taxResult.TotalTax || taxResult.totalTaxAmount || 0);
                                    }
                                  }
                                } catch (error) {
                                  // Fallback: calculate from ratePlan.taxes if available
                                  if (ratePlan.taxes && Array.isArray(ratePlan.taxes)) {
                                    calculatedTax = ratePlan.taxes.reduce((sum, tax) => {
                                      if (isTaxIncluded(tax)) return sum; // Included tax = already in rate, don't add
                                      const taxId = tax.TaxId || tax.taxId || tax.TaxID || 0;
                                      let taxAmt = 0;
                                      if (taxId === 19) {
                                        taxAmt = parseFloat(tax.TaxValue || tax.taxValue || 0);
                                      } else {
                                        taxAmt = parseFloat(tax.TaxAmmount || tax.TaxAmount || tax.taxAmount || 0);
                                      }
                                      return sum + (isNaN(taxAmt) ? 0 : taxAmt);
                                    }, 0);
                                  }
                                }
                                
                                selectedRooms.push({
                                  room: room,
                                  ratePlan: ratePlan,
                                  quantity: qty,
                                  taxAmount: calculatedTax,
                                  calculatedPrice: roomPrice // Store calculated price with loyalty discount
                                });
                              }
                            }
                          }

                          // Calculate subtotals
                          const roomSubtotal = selectedRooms.reduce((total, item) => {
                            // Use calculatedPrice if available (includes loyalty discount), otherwise calculate
                            let price = item.calculatedPrice;
                            if (!price) {
                              // Fallback: calculate price if calculatedPrice not stored
                            const ratePlanPrice = item.ratePlan.price != null ? parseFloat(item.ratePlan.price) : null;
                            const roomBasePrice = item.room.basePrice != null ? parseFloat(item.room.basePrice) : null;
                              price = (ratePlanPrice != null && !isNaN(ratePlanPrice) && ratePlanPrice > 0) 
                              ? ratePlanPrice 
                              : ((roomBasePrice != null && !isNaN(roomBasePrice) && roomBasePrice > 0) ? roomBasePrice : 0);
                            
                            // Apply promotion discount if available
                            if (item.ratePlan.promotionDiscount && item.ratePlan.promotionDiscount.discount > 0 && item.ratePlan.promotionDiscount.afterDiscountAmount) {
                              const discountPrice = parseFloat(item.ratePlan.promotionDiscount.afterDiscountAmount);
                              if (!isNaN(discountPrice) && discountPrice > 0) {
                                price = discountPrice;
                              }
                            }
                            
                            // Apply secret deal discount if available
                            if (item.ratePlan.secretDeal) {
                              price = price * (1 - (item.ratePlan.memberDiscount || 0));
                              }
                            }

                            return total + (price * item.quantity);
                          }, 0);

                          const addOnsSubtotal = Object.entries(addOnQuantities).reduce((total, [addOnId, quantity]) => {
                              if (quantity === 0) return total;
                              const addOn = addOns.find(a => a.id === parseInt(addOnId));
                              return total + (quantity * (addOn?.price || 0));
                          }, 0);

                          // Calculate taxes - use stored taxAmount from tax API (calculated with TaxId = 19 logic)
                          // This is the most accurate as it's calculated when room is selected
                          let taxes = 0;
                          taxes = selectedRooms.reduce((sum, item) => {
                            // First priority: use taxAmount stored from tax API
                            if (item.taxAmount && typeof item.taxAmount === 'number' && !isNaN(item.taxAmount) && item.taxAmount > 0) {
                              return sum + item.taxAmount;
                            } else if (item.ratePlan.taxes && Array.isArray(item.ratePlan.taxes)) {
                              // Fallback: calculate from ratePlan.taxes (from tax API response)
                              const roomTax = item.ratePlan.taxes.reduce((taxSum, tax) => {
                                if (isTaxIncluded(tax)) return taxSum; // Included tax = already in rate, don't add
                                const taxId = tax.TaxId || tax.taxId || tax.TaxID || 0;
                                let taxAmt = 0;
                                // For TaxId = 19, use TaxValue as-is (no quantity multiplication)
                                // For other taxes, use TaxAmmount (API already calculated with quantity and nights)
                                if (taxId === 19) {
                                  taxAmt = parseFloat(tax.TaxValue || tax.taxValue || 0);
                                } else {
                                  taxAmt = parseFloat(tax.TaxAmmount || tax.TaxAmount || tax.taxAmount || 0);
                                }
                                return taxSum + (isNaN(taxAmt) ? 0 : taxAmt);
                              }, 0);
                              return sum + roomTax;
                            } else {
                              // Last fallback: calculate from room.taxes (old structure)
                              let roomTax = 0;
                              if (item.room.taxes) {
                                if (typeof item.room.taxes === 'number') {
                                  roomTax = item.room.taxes;
                                } else if (typeof item.room.taxes === 'object') {
                                  const taxObj = item.room.taxes;
                                  if (taxObj.Tax && Array.isArray(taxObj.Tax)) {
                                    roomTax = taxObj.Tax.reduce((taxSum, tax) => {
                                      if (isTaxIncluded(tax)) return taxSum; // Included tax = already in rate, don't add
                                      const taxId = tax.TaxId || tax.taxId || tax.TaxID || 0;
                                      let taxAmt = 0;
                                      if (taxId === 19) {
                                        taxAmt = parseFloat(tax.TaxValue || tax.taxValue || 0);
                                      } else {
                                        taxAmt = parseFloat(tax.TaxAmmount || tax.TaxAmount || tax.taxAmount || 0);
                                      }
                                      return taxSum + (isNaN(taxAmt) ? 0 : taxAmt);
                                    }, 0);
                                  } else if (taxObj.Tax && typeof taxObj.Tax === 'object') {
                                    const tax = taxObj.Tax;
                                    const taxId = tax.TaxId || tax.taxId || tax.TaxID || 0;
                                    if (isTaxIncluded(tax)) {
                                      roomTax = 0; // Included tax = already in rate, don't add
                                    } else if (taxId === 19) {
                                      roomTax = parseFloat(tax.TaxValue || tax.taxValue || 0);
                                    } else {
                                      roomTax = parseFloat(tax.TaxAmmount || tax.TaxAmount || tax.taxAmount || 0);
                                    }
                                  }
                                }
                              }
                              return sum + roomTax;
                            }
                          }, 0);
                          
                          const totalAmount = roomSubtotal + addOnsSubtotal + taxes;

                          // Prepare URL parameters for checkout page (YYYY-MM-DD for unambiguous URLs)
                          const checkinURL = formatDate(selectedDates.checkIn);
                          const checkoutURL = formatDate(selectedDates.checkOut);
                          const accId = accommodationId || urlParams.hotel_id || urlParams.accommodationid || '';

                          const checkoutParams = new URLSearchParams();
                          checkoutParams.set('hotel_id', accId);
                          checkoutParams.set('checkin', checkinURL);
                          checkoutParams.set('checkout', checkoutURL);
                          checkoutParams.set('adult', adults.toString());
                          checkoutParams.set('child', children.toString());
                          
                          // Add child ages as comma-separated string
                          if (childAges.length > 0) {
                            checkoutParams.set('childAges', childAges.join(','));
                          }
                          
                          // Preserve optional parameters if they exist
                          const promoForCheckout = (promoCode || '').trim() || urlParams.promo;
                          if (promoForCheckout) {
                            checkoutParams.set('promo', promoForCheckout);
                          }
                          if (urlParams.profileid) {
                            checkoutParams.set('profileid', urlParams.profileid);
                          }
                          if (urlParams.multilanguageid) {
                            checkoutParams.set('multilanguageid', urlParams.multilanguageid.toString());
                          }
                          if (urlParams.currency || currentCurrency?.code) {
                            checkoutParams.set('currency', urlParams.currency || currentCurrency?.code);
                          }

                          // Preserve tracking parameters (utm_campaign, utm_medium, utm_source, etc.)
                          const utmCampaign = searchParams.get('utm_campaign');
                          const utmMedium = searchParams.get('utm_medium');
                          const utmSource = searchParams.get('utm_source');
                          const gaParam = searchParams.get('_ga');

                          if (utmCampaign) checkoutParams.set('utm_campaign', utmCampaign);
                          if (utmMedium) checkoutParams.set('utm_medium', utmMedium);
                          if (utmSource) checkoutParams.set('utm_source', utmSource);
                          if (gaParam) checkoutParams.set('_ga', gaParam);

                          // Add session tracking params
                          const sessionId = getSessionId();
                          const wcid = searchParams.get('wcid') || '';
                          const domain = searchParams.get('domain') || '';
                          const lang = searchParams.get('lang') || urlParams.lang || 'en';

                          checkoutParams.set('sessionId', sessionId);
                          if (wcid) checkoutParams.set('wcid', wcid);
                          if (domain) checkoutParams.set('domain', domain);

                          // Build selected add-ons array with id, variationId, qty, and comment
                          const selectedAddOns = [];
                          Object.entries(addOnQuantities).forEach(([addOnId, quantity]) => {
                            if (quantity > 0) {
                              const addOn = addOns.find(a => a.id === parseInt(addOnId));
                              if (addOn) {
                                selectedAddOns.push({
                                  id: addOn.id,
                                  activityId: addOn.activityId || addOn.id,
                                  variationId: addOn.variationId || addOn.id,
                                  qty: quantity,
                                  comment: addOnComments[addOn.id] || '',
                                  name: addOn.name,
                                  price: addOn.price
                                });
                              }
                            }
                          });

                          // Send booking tracking API (step 1 = checkout page)
                          sendBookingTracking({
                            hotel_id: accId,
                            checkin: checkinURL,
                            checkout: checkoutURL,
                            domain: domain || window.location.origin,
                            wcid: wcid,
                            step: 1,
                            lang: lang,
                            pageData: {
                              rooms: selectedRooms.map(item => ({
                                roomId: item.room?.RoomID || item.room?.id,
                                roomName: item.room?.RoomName || item.room?.name,
                                ratePlanId: item.ratePlan?.RatePlanID || item.ratePlan?.id,
                                ratePlanName: item.ratePlan?.RatePlanName || item.ratePlan?.name,
                                quantity: item.quantity,
                                price: item.ratePlan?.price || item.calculatedPrice
                              })),
                              adults: adults,
                              children: children,
                              totalAmount: totalAmount
                            }
                          });

                          // Analytics: track room selected
                          trackRoomSelected(accId, {
                            checkin: checkinURL,
                            checkout: checkoutURL,
                            adults,
                            children,
                            rooms: selectedRooms.map(item => ({
                              roomId: item.room?.RoomID || item.room?.id,
                              roomName: item.room?.RoomName || item.room?.name,
                              ratePlanId: item.ratePlan?.RatePlanID || item.ratePlan?.id,
                              ratePlanName: item.ratePlan?.RatePlanName || item.ratePlan?.name,
                              quantity: item.quantity,
                              price: item.ratePlan?.price || item.calculatedPrice,
                              currency: currentCurrency?.code || item.ratePlan?.currency || item.ratePlan?.CurrencyCode || ''
                            })),
                            totalAmount,
                            currency: currentCurrency?.code || selectedRooms[0]?.ratePlan?.currency || ''
                          });

                          showToast("Proceeding to booking...", "success");
                          navigate(`/checkout-page?${checkoutParams.toString()}`, {
                            state: {
                              hotelName: accommodationDetail?.accommodationName,
                              hotelImage: accommodationDetail?.hotelImages?.[0],
                              hotelAddress: accommodationDetail?.Address,
                              checkIn: selectedDates.checkIn,
                              checkOut: selectedDates.checkOut,
                              adults,
                              children,
                              rooms: Object.values(roomQuantities).reduce((sum, qty) => sum + (qty || 0), 0),
                              selectedRoom: displayRooms[0] || null,
                              selectedRatePlan: 'Flexible Rate',
                              nights: nights,
                              roomSubtotal: roomSubtotal,
                              addOnsSubtotal: addOnsSubtotal,
                              taxes: taxes,
                              totalAmount: totalAmount,
                              selectedRooms: selectedRooms,
                              selectedAddOns: selectedAddOns,
                              currency: currentCurrency?.code || selectedRooms[0]?.ratePlan?.currency || selectedRooms[0]?.ratePlan?.CurrencyCode || accommodationDetail?.CurrencyCode || accommodationDetail?.currencyCode || ''
                          }
                        });
                      }}
                      className="card-head text-white font-semibold py-2.5 px-4 rounded-lg text-sm min-h-[44px] whitespace-nowrap transition-all shadow active:scale-95"
                    >
                      Book Now
                    </button>
                  </div>
                </div>
              </div>
            </div>
            </>
          )}

          {/* Special Offers */}
          {/* <section className="px-4 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Special Offers
            </h3>
            <div className="card-head rounded-2xl p-4 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-lg mb-1">
                    Weekend Getaway
                  </h4>
                  <p className="text-secondary-100 text-sm mb-2">
                    Book 2 nights, get 1 night free
                  </p>
                  <button className="bg-white text-secondary-600 px-4 py-2 rounded-lg font-medium text-sm">
                    Claim Offer
                  </button>
                </div>
                <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <i className="fas fa-gift text-2xl"></i>
                </div>
              </div>
            </div>
          </section> */}

          {/* Contact Information */}
          {/* <section className="px-4 mb-20">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Contact Information
            </h3>
            <div className="bg-white rounded-2xl shadow-lg p-4 border border-gray-100">
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                    <i className="fas fa-phone text-primary-600 text-sm"></i>
                  </div>
                  <span className="text-gray-700">+91 123 456 7890</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                    <i className="fas fa-envelope text-primary-600 text-sm"></i>
                  </div>
                  <span className="text-gray-700">
                    info@mgmbeachresorts.com
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center">
                    <i className="fas fa-map-marker-alt text-primary-600 text-sm"></i>
                  </div>
                  <span className="text-gray-700">
                    East Coast Road, Chennai
                  </span>
                </div>
              </div>
            </div>
          </section> */}
          <div className="mt-auto">
            <Footer accommodationName={accommodationDetail?.accommodationName} />
          </div>
        </main>

        {/* Room Details Modal */}
        <BottomSheet
          open={!!(showRoomDetails && selectedRoomForDetails)}
          onClose={closeModal}
          title={selectedRoomForDetails ? selectedRoomForDetails.name : ''}
        >
          {selectedRoomForDetails && (
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="relative">
                      <p className={`text-sm text-gray-600 mb-3 ${!expandedDescription ? 'line-clamp-3' : ''} overflow-hidden`}>
                      {selectedRoomForDetails.description}
                    </p>
                      {selectedRoomForDetails.description && selectedRoomForDetails.description.length > 150 && (
                        <button
                          onClick={() => setExpandedDescription(!expandedDescription)}
                          className="text-primary-600 hover:text-primary-700 text-xs font-semibold mt-2 transition-colors underline"
                        >
                          {expandedDescription ? 'Show Less' : 'Load More'}
                        </button>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-sm">
                      {/* No-fallback policy: each chip renders only when real data exists (no 'N/A', no fake "2 guests") */}
                      {(() => {
                        const sizeVal = (selectedRoomForDetails.RoomSize && !isNaN(parseFloat(selectedRoomForDetails.RoomSize)))
                          ? parseFloat(selectedRoomForDetails.RoomSize)
                          : (selectedRoomForDetails.size && !isNaN(parseFloat(selectedRoomForDetails.size)))
                            ? parseFloat(selectedRoomForDetails.size)
                            : null;
                        return sizeVal ? (
                          <div className="flex items-center gap-1.5">
                            <i className="fas fa-expand-arrows-alt text-gray-500 text-xs"></i>
                            <span className="text-gray-700 font-medium">{sizeVal} sq. ft.</span>
                          </div>
                        ) : null;
                      })()}
                      {(selectedRoomForDetails.RoomMaxPerson || selectedRoomForDetails.guests) ? (
                        <div className="flex items-center gap-1.5">
                          <i className="fas fa-user-friends text-gray-500 text-xs"></i>
                          <span className="text-gray-700 font-medium">
                            {t('room.upToGuests', { n: selectedRoomForDetails.RoomMaxPerson || selectedRoomForDetails.guests })}
                          </span>
                        </div>
                      ) : null}
                      {(selectedRoomForDetails.BedSize || selectedRoomForDetails.Bed) ? (
                        <div className="flex items-center gap-1.5">
                          <i className="fas fa-bed text-gray-500 text-xs"></i>
                          <span className="text-gray-700 font-medium">
                            {selectedRoomForDetails.BedSize || selectedRoomForDetails.Bed}
                          </span>
                        </div>
                      ) : null}
                      {(() => {
                        const priceStr = selectedRoomForDetails.basePrice
                          ? `${getDisplayCurrency(selectedRoomForDetails.ratePlans?.[0] || {})}${selectedRoomForDetails.basePrice.toLocaleString()}`
                          : (selectedRoomForDetails.ratePlans && selectedRoomForDetails.ratePlans.length > 0 && (selectedRoomForDetails.ratePlans?.[0]?.price || selectedRoomForDetails.ratePlans?.[0]?.Rate))
                            ? `${getDisplayCurrency(selectedRoomForDetails.ratePlans[0])}${(selectedRoomForDetails.ratePlans?.[0]?.price || selectedRoomForDetails.ratePlans?.[0]?.Rate).toLocaleString()}`
                            : null;
                        return priceStr ? (
                          <div className="flex items-center gap-1.5">
                            <i className="fas fa-tag text-gray-500 text-xs"></i>
                            <span className="text-gray-700 font-medium">{priceStr}</span>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  </div>
                  
                  {/* Room Features */}
                  {selectedRoomForDetails.features?.length > 0 && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">
                        {t('room.roomFeatures')}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {selectedRoomForDetails.features.map((feature, index) => (
                          <span key={index}>
                            {index > 0 && <span className="mx-1">•</span>}
                            {feature}
                          </span>
                        ))}
                      </p>
                    </div>
                  )}

                  {/* Room Facilities - grouped by category */}
                  {selectedRoomForDetails.facilityGroups && Object.keys(selectedRoomForDetails.facilityGroups).length > 0 ? (
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(selectedRoomForDetails.facilityGroups).map(([groupName, facilities]) => (
                        <div key={groupName} className="bg-gray-50 rounded-lg p-2.5">
                          <h5 className="font-semibold text-gray-800 mb-1.5 text-xs">{groupName}</h5>
                          <div className="space-y-1">
                            {facilities.map((facility, index) => (
                              <div key={index} className="flex items-center space-x-1.5">
                                <i className="fas fa-check text-green-500" style={{fontSize: '8px'}}></i>
                                <span className="text-xs text-gray-600">{facility}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : selectedRoomForDetails.amenities?.length > 0 ? (
                    <div className="bg-gray-50 rounded-lg p-2.5">
                      <h5 className="font-semibold text-gray-800 mb-1.5 text-xs">{t('room.roomAmenities')}</h5>
                      <div className="grid grid-cols-2 gap-1">
                        {selectedRoomForDetails.amenities.map((amenity, index) => (
                          <div key={index} className="flex items-center space-x-1.5">
                            <i className="fas fa-check text-green-500" style={{fontSize: '8px'}}></i>
                            <span className="text-xs text-gray-600">{amenity}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
          )}
        </BottomSheet>

        {/* Cancellation Policy Modal */}
        {showCancellationPolicy && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900">
                    Cancellation Policy
                  </h3>
                  <button
                    onClick={closeModal}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <i className="fas fa-times text-xl"></i>
                  </button>
                </div>
                <div className="space-y-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">
                      Flexible Rate
                    </h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>
                        • Free cancellation until 24 hours before check-in
                      </li>
                      <li>• No cancellation fees</li>
                      <li>
                        • Full refund for cancellations made within policy
                      </li>
                      <li>
                        • Modifications allowed up to 24 hours before arrival
                      </li>
                    </ul>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-4">
                    <h4 className="font-semibold text-orange-900 mb-2">
                      Non-Refundable Rate
                    </h4>
                    <ul className="text-sm text-orange-800 space-y-1">
                      <li>• No cancellations or modifications allowed</li>
                      <li>• Full payment charged at time of booking</li>
                      <li>• No refunds for any reason</li>
                      <li>• Best price guarantee</li>
                    </ul>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">
                      General Terms
                    </h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>
                        • All cancellations must be made through our booking
                        system
                      </li>
                      <li>
                        • Refunds will be processed within 5-7 business days
                      </li>
                      <li>
                        • Force majeure events may result in policy exceptions
                      </li>
                      <li>
                        • Contact our customer service for special circumstances
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Amenities Modal */}
        {showAmenities && selectedRoomForDetails && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-base font-bold text-gray-900">
                    {t('room.roomAmenities')}
                  </h3>
                  <button
                    onClick={closeModal}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <i className="fas fa-times text-lg"></i>
                  </button>
                </div>
                <div className="space-y-3">
                  {/* Room Info */}
                  <div className="bg-gray-50 rounded-lg p-3">
                    <h4 className="font-semibold text-gray-900 text-sm">
                      {selectedRoomForDetails.name}
                    </h4>
                  </div>

                  {/* Room Facilities - grouped by category */}
                  {selectedRoomForDetails.facilityGroups && Object.keys(selectedRoomForDetails.facilityGroups).length > 0 ? (
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(selectedRoomForDetails.facilityGroups).map(([groupName, facilities]) => (
                        <div key={groupName} className="bg-gray-50 rounded-lg p-2.5">
                          <h5 className="font-semibold text-gray-800 mb-1.5 text-xs">
                            {groupName}
                          </h5>
                          <div className="space-y-1">
                            {facilities.map((facility, index) => (
                              <div key={index} className="flex items-center space-x-1.5">
                                <i className="fas fa-check text-green-500" style={{fontSize: '8px'}}></i>
                                <span className="text-xs text-gray-600">{facility}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : selectedRoomForDetails.amenities?.length > 0 ? (
                    <div className="bg-gray-50 rounded-lg p-2.5">
                      <h5 className="font-semibold text-gray-800 mb-1.5 text-xs">
                        {t('room.roomAmenities')}
                      </h5>
                      <div className="grid grid-cols-2 gap-1">
                        {selectedRoomForDetails.amenities.map((amenity, index) => (
                          <div key={index} className="flex items-center space-x-1.5">
                            <i className="fas fa-check text-green-500" style={{fontSize: '8px'}}></i>
                            <span className="text-xs text-gray-600">{amenity}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {/* Room Features */}
                  {selectedRoomForDetails.features?.length > 0 && (
                    <div className="bg-green-50 rounded-lg p-4">
                      <h4 className="font-semibold text-green-900 mb-3">
                        {t('room.roomFeatures')}
                      </h4>
                      <p className="text-sm text-green-800">
                        {selectedRoomForDetails.features.map((feature, index) => (
                          <span key={index}>
                            {index > 0 && <span className="mx-1">•</span>}
                            {feature}
                          </span>
                        ))}
                      </p>
                    </div>
                  )}

                  {/* Rate Plan Amenities */}
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h4 className="font-semibold text-purple-900 mb-3">
                      {t('room.ratePlanBenefits')}
                    </h4>
                    <div className="space-y-3">
                      {selectedRoomForDetails.ratePlans.map((ratePlan) => (
                        <div key={ratePlan.id} className="border border-purple-200 rounded p-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-purple-900">
                              {ratePlan.name}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-xs ${
                              ratePlan.badgeColor === "green"
                                ? "bg-green-100 text-green-700"
                                : ratePlan.badgeColor === "orange"
                                  ? "bg-orange-100 text-orange-700"
                                  : ratePlan.badgeColor === "purple"
                                    ? "bg-purple-100 text-purple-700"
                                    : "bg-yellow-100 text-yellow-700"
                            }`}>
                              {ratePlan.badge}
                            </span>
                          </div>
                          <div className="space-y-1">
                            {getBreakfastStatus(ratePlan) !== null && (
                            <div className="flex items-center space-x-2 text-xs text-purple-800">
                              <i className={`fas ${getBreakfastStatus(ratePlan) ? 'fa-check-circle text-green-600' : 'fa-times-circle text-red-600'}`}></i>
                              <span>{getBreakfastStatus(ratePlan) ? t('ratePlan.breakfastIncluded') : t('ratePlan.noBreakfastIncluded')}</span>
                            </div>
                            )}
                            {ratePlan.maxGuests && (
                            <div className="flex items-center space-x-2 text-xs text-purple-800">
                              <i className="fas fa-users text-purple-600"></i>
                              <span>Max {ratePlan.maxGuests} guests</span>
                            </div>
                            )}
                            {ratePlan.cancellation && String(ratePlan.cancellation).trim() && (
                            <div className="flex items-center space-x-2 text-xs text-purple-800">
                              <i className="fas fa-file-contract text-purple-600"></i>
                              <span>{ratePlan.cancellation}</span>
                            </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* What's Included Modal (Mobile) */}
        {showIncludedModal && selectedRatePlanForIncluded?.included && (() => {
          const includedText = Array.isArray(selectedRatePlanForIncluded.included) ? selectedRatePlanForIncluded.included.join('\n') : String(selectedRatePlanForIncluded.included);
          const items = includedText.split(/[\n|]+/).map(s => s.trim().replace(/[|]+$/, '').replace(/&amp;/g, '&').trim()).filter(Boolean);
          return (
            <div
              className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4"
              onClick={(e) => { e.stopPropagation(); setShowIncludedModal(false); setSelectedRatePlanForIncluded(null); }}
            >
              <div
                className="bg-white rounded-xl max-w-md w-full max-h-[80vh] overflow-y-auto shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                  <h5 className="text-base font-bold text-gray-900 flex items-center gap-2">
                    ✨ {t('ratePlan.whatsIncluded')}
                  </h5>
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowIncludedModal(false); setSelectedRatePlanForIncluded(null); }}
                    className="text-gray-400 hover:text-gray-600 p-1"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="p-4">
                  <ul className="space-y-2">
                    {items.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-700 leading-relaxed">
                        <svg className="w-4 h-4 mt-0.5 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Rate Plan Policy Modal */}
        <BottomSheet
          open={!!(showRatePlanPolicy && selectedRatePlanForPolicy)}
          onClose={closeModal}
          title={selectedRatePlanForPolicy ? `${selectedRatePlanForPolicy.name} — Policy Details` : 'Policy Details'}
        >
          {selectedRatePlanForPolicy && (
                <div className="space-y-4">
                  {/* Booking Policy */}
                  {selectedRatePlanForPolicy.bookingPolicy && !Array.isArray(selectedRatePlanForPolicy.bookingPolicy) && String(selectedRatePlanForPolicy.bookingPolicy).trim() && (() => {
                    const items = String(selectedRatePlanForPolicy.bookingPolicy).split('|').filter(item => item.trim());
                    const isExpanded = expandedPolicySections.booking;
                    const visibleItems = isExpanded ? items : items.slice(0, 2);
                    return (
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                      <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                        <i className="fas fa-file-contract text-blue-600"></i>
                        Booking Policy
                      </h4>
                      <ul className="text-sm text-blue-800 leading-relaxed space-y-1 list-disc list-inside">
                        {visibleItems.map((item, i) => <li key={i}>{item.trim()}</li>)}
                      </ul>
                      {items.length > 2 && (
                        <button
                          onClick={() => setExpandedPolicySections(prev => ({ ...prev, booking: !prev.booking }))}
                          className="text-xs text-blue-600 hover:text-blue-800 mt-2 font-medium"
                        >
                          {isExpanded ? 'Show Less' : `Show More (${items.length - 2} more)`}
                        </button>
                      )}
                    </div>
                    );
                  })()}

                  {/* Cancellation Policy */}
                  {selectedRatePlanForPolicy.cancellationPolicy && !Array.isArray(selectedRatePlanForPolicy.cancellationPolicy) && String(selectedRatePlanForPolicy.cancellationPolicy).trim() && (() => {
                    const items = String(selectedRatePlanForPolicy.cancellationPolicy).split('|').filter(item => item.trim());
                    const isExpanded = expandedPolicySections.cancellation;
                    const visibleItems = isExpanded ? items : items.slice(0, 2);
                    return (
                    <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                      <h4 className="font-semibold text-indigo-900 mb-2 flex items-center gap-2">
                        <i className="fas fa-undo text-indigo-600"></i>
                        Cancellation Policy
                      </h4>
                      <ul className="text-sm text-indigo-800 leading-relaxed space-y-1 list-disc list-inside">
                        {visibleItems.map((item, i) => <li key={i}>{item.trim()}</li>)}
                      </ul>
                      {items.length > 2 && (
                        <button
                          onClick={() => setExpandedPolicySections(prev => ({ ...prev, cancellation: !prev.cancellation }))}
                          className="text-xs text-indigo-600 hover:text-indigo-800 mt-2 font-medium"
                        >
                          {isExpanded ? 'Show Less' : `Show More (${items.length - 2} more)`}
                        </button>
                      )}
                      {selectedRatePlanForPolicy.cancellation && !Array.isArray(selectedRatePlanForPolicy.cancellation) && String(selectedRatePlanForPolicy.cancellation).trim().toLowerCase() !== 'free cancellation' && selectedRatePlanForPolicy.cancellation !== selectedRatePlanForPolicy.cancellationPolicy && (
                        <ul className="text-sm text-indigo-700 mt-2 leading-relaxed space-y-1 list-disc list-inside">
                          {String(selectedRatePlanForPolicy.cancellation).split('|').map((item, i) => item.trim() && <li key={i}>{item.trim()}</li>)}
                        </ul>
                      )}
                    </div>
                    );
                  })()}

                  {/* No Show Policy */}
                  {((selectedRatePlanForPolicy.noShowPolicy && !Array.isArray(selectedRatePlanForPolicy.noShowPolicy) && String(selectedRatePlanForPolicy.noShowPolicy).trim()) || (selectedRatePlanForPolicy.noShowPolicyType && String(selectedRatePlanForPolicy.noShowPolicyType).trim() && String(selectedRatePlanForPolicy.noShowPolicyType).trim().toLowerCase() !== 'free')) && (() => {
                    const hasPolicy = selectedRatePlanForPolicy.noShowPolicy && !Array.isArray(selectedRatePlanForPolicy.noShowPolicy) && String(selectedRatePlanForPolicy.noShowPolicy).trim();
                    const items = hasPolicy ? String(selectedRatePlanForPolicy.noShowPolicy).split('|').filter(item => item.trim()) : [];
                    const isExpanded = expandedPolicySections.noShow;
                    const visibleItems = isExpanded ? items : items.slice(0, 2);
                    return (
                    <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                      <h4 className="font-semibold text-orange-900 mb-2 flex items-center gap-2">
                        <i className="fas fa-exclamation-triangle text-orange-600"></i>
                        No Show Policy
                      </h4>
                      {items.length > 0 && (
                        <ul className="text-sm text-orange-800 mb-2 leading-relaxed space-y-1 list-disc list-inside">
                          {visibleItems.map((item, i) => <li key={i}>{item.trim()}</li>)}
                        </ul>
                      )}
                      {items.length > 2 && (
                        <button
                          onClick={() => setExpandedPolicySections(prev => ({ ...prev, noShow: !prev.noShow }))}
                          className="text-xs text-orange-600 hover:text-orange-800 mt-2 font-medium"
                        >
                          {isExpanded ? 'Show Less' : `Show More (${items.length - 2} more)`}
                        </button>
                      )}
                      {selectedRatePlanForPolicy.noShowPolicyType && !Array.isArray(selectedRatePlanForPolicy.noShowPolicyType) && String(selectedRatePlanForPolicy.noShowPolicyType).trim() && String(selectedRatePlanForPolicy.noShowPolicyType).trim().toLowerCase() !== 'free' && (
                        <p className="text-sm text-orange-700">
                          <span className="font-medium">Policy Type:</span> {selectedRatePlanForPolicy.noShowPolicyType}
                        </p>
                      )}
                    </div>
                    );
                  })()}
                </div>
          )}
        </BottomSheet>

        {/* What's Included Modal */}
        <BottomSheet
          open={!!(showWhatsIncluded && selectedRatePlanForIncluded)}
          onClose={closeModal}
          title={selectedRatePlanForIncluded ? `${selectedRatePlanForIncluded.name} — What's Included` : "What's Included"}
        >
          {selectedRatePlanForIncluded && (
            <div className="space-y-4">
              {/* BreakFastDescription */}
              {(selectedRatePlanForIncluded.description || selectedRatePlanForIncluded.breakFastDescription) && (
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <h4 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                    <i className="fas fa-utensils text-green-600"></i>
                    What's Included
                  </h4>
                  {(() => {
                    const content = selectedRatePlanForIncluded.breakFastDescription || selectedRatePlanForIncluded.description || '';
                    if (content.includes('|')) {
                      return (
                        <ul className="space-y-2">
                          {content.split('|').map((item, index) => (
                            item.trim() && (
                              <li key={index} className="flex items-start gap-2 text-sm text-green-800">
                                <i className="fas fa-check-circle text-green-600 mt-0.5 flex-shrink-0"></i>
                                <span className="leading-relaxed">{item.trim()}</span>
                              </li>
                            )
                          ))}
                        </ul>
                      );
                    } else {
                      return (
                        <p className="text-sm text-green-800 leading-relaxed">
                          {content}
                        </p>
                      );
                    }
                  })()}
                </div>
              )}

              {/* If no description, show a message */}
              {!selectedRatePlanForIncluded.description && !selectedRatePlanForIncluded.breakFastDescription && (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-center">
                  <p className="text-sm text-gray-600">
                    No additional information available for this rate plan.
                  </p>
                </div>
              )}
            </div>
          )}
        </BottomSheet>

        {/* Full-screen image lightbox — tap room image to view large + swipe */}
        {imageLightbox && imageLightbox.images?.length > 0 && (
          <div
            className="fixed inset-0 z-[10000] bg-black/95 flex flex-col"
            onClick={() => setImageLightbox(null)}
          >
            {/* Top bar: name + counter + close */}
            <div className="flex items-center justify-between px-4 pt-3 pb-2 text-white flex-shrink-0" style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}>
              <span className="text-sm font-medium truncate pr-3">{imageLightbox.roomName}</span>
              <span className="text-xs text-white/70 flex-shrink-0 mr-2">{imageLightbox.index + 1} / {imageLightbox.images.length}</span>
              <button
                onClick={(e) => { e.stopPropagation(); setImageLightbox(null); }}
                aria-label="Close"
                className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full bg-white/15 active:bg-white/25 active:scale-90 transition-all"
              >
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Image (contain) + swipe */}
            <div
              className="flex-1 min-h-0 flex items-center justify-center relative"
              style={{ touchAction: 'pan-y' }}
              onClick={(e) => e.stopPropagation()}
              onTouchStart={(e) => { lightboxTouchX.current = e.touches[0].clientX; }}
              onTouchEnd={(e) => {
                if (lightboxTouchX.current === null) return;
                const dx = e.changedTouches[0].clientX - lightboxTouchX.current;
                if (Math.abs(dx) > 40) {
                  setImageLightbox(prev => {
                    const n = prev.images.length;
                    const next = dx < 0 ? (prev.index + 1) % n : (prev.index - 1 + n) % n;
                    return { ...prev, index: next };
                  });
                }
                lightboxTouchX.current = null;
              }}
            >
              <img
                src={imageLightbox.images[imageLightbox.index]}
                alt={`${imageLightbox.roomName} ${imageLightbox.index + 1}`}
                className="max-w-full max-h-full object-contain select-none"
              />

              {/* Prev / Next (only if >1) */}
              {imageLightbox.images.length > 1 && (
                <>
                  <button
                    onClick={() => setImageLightbox(prev => ({ ...prev, index: (prev.index - 1 + prev.images.length) % prev.images.length }))}
                    aria-label="Previous"
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/15 active:bg-white/25 active:scale-90 transition-all"
                  >
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setImageLightbox(prev => ({ ...prev, index: (prev.index + 1) % prev.images.length }))}
                    aria-label="Next"
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/15 active:bg-white/25 active:scale-90 transition-all"
                  >
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </>
              )}
            </div>

            {/* Dots */}
            {imageLightbox.images.length > 1 && (
              <div className="flex justify-center gap-1.5 pb-6 pt-3 flex-shrink-0" style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }} onClick={(e) => e.stopPropagation()}>
                {imageLightbox.images.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setImageLightbox(prev => ({ ...prev, index: i }))}
                    className={`h-1.5 rounded-full transition-all ${i === imageLightbox.index ? 'w-5 bg-white' : 'w-1.5 bg-white/40'}`}
                    aria-label={`Image ${i + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Tax Modal for Rate Plan */}
        {showTaxModal && selectedRatePlanForTax && selectedRoomForTax && (
          <BottomSheet
            open={true}
            onClose={() => setShowTaxModal(false)}
            title={(() => {
              const nights = Math.ceil((selectedDates.checkOut - selectedDates.checkIn) / (1000 * 60 * 60 * 24)) || 1;
              const g = adults + children;
              return `Rate for ${nights} Night${nights > 1 ? 's' : ''} · ${g} Guest${g > 1 ? 's' : ''} · ${selectedQuantityForTax} Room${selectedQuantityForTax > 1 ? 's' : ''}`;
            })()}
          >
              {/* Content */}
              <div>
                {isLoadingTax ? (
                  <div className="text-center py-6">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                    <p className="mt-3 text-sm text-gray-600">Calculating taxes...</p>
                  </div>
                ) : taxError ? (
                  <div className="text-center py-6">
                    <p className="text-sm text-red-600">{taxError}</p>
                    <button
                      onClick={() => setShowTaxModal(false)}
                      className="mt-3 px-4 py-2 text-sm rounded-lg transition-all border-2 bg-transparent active:bg-[var(--primary-color)] active:!text-white active:scale-95"
                      style={{ borderColor: 'var(--primary-color)', color: 'var(--primary-color)' }}
                    >
                      Close
                    </button>
                  </div>
                ) : taxData ? (
                  <>
                    {/* Booking Summary */}
                    <div className="mb-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="text-xs text-gray-600">Date</p>
                          <p className="text-sm font-medium text-gray-900">
                            {selectedDates?.checkIn ? new Date(selectedDates.checkIn).toLocaleDateString('en-US', {
                              weekday: 'short',
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            }) : 'N/A'}
                          </p>
                        </div>
                        <div className="text-right">
                          {(() => {
                            const quantity = selectedQuantityForTax;
                            const nights = Math.ceil(
                              (selectedDates.checkOut - selectedDates.checkIn) / (1000 * 60 * 60 * 24)
                            ) || 1;
                            // Promotion only applies if both name and discount value are present
                            const hasPromotion = selectedRatePlanForTax.promotionDiscount?.discount > 0 && 
                              (selectedRatePlanForTax.promotionDiscount?.promotionName || selectedRatePlanForTax.promotionName);
                            let calculatedPrice = selectedRatePlanForTax.price || selectedRoomForTax.basePrice || 0;
                            let originalPrice = calculatedPrice;
                            
                            if (hasPromotion) {
                              originalPrice = calculatedPrice;
                              calculatedPrice = selectedRatePlanForTax.promotionDiscount.afterDiscountAmount;
                            }
                            
                            // Apply secret deal discount if available
                            if (selectedRatePlanForTax.secretDeal) {
                              calculatedPrice = calculatedPrice * (1 - (selectedRatePlanForTax.memberDiscount || 0));
                            }
                            
                            return (
                              <>
                                {hasPromotion && (
                                  <>
                                    <p className="text-xs line-through text-gray-500">
                                      {getDisplayCurrency(selectedRatePlanForTax)} {(originalPrice * quantity * nights || 0).toLocaleString()}
                                    </p>
                                    <p className="text-base font-bold text-gray-900">
                                      {getDisplayCurrency(selectedRatePlanForTax)} {(calculatedPrice * quantity * nights || 0).toLocaleString()}
                                    </p>
                                    <p className="text-xs text-red-600 font-medium">
                                      Save {getDisplayCurrency(selectedRatePlanForTax)} {((originalPrice - calculatedPrice) * quantity * nights || 0).toLocaleString()}
                                    </p>
                                  </>
                                )}
                                {!hasPromotion && (
                                  <p className="text-base font-bold text-gray-900">
                                    {getDisplayCurrency(selectedRatePlanForTax)} {(calculatedPrice * quantity * nights || 0).toLocaleString()}
                                  </p>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </div>

                    {/* Room Taxes */}
                    <div className="mb-4 border-t border-gray-200 pt-3">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Room Taxes</h4>
                      <div className="space-y-2">
                        {(() => {
                          const taxes = taxData.taxesBreakDown || taxData.taxes || taxData.Taxes || taxData.taxBreakdown || taxData.data?.taxes || [];
                          const totalTax = taxData.totalTax || taxData.TotalTax || taxData.totalTaxAmount || taxData.TotalTaxAmount || 0;
                          const quantity = selectedQuantityForTax;
                          
                          if (Array.isArray(taxes) && taxes.length > 0) {
                            return taxes.map((tax, index) => {
                              const taxId = tax.TaxId || tax.taxId || tax.TaxID || 0;
                              let taxAmount = taxId === 19 
                                ? (tax.TaxValue || tax.taxValue || 0)
                                : (tax.TaxAmmount || tax.TaxAmount || tax.taxAmount || 0);
                              
                              // TaxId 19: TaxValue used as-is (no quantity multiplication)
                              
                              return (
                                <div key={index} className="flex justify-between items-center py-1">
                                  <span className="text-xs text-gray-700">
                                    {tax.TaxName || tax.taxName || tax.name || 'Tax'}
                                  </span>
                                  <span className="text-xs font-medium text-gray-900">
                                    {getDisplayCurrency(selectedRatePlanForTax)} {parseFloat(taxAmount || 0).toLocaleString()}
                                  </span>
                                </div>
                              );
                            });
                          } else if (totalTax > 0) {
                            return (
                              <div className="flex justify-between items-center py-1">
                                <span className="text-xs text-gray-700">GST</span>
                                <span className="text-xs font-medium text-gray-900">
                                  {getDisplayCurrency(selectedRatePlanForTax)} {parseFloat(totalTax).toLocaleString()}
                                </span>
                              </div>
                            );
                          } else {
                            return (
                              <div className="text-xs text-gray-500">No tax information available</div>
                            );
                          }
                        })()}
                      </div>
                    </div>

                    {/* Total */}
                    <div className="border-t border-gray-200 pt-3 mb-4">
                      <div className="flex justify-between items-center mb-1">
                        <h4 className="text-base font-bold text-gray-900">Total</h4>
                        <div className="text-right">
                          <p className="text-base font-bold text-gray-900">
                            {getDisplayCurrency(selectedRatePlanForTax)} {(() => {
                              const quantity = selectedQuantityForTax;
                              const nights = Math.ceil(
                                (selectedDates.checkOut - selectedDates.checkIn) / (1000 * 60 * 60 * 24)
                              ) || 1;
                              // Promotion only applies if both name and discount value are present
                            const hasPromotion = selectedRatePlanForTax.promotionDiscount?.discount > 0 && 
                              (selectedRatePlanForTax.promotionDiscount?.promotionName || selectedRatePlanForTax.promotionName);
                              let calculatedPrice = selectedRatePlanForTax.price || selectedRoomForTax.basePrice || 0;
                              
                              if (hasPromotion) {
                                calculatedPrice = selectedRatePlanForTax.promotionDiscount.afterDiscountAmount;
                              }
                              
                              // Apply secret deal discount if available
                              if (selectedRatePlanForTax.secretDeal) {
                                calculatedPrice = calculatedPrice * (1 - (selectedRatePlanForTax.memberDiscount || 0));
                              }
                              
                              const basePrice = calculatedPrice * quantity * nights;
                              const taxes = taxData.taxesBreakDown || taxData.taxes || [];
                              let totalTaxAmount = 0;
                              
                              if (Array.isArray(taxes) && taxes.length > 0) {
                                totalTaxAmount = taxes.reduce((sum, tax) => {
                                  if (isTaxIncluded(tax)) return sum; // Included tax = already in rate, don't add
                                  const taxId = tax.TaxId || tax.taxId || tax.TaxID || 0;
                                  let taxAmount = 0;

                                  if (taxId === 19) {
                                    const taxValue = parseFloat(tax.TaxValue || tax.taxValue || 0);
                                    taxAmount = taxValue;
                                  } else {
                                    taxAmount = parseFloat(tax.TaxAmmount || tax.TaxAmount || tax.taxAmount || tax.amount || 0);
                                  }

                                  return sum + (isNaN(taxAmount) ? 0 : taxAmount);
                                }, 0);
                              } else {
                                totalTaxAmount = parseFloat(taxData.totalTax || taxData.TotalTax || taxData.totalTaxAmount || 0);
                              }
                              
                              const finalTotal = basePrice + totalTaxAmount;
                              return parseFloat(finalTotal).toLocaleString();
                            })()}
                          </p>
                          <p className="text-xs text-gray-500">(Tax Inclusive)</p>
                        </div>
                      </div>
                    </div>

                    {/* Close Button */}
                    <button
                      onClick={() => setShowTaxModal(false)}
                      className="w-full font-semibold py-2 text-sm rounded-lg transition-all border-2 bg-transparent active:bg-[var(--primary-color)] active:!text-white active:scale-95"
                      style={{ borderColor: 'var(--primary-color)', color: 'var(--primary-color)' }}
                    >
                      Close
                    </button>
                  </>
                ) : (
                  <div className="text-center py-6">
                    <p className="text-sm text-gray-600">No tax information available</p>
                    <button
                      onClick={() => setShowTaxModal(false)}
                      className="mt-3 px-4 py-2 text-sm rounded-lg transition-all border-2 bg-transparent active:bg-[var(--primary-color)] active:!text-white active:scale-95"
                      style={{ borderColor: 'var(--primary-color)', color: 'var(--primary-color)' }}
                    >
                      Close
                    </button>
                  </div>
                )}
              </div>
          </BottomSheet>
        )}

        {/* Tax Information Modal */}
        {showTaxInformation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Tax Information
                  </h3>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <i className="fas fa-times text-xl"></i>
                  </button>
                </div>
                <div className="space-y-4">
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <h4 className="font-medium text-blue-900 mb-2">
                      GST (Goods and Services Tax)
                    </h4>
                    <p className="text-sm text-blue-800">
                      As per Indian tax regulations, hotel accommodation is
                      subject to GST at 12%.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">
                      Tax Breakdown
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">GST (Total):</span>
                        <span className="font-medium">12%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">
                          CGST (Central GST):
                        </span>
                        <span className="font-medium">6%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">SGST (State GST):</span>
                        <span className="font-medium">6%</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">
                      Example Calculation
                    </h4>
                    <div className="bg-gray-50 p-3 rounded-lg text-sm">
                      <div className="space-y-1">
                        <div className="flex justify-between">
                          <span>Base Price:</span>
                          <span>INR 8,800</span>
                        </div>
                        <div className="flex justify-between">
                          <span>GST (12%):</span>
                          <span>INR 1,056</span>
                        </div>
                        <div className="flex justify-between">
                          <span>CGST (6%):</span>
                          <span>INR 528</span>
                        </div>
                        <div className="flex justify-between">
                          <span>SGST (6%):</span>
                          <span>INR 528</span>
                        </div>
                        <div className="border-t border-gray-300 pt-1 flex justify-between font-medium">
                          <span>Total with Tax:</span>
                          <span>INR 9,856</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-yellow-50 p-3 rounded-lg">
                    <div className="flex items-start">
                      <i className="fas fa-info-circle text-yellow-600 mt-0.5 mr-2"></i>
                      <div className="text-sm text-yellow-800">
                        <p className="font-medium mb-1">Important Notes:</p>
                        <ul className="space-y-1">
                          <li>
                            • Tax rates are as per current Indian regulations
                          </li>
                          <li>• All prices shown include applicable taxes</li>
                          <li>
                            • Tax calculation is based on the base room rate
                          </li>
                          <li>
                            • Additional charges may apply for extra services
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add-on Policy Modal */}
        {showAddOnPolicyModal && selectedAddOnPolicy && (
          <div 
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50" 
            onClick={() => {
              setShowAddOnPolicyModal(false);
              setSelectedAddOnPolicy(null);
            }}
            style={{ zIndex: 9999 }}
          >
            <div 
              className="bg-white rounded-lg shadow-2xl max-w-md w-full mx-4 max-h-[70vh] overflow-hidden flex flex-col" 
              onClick={(e) => e.stopPropagation()}
              style={{ zIndex: 10000 }}
            >
              {/* Header */}
              <div 
                className="card-head text-white px-4 py-3 rounded-t-lg flex items-center justify-between"
                style={{ backgroundColor: 'var(--primary-color)', background: 'var(--primary-color)' }}
              >
                <h3 className="text-sm font-semibold">Policy - {selectedAddOnPolicy.name}</h3>
                <button
                  onClick={() => {
                    setShowAddOnPolicyModal(false);
                    setSelectedAddOnPolicy(null);
                  }}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Content - Scrollable */}
              <div className="p-4 overflow-y-auto flex-1">
                {/* Guarantee Policy */}
                {selectedAddOnPolicy.guaranteePolicy && (
                  <div className="mb-4">
                    <h4 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-2">
                      <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      Guarantee Policy
                    </h4>
                    <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
                      {selectedAddOnPolicy.guaranteePolicy}
                    </p>
                  </div>
                )}

                {/* Cancellation Policy */}
                {selectedAddOnPolicy.cancellationPolicy && (
                  <div className={selectedAddOnPolicy.guaranteePolicy ? "border-t border-gray-200 pt-4" : ""}>
                    <h4 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-2">
                      <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Cancellation Policy
                    </h4>
                    <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
                      {selectedAddOnPolicy.cancellationPolicy}
                    </p>
                  </div>
                )}

                {/* No Policy Message */}
                {!selectedAddOnPolicy.guaranteePolicy && !selectedAddOnPolicy.cancellationPolicy && (
                  <div className="text-center py-6">
                    <p className="text-sm text-gray-500">No policy information available for this add-on.</p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="border-t border-gray-200 px-4 py-3">
                <button
                  onClick={() => {
                    setShowAddOnPolicyModal(false);
                    setSelectedAddOnPolicy(null);
                  }}
                  className="w-full font-semibold py-2 text-sm rounded-lg transition-all border-2 bg-transparent active:bg-[var(--primary-color)] active:!text-white active:scale-95"
                  style={{ borderColor: 'var(--primary-color)', color: 'var(--primary-color)' }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add-on Comment Modal */}
        {showCommentModal && selectedAddOnForComment && (
          <div 
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50" 
            onClick={() => {
              setShowCommentModal(false);
              setSelectedAddOnForComment(null);
            }}
            style={{ zIndex: 9999 }}
          >
            <div 
              className="bg-white rounded-lg shadow-2xl max-w-md w-full mx-4 max-h-[70vh] overflow-hidden flex flex-col" 
              onClick={(e) => e.stopPropagation()}
              style={{ zIndex: 10000 }}
            >
              {/* Header */}
              <div 
                className="card-head text-white px-4 py-3 rounded-t-lg flex items-center justify-between"
                style={{ backgroundColor: 'var(--primary-color)', background: 'var(--primary-color)' }}
              >
                <h3 className="text-sm font-semibold">Add Comment - {selectedAddOnForComment.name}</h3>
                <button
                  onClick={() => {
                    setShowCommentModal(false);
                    setSelectedAddOnForComment(null);
                  }}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Content */}
              <div className="p-4">
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  rows="4"
                  placeholder="Enter your comment here..."
                  defaultValue={addOnComments[selectedAddOnForComment.id] || ""}
                  autoFocus
                />
              </div>

              {/* Footer */}
              <div className="border-t border-gray-200 px-4 py-3 flex space-x-3">
                <button
                  onClick={() => {
                    setShowCommentModal(false);
                    setSelectedAddOnForComment(null);
                  }}
                  className="flex-1 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const textarea = document.querySelector(`textarea[placeholder="Enter your comment here..."]`);
                    const comment = textarea?.value?.trim() || '';
                    if (comment) {
                      setAddOnComments((prev) => ({ ...prev, [selectedAddOnForComment.id]: comment }));
                    } else {
                      // Remove comment if empty
                      setAddOnComments((prev) => {
                        const updated = { ...prev };
                        delete updated[selectedAddOnForComment.id];
                        return updated;
                      });
                    }
                    setShowCommentModal(false);
                    setSelectedAddOnForComment(null);
                  }}
                  className="flex-1 px-4 py-2 text-sm text-white font-semibold rounded-lg transition-colors"
                  style={{ backgroundColor: 'var(--primary-color)', background: 'var(--primary-color)' }}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Sign In / Register Modal */}
        {(accommodationGroupDetail?.IsOtpLogin == 1 || accommodationGroupDetail?.isOtpLogin == 1 || accommodationGroupDetail?.accommodationdetailsdb?.[0]?.IsOtpLogin == 1) ? (
          <AuthModal
            show={showSignInModal}
            onClose={() => setShowSignInModal(false)}
            accommodationGroupDetail={accommodationGroupDetail}
            accommodationDetail={accommodationDetail}
            memberPercent={memberSavePercent}
          />
        ) : (
          <SignInRegisterModal
            show={showSignInModal}
            onClose={() => setShowSignInModal(false)}
            signInTab={signInTab}
            setSignInTab={setSignInTab}
            accommodationGroupDetail={accommodationGroupDetail}
            accommodationDetail={accommodationDetail}
          />
        )}
      </div>

      {/* Toast Container */}
      <div className="fixed bottom-4 left-4 right-4 z-50 space-y-2 safe-bottom">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`max-w-sm w-full bg-white rounded-lg shadow-lg border-l-4 p-4 transform transition-all duration-300 ease-in-out ${
              toast.type === "success"
                ? "border-green-500"
                : toast.type === "error"
                  ? "border-red-500"
                  : toast.type === "warning"
                    ? "border-yellow-500"
                    : "border-blue-500"
            }`}
            style={{
              animation: "slideInUp 0.3s ease-out",
            }}
          >
            <div className="flex items-start">
              <div className="flex-shrink-0">
                {toast.type === "success" && (
                  <i className="fas fa-check-circle text-green-500 text-lg"></i>
                )}
                {toast.type === "error" && (
                  <i className="fas fa-exclamation-circle text-red-500 text-lg"></i>
                )}
                {toast.type === "warning" && (
                  <i className="fas fa-exclamation-triangle text-yellow-500 text-lg"></i>
                )}
                {toast.type === "info" && (
                  <i className="fas fa-info-circle text-blue-500 text-lg"></i>
                )}
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-gray-900">
                  {toast.message}
                </p>
              </div>
              <div className="ml-4 flex-shrink-0">
                <button
                  onClick={() => removeToast(toast.id)}
                  className="inline-flex text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600 transition-colors"
                >
                  <i className="fas fa-times text-sm"></i>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AvailabilityComponent;
